// NeuralNetworkSimulator.ts: Modular engine for fractal neural network simulation, wave evolution, and psychic potential.

import { Node, Edge, Wave, SpaceEnergy, LinguisticElement, SyllableStructure, WordFormation, VerbalCommunication, VoiceCharacteristics, SynthesisConfig, LanguagePattern, LanguageMemory, FamilialConnection, MusicalPattern, MusicalNote, RhythmPattern, MusicalScale, MusicalPhrase } from './types';
import { logNodeFailure } from './helpers';
import { generateFractalVoiceCharacteristics, calculateFractalFormants, calculateFractalPitchModulation, generateFractalVoiceTexture } from './fractalUtils';
import { createCreatureVoiceSystem, CreatureVoiceSystem } from './VoiceSynthesis';
import { createBreathingSystem, BreathingSystem } from './breathingSystem';
import { LimbSystem, Limb, LimbType } from './limbSystem';
import { EnergySystem } from './energySystem';
import { OrganSystem } from './organSystem';
import { LivingLanguageSystem } from './livingLanguage';
import { ConceptualAbstractionSystem } from './conceptualAbstraction';
import { createPhysicalSoundSynthesizer, PhysicalSoundSynthesizer } from './physicalSoundSynthesis';
import { LifeBehaviorMathEngine, CoupledOscillatorState } from './lifeBehaviorMath';
import { ArchetypeEngine, ArchetypeSummary } from './core/archetypeLibrary';
import { computeArchetypeBoostsFromConceptual } from './core/archetypeBridge';
import { getAudioEngine } from './core/audio';
import { MouthSystem } from './mouthSystem';

const GOLDEN_RATIO = 4.669;

type MetaDomain = 'network' | 'breathing' | 'voice' | 'limb' | 'energy' | 'organ' | 'language' | 'conceptual' | 'musical';

interface MetaDomainState {
	activation: number;
	pressure: number;
	novelty: number;
	entropy: number;
	lastDelta: number;
}

interface SystemSnapshot {
	activeNodeCount: number;
	nodePressure: number;
	avgEnergy: number;
	stability: number;
	breathingMastery: number;
	voiceUnlocked: number;
	limbCount: number;
	organCount: number;
	languageCreativity: number;
	conceptCount: number;
	musicalComplexity: number;
	totalEnergy: number;
}

interface ProceduralProfile {
	id: string;
	name: string;
	emphasis: Partial<Record<MetaDomain, number>>;
	cadence: {
		breathing: number;
		limb: number;
		language: number;
		conceptual: number;
		voiceEvolution: number;
		morphology: number;
	};
	mutationBias: number;
	rewiringBias: number;
}

interface ProceduralCadence {
	breathingChance: number;
	limbChance: number;
	languageChance: number;
	conceptualChance: number;
	voiceEvolutionScale: number;
	morphologyChance: number;
}

interface FractalUpgradeStage {
	id: string;
	name: string;
	multiplier: number;
	minStep: number;
	minStability: number;
	minAvgEnergy: number;
	minConcepts: number;
	minLanguageCreativity: number;
}

export class NeuralNetworkSimulator {
	nodeBuffer: any;
	maxBufferSize: any;
	applyPhysics() {
		// Skeletal neural network physics parameters - ANTI-CLUSTERING CONFIGURATION
		const structuralSpring = 0.08; // Reduced spring forces to prevent clustering
		const repulsion = 0.5; // MUCH stronger repulsion to push nodes apart
		const expansionForce = 0.04; // Stronger expansion force to spread out
		const damping = 0.88; // Higher damping for stability
		const nodeRadius = 15; // Larger nodes for better spacing
		const structuralDamping = 0.97; // Extra damping for structural nodes

		// Update spatial hash periodically (every 5 steps for performance)
		if (this.step % 5 === 0) {
			this.updateSpatialHash();
		}

		const nodesLen = this.nodes.length;
		const time = this.step / 60;
		
		// Initialize velocities and apply structural constraints
		for (let i = 0; i < nodesLen; i++) {
			const node = this.nodes[i];
			if (!node || node.corroded) continue;
			
			if (typeof node.vx !== 'number') node.vx = 0;
			if (typeof node.vy !== 'number') node.vy = 0;
			if (typeof node.vz !== 'number') node.vz = 0;
			
			// Apply very subtle breathing motion for life-like movement
			const breathingIntensity = 0.02;
			const breathingFreq = 0.1;
			const breathingPhase = node.id * 0.1;
			
			node.vx += Math.sin(time * breathingFreq + breathingPhase) * breathingIntensity;
			node.vy += Math.cos(time * breathingFreq + breathingPhase * 1.3) * breathingIntensity;
			node.vz += Math.sin(time * breathingFreq * 0.7 + breathingPhase * 0.8) * breathingIntensity * 0.5;
		}
		
		// Apply structural spring forces - this is the core of skeletal physics
		const edgesLen = this.edges.length;
		for (let ei = 0; ei < edgesLen; ei++) {
			const edge = this.edges[ei];
			const a = this.nodes[edge.from];
			const b = this.nodes[edge.to];
			if (!a || !b || a.corroded || b.corroded) continue;
			
			// Ensure velocities are initialized
			if (typeof a.vx !== 'number') a.vx = 0;
			if (typeof a.vy !== 'number') a.vy = 0;
			if (typeof a.vz !== 'number') a.vz = 0;
			if (typeof b.vx !== 'number') b.vx = 0;
			if (typeof b.vy !== 'number') b.vy = 0;
			if (typeof b.vz !== 'number') b.vz = 0;
			
			// Calculate 3D distance and direction
			const dx = b.x - a.x;
			const dy = b.y - a.y;
			const dz = b.z - a.z;
			const dist3D = Math.sqrt(dx*dx + dy*dy + dz*dz);
			if (dist3D < 1e-6) continue;
			
			// Dynamic rest length based on edge weight and node properties
			const baseRestLength = nodeRadius * 3.5;
			const weightFactor = 0.5 + edge.weight * 0.5; // 0.5 to 1.0 based on weight
			const restLength = baseRestLength * weightFactor;
			
			// Strong spring force to maintain skeletal structure
			const displacement = dist3D - restLength;
			const springForce = structuralSpring * displacement;
			
			// Apply force in direction of displacement
			const fx = springForce * dx / dist3D;
			const fy = springForce * dy / dist3D;
			const fz = springForce * dz / dist3D;
			
			// Apply forces with structural damping
			a.vx += fx * structuralDamping;
			a.vy += fy * structuralDamping;
			a.vz += fz * structuralDamping;
			b.vx -= fx * structuralDamping;
			b.vy -= fy * structuralDamping;
			b.vz -= fz * structuralDamping;
		}
		
		// Apply gentle repulsion to prevent node overlap
		for (let i = 0; i < nodesLen; i++) {
			const a = this.nodes[i];
			if (!a || a.corroded) continue;
			
			const nearbyIndices = this.getNearbyNodes(a.x, a.y, a.z, nodeRadius * 3);
			
			for (const j of nearbyIndices) {
				if (i >= j) continue;
				const b = this.nodes[j];
				if (!b || b.corroded) continue;
				
				const dx = b.x - a.x;
				const dy = b.y - a.y;
				const dz = b.z - a.z;
				const dist3D = Math.sqrt(dx*dx + dy*dy + dz*dz);
				if (dist3D < 1e-6) continue;
				
				const minDist = nodeRadius * 3.5; // Increased minimum distance to prevent clustering
				if (dist3D < minDist) {
					const force = repulsion * (minDist - dist3D) / minDist * 2; // Doubled repulsion force
					const fx = force * dx / dist3D;
					const fy = force * dy / dist3D;
					const fz = force * dz / dist3D;
					
					a.vx! -= fx;
					a.vy! -= fy;
					a.vz! -= fz;
					b.vx! += fx;
					b.vy! += fy;
					b.vz! += fz;
				}
			}
		}
		
		// GLOBAL ANTI-CLUSTERING: Push nodes away from dense areas
		// Calculate local density for each node and push outward
		const antiClusteringForce = 0.03;
		const densityRadius = 60; // Check nodes within this radius
		
		for (let i = 0; i < nodesLen; i++) {
			const node = this.nodes[i];
			if (!node || node.corroded) continue;
			
			// Count nearby nodes (density check)
			const nearbyNodes: { x: number; y: number; z: number }[] = [];
			const nearbyIndices = this.getNearbyNodes(node.x, node.y, node.z, densityRadius);
			
			for (const j of nearbyIndices) {
				const nearby = this.nodes[j];
				if (!nearby || nearby.corroded) continue;
				nearbyNodes.push(nearby);
			}
			
			// If too many nodes nearby, push outward from their center
			if (nearbyNodes.length > 3) {
				// Calculate center of nearby nodes
				const centerX = nearbyNodes.reduce((sum, n) => sum + n.x, 0) / nearbyNodes.length;
				const centerY = nearbyNodes.reduce((sum, n) => sum + n.y, 0) / nearbyNodes.length;
				const centerZ = nearbyNodes.reduce((sum, n) => sum + (n.z || 0), 0) / nearbyNodes.length;
				
				// Push node away from center
				const dx = node.x - centerX;
				const dy = node.y - centerY;
				const dz = (node.z || 0) - centerZ;
				const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
				
				if (dist > 1e-6) {
					const pushStrength = antiClusteringForce * (nearbyNodes.length / 5); // Stronger if more dense
					node.vx! += (dx / dist) * pushStrength;
					node.vy! += (dy / dist) * pushStrength;
					node.vz! += (dz / dist) * pushStrength;
				}
			}
		}
		
		// Apply gentle expansion force to prevent clustering toward center
		if (this.crystalHeartId !== null) {
			const heart = this.nodes[this.crystalHeartId];
			if (heart) {
				for (let i = 0; i < nodesLen; i++) {
					const node = this.nodes[i];
					if (!node || node.corroded || i === this.crystalHeartId) continue;
					
					const dx = node.x - heart.x;
					const dy = node.y - heart.y;
					const dz = node.z - heart.z;
					const distFromHeart = Math.sqrt(dx*dx + dy*dy + dz*dz);
					
			// Apply expansion force if node is too close to heart
			const minHeartDistance = 100; // Increased minimum distance from heart (was 50)
			if (distFromHeart < minHeartDistance && distFromHeart > 1e-6) {
						const expansionStrength = expansionForce * (minHeartDistance - distFromHeart) / minHeartDistance;
						const fx = expansionStrength * dx / distFromHeart;
						const fy = expansionStrength * dy / distFromHeart;
						const fz = expansionStrength * dz / distFromHeart;
						
						node.vx! += fx;
						node.vy! += fy;
						node.vz! += fz;
					}
				}
			}
		}
		
		// Apply damping and position updates
		for (let i = 0; i < nodesLen; i++) {
			const node = this.nodes[i];
			if (!node || node.corroded) continue;
			
			// Ensure velocities are initialized
			if (typeof node.vx !== 'number') node.vx = 0;
			if (typeof node.vy !== 'number') node.vy = 0;
			if (typeof node.vz !== 'number') node.vz = 0;
			
			// Add small random force to prevent stagnation
			const randomForce = 0.005;
			node.vx += (Math.random() - 0.5) * randomForce;
			node.vy += (Math.random() - 0.5) * randomForce;
			node.vz += (Math.random() - 0.5) * randomForce;
			
			// Strong damping for structural stability
			node.vx *= damping;
			node.vy *= damping;
			node.vz *= damping;
			
			// Apply position updates
			node.x += node.vx;
			node.y += node.vy;
			node.z += node.vz;
		}
		
		// CRITICAL FIX: Occasionally prune edges that have become extremely long (not aggressive)
		// Only do this rarely to prevent constant reconnection issues
		const maxEdgeLength = 300; // Much more lenient threshold (only remove extreme outliers)
		
		// Only prune occasionally (every 100 steps) to avoid constant disconnection
		if (this.step % 100 === 0) {
			this.edges = this.edges.filter(edge => {
				const fromNode = this.nodes[edge.from];
				const toNode = this.nodes[edge.to];
				
				if (!fromNode || !toNode || fromNode.corroded || toNode.corroded) {
					return false; // Remove edges to deleted nodes
				}
				
				// Calculate 3D distance between nodes
				const dx = fromNode.x - toNode.x;
				const dy = fromNode.y - toNode.y;
				const dz = ((fromNode.z || 0) - (toNode.z || 0));
				const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
				
				// Keep edge unless it's extremely long (indicating a bad initial connection)
				return distance < maxEdgeLength;
			});
		}
	}
	hasFailed(): boolean {
		return this.simulationFailed;
	}
	removeCorrodedNodes: any;
	stepSimulation() {
		// This method orchestrates a single simulation step
		const proceduralCadence = this.updateMetaArchitecture();
		this.simulateWavePatterns();
		this.applyPsychonautCreatureInfluence();
		this.energizeNodes();
		this.heartLifeForceCycle(); // Heart's humming life force
		this.energySurgeThroughNetwork(); // Energy surges through connections
		if (Math.random() < proceduralCadence.morphologyChance) {
			this.simulateEvolution();
		}
		
		// Enhanced musical voice system (replaces old linguistic system)
		this.discoverMusicalPattern();
		this.attemptMusicalCommunication();
		this.updateMusicalComplexity();
		
		// Check for automatic voice attempts and log them
		this.checkForAutomaticVoiceAttempts();
		
		// Update breathing system
		if (Math.random() < proceduralCadence.breathingChance) {
			this.breathingSystem.update();
		}
		
		// Autonomous mouth exploration and learning
		this.updateMouthExploration();
		
		// Creature's voice evolves with the network
		this.creatureVoice.updateVoiceFromNetworkActivity();
		if (Math.random() < 0.02 * proceduralCadence.voiceEvolutionScale) {
			this.creatureVoice.evolveVoiceWithNetwork();
		}
		
		// Limb system development and updates
		if (this.limbDevelopmentEnabled) {
			if (Math.random() < proceduralCadence.limbChance) {
				this.updateLimbSystem();
			}
		}
		
		// Living language system evolution
		if (Math.random() < proceduralCadence.languageChance) {
			this.livingLanguage.evolveLanguage();
		}
		
		// Conceptual abstraction system evolution
		if (this.conceptualThinking) {
			if (Math.random() < proceduralCadence.conceptualChance) {
				this.conceptualAbstraction.evolveConcepts();
			}
		}
		
		this.step++;
	}
	toggleSun() {
		// Toggle sun effect for visualization
		if (this.sunEnabled === undefined) this.sunEnabled = false;
		this.sunEnabled = !this.sunEnabled;
		console.log(`Sun effect ${this.sunEnabled ? 'enabled' : 'disabled'}`);
	}
	// --- Core Data Structures ---
	nodes: Node[] = [];
	edges: Edge[] = [];
	branches: number[][] = [];
	branchSelfSustaining: boolean[] = [];
	wavePatterns: number[] = [];
	waves: Wave[] = [];
	spaceEnergies: SpaceEnergy[] = [];
	step: number = 0;
	simulationFailed: boolean = false;
	debugTrace: string[] = [];

	// --- Simulation Parameters ---
	evolutionInterval: number = 60;
	pulseInterval: number = 30;
	corrodeThreshold: number = 0.01;
	corrodeDelay: number = 1;
	spaceSize: {
		depth: number; width: number; height: number 
} = {
	width: 1200, height: 800,
	depth: 0
};

	// --- Crystal Heart Life Force ---
	crystalHeartId: number | null = null;
	crystalHeartPulseInterval: number = Math.round(60 * 1000 / 138 / 16.67 * 0.888 / 0.369);
	heartHumFrequency: number = 60; // Hz - the heart's natural hum
	heartLifeForce: number = 1.0; // Life force strength
	energySurgeActive: boolean = false;
	energySurgeIntensity: number = 0;

	// --- Optimized Memory & Growth Control ---
	maxNodesPerCycle: number = 8 * 0.369; // ~2.95 nodes per cycle (smooth 0.369 speed)
	maxNodesPerMinute: number = 8 * 0.369; // ~2.95 nodes per minute (smooth 0.369 speed)
	nodesGeneratedThisCycle: number = 0; // Counter for nodes generated in current simulation cycle
	nodeGenerationAccumulator: number = 0; // Accumulator for fractional node generation
	energyTransferMatrix: Map<number, number> = new Map(); // Optimized energy transfer lookup
	nodeGenerationProbability: number = 0.05 * 0.369; // Base probability for node generation (scaled)
	growthVarianceSeed: number = Math.random(); // Seed for natural variance
	lastGrowthCycle: number = 0;
	activeNodeIndices: Set<number> = new Set(); // Track active nodes for optimization
	
	// --- Performance Optimization Structures ---
	spatialHash: Map<string, number[]> = new Map(); // Spatial hash for O(1) neighbor lookups
	spatialHashCellSize: number = 50; // Size of spatial hash cells
	neighborCache: Map<number, number[]> = new Map(); // Cache for node neighbors
	cacheValidStep: number = -1; // Track when cache was last updated
	
	// Computation frequency control
	lastEnergyUpdate: number = 0;
	energyUpdateInterval: number = 2; // Update energy every 2 steps instead of every step
	lastPhysicsUpdate: number = 0;
	physicsUpdateInterval: number = 1; // Keep physics at every step for smoothness
	
	// --- Creature State Management ---
	creatureState: 'growing' | 'stabilizing' | 'sensing' | 'exploring' = 'growing';
	totalNetworkEnergy: number = 0;
	energyStabilityThreshold: number = 500; // When to start sensing
	energyGrowthThreshold: number = 300; // When to resume growing
	sensingRadius: number = 200; // How far the creature can sense
	lastStateChange: number = 0;
	stateChangeCooldown: number = 1000; // Minimum time between state changes
	
	// --- Creature Identity & Limits ---
	creatureId: string = 'creature_1'; // Unique identifier for this creature
	maxNodesPerCreature: number = 369; // Base node capacity before fractal upgrades
	absoluteNodeLimit: number = 4096; // Safety ceiling despite fractal upgrades
	hasReachedNodeLimit: boolean = false;
	fractalUpgradeStages: FractalUpgradeStage[] = [
		{ id: 'seed', name: 'Seed Lattice', multiplier: 1.0, minStep: 0, minStability: 0.0, minAvgEnergy: 0.0, minConcepts: 0, minLanguageCreativity: 0.0 },
		{ id: 'phi-bloom', name: 'Phi Bloom', multiplier: 1.618, minStep: 900, minStability: 0.22, minAvgEnergy: 0.36, minConcepts: 8, minLanguageCreativity: 0.30 },
		{ id: 'doubling-shell', name: 'Doubling Shell', multiplier: 2.0, minStep: 1800, minStability: 0.30, minAvgEnergy: 0.42, minConcepts: 16, minLanguageCreativity: 0.35 },
		{ id: 'fractal-mantle', name: 'Fractal Mantle', multiplier: 3.236, minStep: 3600, minStability: 0.38, minAvgEnergy: 0.48, minConcepts: 30, minLanguageCreativity: 0.42 },
		{ id: 'mutant-supercluster', name: 'Mutant Supercluster', multiplier: 5.236, minStep: 7200, minStability: 0.45, minAvgEnergy: 0.55, minConcepts: 48, minLanguageCreativity: 0.50 }
	];
	currentFractalUpgradeStageIndex: number = 0;
	lastFractalUpgradeStep: number = 0;
	evolutionPhase: 'complexify' | 'rebalance' | 'simplify' = 'complexify';
	phaseDurationSteps: number = 240;
	lastPhaseShiftStep: number = 0;
	minSoftCapRatio: number = 0.65;
	maxSoftCapRatio: number = 0.95;
	adaptiveSoftCapRatio: number = 0.82;
	homeostaticTargetEnergy: number = 0.55;
	homeostaticGain: number = 0.25;
	globalPlasticityScale: number = 1.0;
	lastRewireStep: number = 0;
	rewireCooldown: number = 12;
	lastMutationScore: number = 0;
	lastMutationCost: number = 0;
	lastMutationAccepted: boolean = false;
	metaDomains: Map<MetaDomain, MetaDomainState> = new Map();
	metaLinks: Map<string, number> = new Map();
	proceduralProfiles: ProceduralProfile[] = [];
	activeProceduralProfileId: string = 'homeostatic-weave';
	metaMorphInterval: number = 90;
	lastMetaMorphStep: number = 0;
	proceduralNoveltySeed: number = Math.random();
	structuralMutationBias: number = 1.0;
	structuralRewiringBias: number = 1.0;
	
	// --- Linguistic Communication System ---
	linguisticElements: Map<string, LinguisticElement> = new Map();
	discoveredVowels: string[] = [];
	discoveredConsonants: string[] = [];
	discoveredSyllables: SyllableStructure[] = [];
	
	// --- Musical Pattern Discovery System ---
	musicalPatterns: Map<string, MusicalPattern> = new Map();
	discoveredMelodies: MusicalPattern[] = [];
	musicalPhrases: Map<string, MusicalPhrase> = new Map();
	availableScales: MusicalScale[] = [];
	currentMusicalKey: string = 'C major';
	currentMusicalMode: 'major' | 'minor' | 'pentatonic' | 'blues' | 'dorian' | 'mixolydian' = 'pentatonic';
	musicalComplexity: number = 1;
	lastMusicalNote: number = 0; // Last note frequency for interval calculation
	lastHarmonyNote: number = 0; // Last harmony note frequency
	rootFrequency: number = 220; // Root frequency for harmonic intervals (based on zodiac/color)
	rhythmPatterns: string[] = ['quarter', 'eighth', 'half', 'whole', 'triplet', 'dotted_quarter', 'dotted_eighth'];
	improvisationLevel: number = 0.1; // 0-1, how much improvisation vs structured patterns
	harmonicComplexity: number = 1; // 1-10, complexity of harmonic patterns
	isSinging: boolean = false;
	currentSingingPattern: MusicalPattern | null = null;
	
	// --- Language Learning System ---
	languagePatterns: Map<string, LanguagePattern> = new Map();
	familialConnections: Map<string, string[]> = new Map();
	patternRecognitionWeights: Map<string, number> = new Map();
	languageEvolutionStep: number = 0;
	learnedPhonemes: Set<string> = new Set();
	phonemeFrequency: Map<string, number> = new Map();
	languageMemory: Map<string, LanguageMemory> = new Map();
	discoveredWords: WordFormation[] = [];
	verbalCommunications: VerbalCommunication[] = [];
	linguisticComplexity: number = 1; // Current linguistic complexity level
	communicationAttempts: number = 0;
	successfulCommunications: number = 0;
	lastCommunicationStep: number = 0;
	
	// --- Creature's Intrinsic Voice System ---
	creatureVoice: CreatureVoiceSystem;
	
	// --- Breathing System ---
	breathingSystem: BreathingSystem;
	
	// --- Mathematical Life Behavior Models ---
	mathEngine: LifeBehaviorMathEngine;
	nodeOscillatorStates: Map<number, CoupledOscillatorState>; // For synchronized oscillations
	
	// --- Physical Sound Synthesis ---
	physicalSynthesizer: PhysicalSoundSynthesizer | null = null;
	
	// --- Limb System Integration ---
	limbSystem: LimbSystem;
	limbDevelopmentEnabled: boolean = true;
	limbGrowthRate: number = 0.1; // Probability of limb growth per step
	
	// --- Energy System ---
	energySystem: EnergySystem;
	lastLimbDevelopment: number = 0;
	limbDevelopmentInterval: number = 100; // Steps between limb development checks
	
	// --- Living Language System Integration ---
	livingLanguage: LivingLanguageSystem;
	conceptualAbstraction: ConceptualAbstractionSystem;
	languageTranscendence: number = 0.3; // Level of language transcendence
	
	// --- Organ System Integration ---
	organSystem: OrganSystem | null = null;
	conceptualThinking: boolean = true; // Enable conceptual thinking beyond words
	
	// Method to check for automatic voice attempts
	checkForAutomaticVoiceAttempts(): void {
		const breathingState = this.breathingSystem.getBreathingState();
		const now = Date.now();
		
		// Check if voice is being called automatically
		if (this.creatureVoice && !breathingState.voiceUnlocked) {
			// Log any attempts to use voice when it should be locked
			console.log(`[AUDIT ${this.step}] VOICE_LOCK_CHECK: Voice should be locked - mastery: ${(breathingState.breathingMastery * 100).toFixed(1)}%, stage: ${breathingState.breathingDevelopmentStage}`);
			
			// Check if there are any voice attempts in the last second
			if (now - this.lastVoiceAttemptTime < 1000) {
				this.voiceAttemptCount++;
				console.log(`[AUDIT ${this.step}] VOICE_ATTEMPT_DETECTED: Attempt #${this.voiceAttemptCount} - voice should be locked!`);
			}
		}
		
		// Reset counter every 10 seconds
		if (now - this.lastVoiceAttemptTime > 10000) {
			this.voiceAttemptCount = 0;
		}
	}

	// --- Audio Synthesis System ---
	audioContext: AudioContext | null = null;
	reverbNode: ConvolverNode | null = null;
	masterGain: GainNode | null = null;
	// Subtle electrical zap drone
	private zapOscillator?: OscillatorNode;
	private zapGain?: GainNode;
	private enableZapAudio: boolean = true; // enable chain lightning zap audio
	private enableZapOneShots: boolean = false; // disable short zaps in favor of drone
	private lastZapPulseByTip: Map<number, number> = new Map();
	private audioInteractionHandlerSetup: boolean = false;

	// Chain lightning state to drive zap drone
	private chainLightningActive: boolean = false;
	private lastChainLightningAt: number = 0;
	private audioWaitingLogTime: number = 0;
	voiceCharacteristics: VoiceCharacteristics = {
		pitch: 150, // Base pitch for neural voice
		pitchVariation: 20, // Pitch variation range
		formant1: 800, // First formant
		formant2: 1200, // Second formant
		formant3: 2500, // Third formant
		attackTime: 50, // Quick attack
		decayTime: 100, // Short decay
		sustainLevel: 1.2, // Higher sustain for louder voice
		releaseTime: 200, // Medium release
		breathiness: 0.3, // Some breathiness
		roughness: 0.2, // Slight roughness
		
		// Enhanced voice personality
		personality: 'mysterious',
		emotionalState: 'contemplative',
		age: 'adult',
		gender: 'neutral',
		
		// Speech patterns
		speechRate: 1.2, // Moderate speech rate
		pauseFrequency: 0.3, // Occasional pauses
		pauseDuration: 200, // Short pauses
		emphasisStrength: 0.6, // Moderate emphasis
		
		// Intonation patterns
		intonationRange: 100, // Pitch range for intonation
		questionRise: 0.8, // Strong question intonation
		statementFall: 0.6, // Moderate statement fall
		exclamationRise: 0.9 // Strong exclamation rise
	};
	synthesisConfig: SynthesisConfig = {
		voiceCharacteristics: this.voiceCharacteristics,
		volume: 2.0, // Increased volume to compete with other sounds
		speed: 1.0, // Normal speed
		emphasis: 0.7, // Higher emphasis
		reverb: 0.3 // More reverb for presence
	};
	lastSynthesisTime: number = 0;
	synthesisCooldown: number = 200; // 200ms cooldown for more frequent synthesis
	
	// --- Microcycle Growth Control ---
	microcycleInterval: number = 60000 / 8; // ~7.5 seconds between nodes (8 nodes per minute)
	lastMicrocycleTime: number = 0;
	nodesGeneratedThisMinute: number = 0;
	minuteStartTime: number = performance.now();
	nextMicrocycleTime: number = 0;
	
	// --- Voice Lock Monitoring ---
	lastVoiceAttemptTime: number = 0;
	voiceAttemptCount: number = 0;

	// Plugins
	plugins: Array<(sim: NeuralNetworkSimulator) => void> = [];

	// Archetypal cognition
	archetypeEngine: ArchetypeEngine = new ArchetypeEngine();
	archetypeMetrics?: ArchetypeSummary;

	// Sound callbacks
	onNodeGrowth?: (count: number, color?: string) => void;
	onCrystalHeartPulse?: (event?: string) => void;

	psychonautCreature: {
		x: number,
		y: number,
		depth: number,
		phase: number,
		influenceRadius: number,
	} = {
		x: 600,
		y: 400,
		depth: 2.7 + Math.random(),
		phase: Math.random() * Math.PI * 2,
		influenceRadius: 180
	};

	// Recent node tracking for efficient physics
	recentNodeIds: number[] = [];
	recentNodeWindow: number = 400;
	sunEnabled: any;
	getRenderableNodes: any;
	edgeEnergyFlow: boolean = false;
	sunPosition: any;

	// Remove buffer limit for full chaos
	// nodeBuffer: number[] = [];
	// maxBufferSize: number = 1200;

	// Add a stability metric
	stabilityMetric: number = 0;

	// Add this property to track heart pulse state
	lastHeartPulseStep: number = -1;

	// Mouth system for articulatory control
	mouthSystem: MouthSystem;

	constructor() {
		this.nodes = [];
		this.edges = [];
		this.wavePatterns = [];
		this.step = 0;
		this.waves = [];
		this.branches = [];
		this.spaceEnergies = [];
		
		// Initialize mathematical models for life behaviors
		this.mathEngine = new LifeBehaviorMathEngine();
		this.nodeOscillatorStates = new Map();
		
		// Initialize breathing system
		this.breathingSystem = createBreathingSystem(this);

		// Initialize mouth system (articulators for voice shaping)
		this.mouthSystem = new MouthSystem();
		
		// Initialize creature's intrinsic voice system with breathing system
		this.creatureVoice = createCreatureVoiceSystem(this, this.breathingSystem);
		
		// Initialize reverb node
		this.initializeReverbNode();
		
		// Initialize limb system
		this.limbSystem = new LimbSystem(this);
		
		// Initialize energy system
		this.energySystem = new EnergySystem(this);
		
		// Initialize living language system
		this.livingLanguage = new LivingLanguageSystem(this);
		
		// Initialize conceptual abstraction system
		this.conceptualAbstraction = new ConceptualAbstractionSystem(this);
		this.initializeMetaArchitecture();
		
		// Initialize organ system (depends on other systems)
		this.organSystem = new OrganSystem(this, this.limbSystem, this.conceptualAbstraction, this.livingLanguage);
		
		// Pre-populate basic linguistic elements for immediate audio
		this.initializeBasicLinguisticElements();
		
		// Initialize musical scales and patterns
		this.initializeMusicalScales();
		
		// Initialize language learning system
		this.initializeLanguageLearning();
		
		this.generateNetwork();
		this.addCrystalHeart();
		this.seedNodeEnergies();
	}

	exportState(): string {
		// Export comprehensive simulation state for save functionality
		const state = {
			// Core network data
			nodes: this.nodes.map(n => ({
				id: n.id,
				x: n.x,
				y: n.y,
				z: n.z,
				value: n.value,
				energy: n.energy,
				color: n.color,
				nodeType: n.nodeType
			})),
			edges: this.edges,
			branches: this.branches,
			
			// Simulation state
			step: this.step,
			creatureState: this.creatureState,
			totalNetworkEnergy: this.totalNetworkEnergy,
			
			// Breathing system state
			breathingState: this.breathingSystem.getBreathingState(),
			
			// Voice system state
			voiceUnlocked: this.breathingSystem.getBreathingState().voiceUnlocked,
			
			// Linguistic elements
			linguisticElements: Array.from(this.linguisticElements.values()),
			
			// Musical patterns
			musicalPatterns: Array.from(this.musicalPatterns.values()),
			discoveredMelodies: this.discoveredMelodies,
			currentMusicalKey: this.currentMusicalKey,
			currentMusicalMode: this.currentMusicalMode,
			
			// Timestamp
			exportedAt: Date.now(),
			version: '1.0'
		};
		
		return JSON.stringify(state);
	}

	importState(stateJson: string): void {
		try {
			const state = JSON.parse(stateJson);
			
			// Restore core network data
			this.nodes = state.nodes;
			this.edges = state.edges;
			this.branches = state.branches;
			
			// Restore simulation state
			this.step = state.step || 0;
			this.creatureState = state.creatureState || 'growing';
			this.totalNetworkEnergy = state.totalNetworkEnergy || 0;
			
			// Restore linguistic elements
			if (state.linguisticElements) {
				state.linguisticElements.forEach((el: LinguisticElement) => {
					this.linguisticElements.set(el.phoneticValue, el);
				});
			}
			
			// Restore musical patterns
			if (state.musicalPatterns) {
				state.musicalPatterns.forEach((pattern: MusicalPattern) => {
					this.musicalPatterns.set(pattern.id, pattern);
				});
			}
			
			if (state.discoveredMelodies) {
				this.discoveredMelodies = state.discoveredMelodies;
			}
			
			if (state.currentMusicalKey) {
				this.currentMusicalKey = state.currentMusicalKey;
			}
			
			if (state.currentMusicalMode) {
				this.currentMusicalMode = state.currentMusicalMode;
			}
			
			// Restore breathing state
			if (state.breathingState) {
				this.breathingSystem.restoreState(state.breathingState);
			}
			
			console.log('[Simulation] State imported successfully');
		} catch (error) {
			console.error('[Simulation] Failed to import state:', error);
			throw new Error('Invalid state format');
		}
	}

	// --- Skeletal Neural Network Generation ---
	generateNetwork(depth: number = GOLDEN_RATIO, spread: number = 120, centerX: number = 300, centerY: number = 200, centerZ: number = 0) {
		let nodeId = 0;
		
		// Skeletal structure parameters
		const layerCount = 4; // Number of neural layers
		const nodesPerLayer = 8; // Nodes per layer for structured growth
		const layerSpacing = 80; // Distance between layers
		const nodeSpacing = 60; // Distance between nodes in same layer
		const branchCount = 6; // Number of main neural pathways
		
		// Safety check: ensure initial generation doesn't exceed node limit
		const currentLimit = this.getCurrentNodeLimit();
		const estimatedNodes = layerCount * nodesPerLayer + 1; // +1 for crystal heart
		if (estimatedNodes > currentLimit) {
			console.warn(`[Creature ${this.creatureId}] Initial network generation would exceed node limit (${estimatedNodes} > ${currentLimit}). Adjusting parameters.`);
			// Reduce nodes per layer to fit within limit
			const maxNodesPerLayer = Math.floor((currentLimit - 1) / layerCount);
			const adjustedNodesPerLayer = Math.min(nodesPerLayer, maxNodesPerLayer);
			console.log(`[Creature ${this.creatureId}] Adjusted nodes per layer to ${adjustedNodesPerLayer} for safe initial generation.`);
		}

		// Generate structured neural layers
		for (let layer = 0; layer < layerCount; layer++) {
			const layerZ = centerZ + (layer - layerCount / 2) * layerSpacing;
			const layerNodes: number[] = [];
			
			// Create nodes in this layer with structured positioning
			for (let nodeInLayer = 0; nodeInLayer < nodesPerLayer; nodeInLayer++) {
				const id = nodeId++;
				const angle = (nodeInLayer / nodesPerLayer) * Math.PI * 2;
				const radius = 40 + layer * 15; // Expand outward with each layer
				
				// Structured positioning - no random variation
				const x = centerX + Math.cos(angle) * radius;
				const y = centerY + Math.sin(angle) * radius;
				const z = layerZ;
				
				const value = Math.random();
				const energy = 0.5 + Math.random() * 0.5;
				const color = `hsl(${Math.floor(value * 360)}, 70%, 60%)`;
				
				this.nodes.push({
					id, x, y, z, value, energy, energyStorage: 0, color, highlight: 0, corroded: false, vz: 0
				});
				layerNodes.push(id);
			}
			
			// Connect nodes within the same layer (lateral connections) - DISTANCE-BASED
			const maxLateralDistance = 120;
			for (let i = 0; i < layerNodes.length; i++) {
				const nodeAId = layerNodes[i];
				const nodeA = this.nodes[nodeAId];
				if (!nodeA) continue;
				
				// Connect to next node if close enough
				const nodeBId = layerNodes[(i + 1) % layerNodes.length];
				const nodeB = this.nodes[nodeBId];
				if (!nodeB) continue;
				
				const dx = nodeA.x - nodeB.x;
				const dy = nodeA.y - nodeB.y;
				const dz = (nodeA.z || 0) - (nodeB.z || 0);
				const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
				
				if (distance < maxLateralDistance) {
					this.edges.push({ 
						from: nodeAId, 
						to: nodeBId, 
						weight: 0.8 + Math.random() * 0.2 * (1 - distance / maxLateralDistance)
					});
				}
			}
			
			// Connect to previous layer (forward connections) - DISTANCE-BASED
			const maxForwardDistance = 150;
			if (layer > 0) {
				const prevLayerNodes = this.branches[this.branches.length - 1];
				for (let i = 0; i < layerNodes.length; i++) {
					const fromNodeId = layerNodes[i];
					const fromNode = this.nodes[fromNodeId];
					if (!fromNode) continue;
					
					// Find nearest node in previous layer
					let nearestPrevNodeId: number | null = null;
					let minDistance = Infinity;
					
					for (let j = 0; j < prevLayerNodes.length; j++) {
						const prevNodeId = prevLayerNodes[j];
						const prevNode = this.nodes[prevNodeId];
						if (!prevNode) continue;
						
						const dx = fromNode.x - prevNode.x;
						const dy = fromNode.y - prevNode.y;
						const dz = (fromNode.z || 0) - (prevNode.z || 0);
						const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
						
						if (distance < maxForwardDistance && distance < minDistance) {
							minDistance = distance;
							nearestPrevNodeId = prevNodeId;
						}
					}
					
					if (nearestPrevNodeId !== null && minDistance < maxForwardDistance) {
						this.edges.push({ 
							from: nearestPrevNodeId, 
							to: fromNodeId, 
							weight: 0.9 + Math.random() * 0.1 * (1 - minDistance / maxForwardDistance)
						});
					}
				}
			}
			
			this.branches.push(layerNodes);
			this.branchSelfSustaining.push(false);
		}
		
		// Create main neural pathways (branches)
		for (let b = 0; b < branchCount; b++) {
			const branch: number[] = [];
			const branchAngle = (b / branchCount) * Math.PI * 2;
			const branchStartRadius = 60;
			
			// Start from the center and extend outward
			let x = centerX + Math.cos(branchAngle) * branchStartRadius;
			let y = centerY + Math.sin(branchAngle) * branchStartRadius;
			let z = centerZ;
			let parentId = -1;
			
			// Create branch nodes with structured spacing
			for (let d = 0; d < Math.floor(depth); d++) {
				const id = nodeId++;
				const value = Math.random();
				const energy = 0.3 + Math.random() * 0.4;
				const color = `hsl(${Math.floor(value * 360)}, 70%, 60%)`;
				
				// Structured positioning along branch
				const branchLength = 40 + d * 25; // Increasing length
				x = centerX + Math.cos(branchAngle) * (branchStartRadius + branchLength);
				y = centerY + Math.sin(branchAngle) * (branchStartRadius + branchLength);
				z = centerZ + (Math.random() - 0.5) * 20; // Small Z variation for 3D effect
				
				this.nodes.push({
					id, x, y, z, value, energy, energyStorage: 0, color, highlight: 0, corroded: false, vz: 0
				});
				branch.push(id);
				
				if (parentId >= 0) {
					this.edges.push({ 
						from: parentId, 
						to: id, 
						weight: 0.7 + Math.random() * 0.3 
					});
				}
				
				parentId = id;
			}
			
			this.branches.push(branch);
			this.branchSelfSustaining.push(false);
		}

		// Create structured cross-connections between layers and branches
		// CRITICAL FIX: Only connect nearby nodes based on actual 3D distance
		const maxCrossConnectionDistance = 100; // Maximum distance for cross-connections
		
		for (let layer = 0; layer < this.branches.length - 1; layer++) {
			const currentLayer = this.branches[layer];
			const nextLayer = this.branches[layer + 1];
			
			// Create cross-connections only between nodes that are physically close
			for (let i = 0; i < currentLayer.length; i++) {
				const fromNodeId = currentLayer[i];
				const fromNode = this.nodes[fromNodeId];
				if (!fromNode) continue;
				
				// Find nearest node in next layer
				let nearestNodeId: number | null = null;
				let minDistance = Infinity;
				
				for (let j = 0; j < nextLayer.length; j++) {
					const toNodeId = nextLayer[j];
					const toNode = this.nodes[toNodeId];
					if (!toNode) continue;
					
					const dx = fromNode.x - toNode.x;
					const dy = fromNode.y - toNode.y;
					const dz = (fromNode.z || 0) - (toNode.z || 0);
					const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
					
					if (distance < maxCrossConnectionDistance && distance < minDistance) {
						minDistance = distance;
						nearestNodeId = toNodeId;
					}
				}
				
				// Only create edge if nodes are close enough
				if (nearestNodeId !== null) {
					// Calculate weight based on distance (closer = stronger)
					const weight = 0.6 + (1 - minDistance / maxCrossConnectionDistance) * 0.3;
					
					// Check if edge already exists (avoid duplicates)
					const edgeExists = this.edges.some(e => 
						(e.from === fromNodeId && e.to === nearestNodeId) || 
						(e.from === nearestNodeId && e.to === fromNodeId)
					);
					
					if (!edgeExists) {
						this.edges.push({ 
							from: fromNodeId, 
							to: nearestNodeId, 
							weight: weight
						});
					}
				}
			}
		}
		
		// Connect branches to the central layers for neural pathway integration
		// CRITICAL FIX: Only connect nodes that are physically close to each other
		const centralLayers = this.branches.slice(0, layerCount);
		const neuralBranches = this.branches.slice(layerCount);
		const maxConnectionDistance = 80; // Maximum distance for edge creation
		
		for (let branchIdx = 0; branchIdx < neuralBranches.length; branchIdx++) {
			const branch = neuralBranches[branchIdx];
			if (branch.length === 0) continue;
			
			const branchStartNode = this.nodes[branch[0]];
			if (!branchStartNode) continue;
			
			// Find the nearest central layer node that's actually close
			let nearestNodeId: number | null = null;
			let minDistance = Infinity;
			
			// Search through all central layers to find the closest node
			for (const layer of centralLayers) {
				for (const nodeId of layer) {
					const centralNode = this.nodes[nodeId];
					if (!centralNode) continue;
					
					const dx = branchStartNode.x - centralNode.x;
					const dy = branchStartNode.y - centralNode.y;
					const dz = (branchStartNode.z || 0) - (centralNode.z || 0);
					const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
					
					// Only consider nodes within max connection distance
					if (distance < maxConnectionDistance && distance < minDistance) {
						minDistance = distance;
						nearestNodeId = nodeId;
					}
				}
			}
			
			// Only create edge if we found a nearby node
			if (nearestNodeId !== null) {
				// Calculate weight based on distance (closer = stronger)
				const weight = 0.7 + (1 - minDistance / maxConnectionDistance) * 0.3;
				
				this.edges.push({ 
					from: nearestNodeId, 
					to: branch[0], 
					weight: weight
				});
			}
		}
	}

	addCrystalHeart() {
		// Safety check: ensure we don't exceed node limit when adding crystal heart
		const currentLimit = this.getCurrentNodeLimit();
		if (this.nodes.length >= currentLimit) {
			console.error(`[Creature ${this.creatureId}] Cannot add crystal heart: node limit reached (${this.nodes.length}/${currentLimit})`);
			return;
		}
		
		const heartId = this.nodes.length;
		this.crystalHeartId = heartId;
		const centerX = 300, centerY = 200, centerZ = 0; // 3D center position
		this.nodes.push({
			id: heartId,
			x: centerX,
			y: centerY,
			z: centerZ,
			value: 1,
			energy: 888,
			energyStorage: 888,
			color: 'hsl(290, 90%, 70%)',
			highlight: 1000,
			vz: 0
		});
		
		// CRITICAL FIX: Only connect heart to nearby branch starts
		const maxHeartDistance = 200; // Maximum distance from heart to connect
		
		for (const branch of this.branches) {
			if (branch.length > 0) {
				const branchStartNode = this.nodes[branch[0]];
				if (!branchStartNode) continue;
				
				const dx = branchStartNode.x - centerX;
				const dy = branchStartNode.y - centerY;
				const dz = (branchStartNode.z || 0) - centerZ;
				const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
				
				// Only connect if branch start is reasonably close to the heart
				if (distance < maxHeartDistance) {
					// Weight decreases with distance
					const weight = 1.0 - (distance / maxHeartDistance) * 0.3;
					this.edges.push({ from: heartId, to: branch[0], weight: Math.max(0.7, weight) });
				}
			}
		}
	}

	seedNodeEnergies() {
		for (const node of this.nodes) {
			if (node.id !== this.crystalHeartId) {
				node.energy = 10 + Math.random() * 10;
				node.energyStorage = 20 + Math.random() * 20;
			}
		}
	}

	getDynamicLoopThreshold(branchLength: number): number {
		const base = branchLength * 4.669;
		const stepFactor = Math.max(1, Math.floor(this.step / 500));
		return Math.min(1000, base * stepFactor);
	}

	mergeBranchesByColorProximity() {
		const colorTolerance = 18;
		const proximity = 32;
		const merged: Set<number> = new Set();

		for (let i = 0; i < this.branches.length; i++) {
			const branchA = this.branches[i];
			if (branchA.length === 0 || merged.has(i)) continue;
			const tipA = this.nodes[branchA[branchA.length - 1]];
			if (!tipA) continue;
			const hueA = this.extractHue(tipA.color);

			for (let j = i + 1; j < this.branches.length; j++) {
				if (merged.has(j)) continue;
				const branchB = this.branches[j];
				if (branchB.length === 0) continue;
				const tipB = this.nodes[branchB[branchB.length - 1]];
				if (!tipB) continue;
				const hueB = this.extractHue(tipB.color);

				if (Math.abs(hueA - hueB) <= colorTolerance) {
					const dist = Math.hypot(tipA.x - tipB.x, tipA.y - tipB.y);
					if (dist < proximity) {
						this.edges.push({ from: tipA.id, to: tipB.id, weight: 1 });
						branchA.push(...branchB);
						branchB.length = 0;
						merged.add(j);
					}
				}
			}
		}
		this.branches = this.branches.filter(b => b.length > 0);
	}

	extractHue(color: string): number {
		const match = color.match(/hsl\((\d+)/);
		return match ? parseInt(match[1]) : 0;
	}

	// Modify crystal heart pulse logic to visually propagate energy as "lightning" and spawn new nodes at ends
	energizeNodes() {
		let totalIterations = 0;
		const maxTotalIterations = 5000;
		const maxBounceTotalGuard = 300;
		for (let branchIdx = 0; branchIdx < this.branches.length; branchIdx++) {
			const branch = this.branches[branchIdx];
			if (branch.length === 0) continue;
			const totalBranchEnergy = branch.reduce((sum, nid) => sum + (this.nodes[nid]?.energy ?? 0), 0);
			const selfSustainThreshold = branch.length * 8;
			if (!this.branchSelfSustaining[branchIdx] && totalBranchEnergy > selfSustainThreshold) {
				this.branchSelfSustaining[branchIdx] = true;
			}
			if (this.branchSelfSustaining[branchIdx]) {
				for (const nid of branch) {
					const node = this.nodes[nid];
					if (node && !node.corroded) {
						node.energy = Math.max(0, node.energy * 0.98 + 0.02 * totalBranchEnergy / branch.length);
						node.highlight = Math.max(node.highlight || 0, 10);
					}
				}
				continue;
			}
			if (branch.length === 1) {
				const node = this.nodes[branch[0]];
				if (node && !node.corroded) {
					node.energy = Math.max(0, Math.min(node.energy, 0.5 + 0.5 * Math.sin((this.step / 18) + branch[0]) + (Math.random() - 0.5) * 0.2));
					if (node.energy < 2 && node.energyStorage && node.energyStorage > 0) {
						const transfer = Math.min(2, node.energyStorage);
						node.energy += transfer;
						node.energyStorage -= transfer;
					}
					if (node.energy < this.corrodeThreshold) {
						node.corroded = true;
						node.corrodeStep = this.step;
						logNodeFailure(this.debugTrace, this.step, node, "energy below corrodeThreshold");
					}
				}
				continue;
			}
			let pulse = 0.5 + 0.5 * Math.sin((this.step / 18) + branch[0]);
			let direction = 1;
			let i = 0;
			let bounceCount = 0;
			const maxBounces = 4;
			let pulseBuffer = 0;
			const maxBranchLength = 50;
			if (branch.length > maxBranchLength) continue;
			const maxNodesPerFrame = 100;
			if (branch.length > maxNodesPerFrame) continue;
			bounceLoop: while (bounceCount < maxBounces && branch.length > 1) {
				let loopCount = 0;
				const maxLoopCount = Math.min(this.getDynamicLoopThreshold(branch.length), 60);
				let bounced = false;
				let prevBounceCount = bounceCount;
				let prevI = i;
				let bounceGuard = 0;
				let bounceLoopGuard = 0;
				let bounceDirectionGuard = 0;
				let lastDirection = direction;
				let bounceEndGuard = 0;
				let bounceTotalGuard = 0;
				while (i >= 0 && i < branch.length) {
					loopCount++;
					totalIterations++;
					bounceLoopGuard++;
					bounceTotalGuard++;
					if (lastDirection !== direction) {
						bounceDirectionGuard++;
						lastDirection = direction;
					}
					if (loopCount > maxLoopCount || totalIterations > maxTotalIterations) {
						this.simulationFailed = true;
						break bounceLoop;
					}
					if (bounceTotalGuard > maxBounceTotalGuard) break bounceLoop;
					if (bounceLoopGuard > branch.length * 2 || bounceDirectionGuard > maxBounces * 2) break bounceLoop;
					const node = this.nodes[branch[i]];
					if (!node) { i += direction; continue; }
					if (!node.corroded) {
						node.energy = Math.max(0, Math.min(node.energy, pulse + (Math.random() - 0.5) * 0.2));
						if (node.energy < 2 && node.energyStorage && node.energyStorage > 0) {
							const transfer = Math.min(2, node.energyStorage);
							node.energy += transfer;
							node.energyStorage -= transfer;
						}
						pulse *= 0.85 + Math.random() * 0.1;
						if ((i === branch.length - 1 && direction === 1) || (i === 0 && direction === -1)) {
							bounceEndGuard++;
							if (bounceEndGuard > maxBounces * 2) break bounceLoop;
							let oldI = i;
							i += direction;
							if (i < 0) i = branch.length - 1;
							if (i >= branch.length) i = 0;
							direction *= -1;
							pulse = Math.abs(pulse);
							bounceCount++;
							pulseBuffer += pulse;
							bounced = true;
							if (i === oldI) break;
							prevI = i;
							bounceGuard++;
							if (bounceGuard > branch.length) break;
							break;
						}
						if (node.energy < this.corrodeThreshold) {
							node.corroded = true;
							node.corrodeStep = this.step;
							logNodeFailure(this.debugTrace, this.step, node, "energy below corrodeThreshold");
						}
					}
					i += direction;
				}
				if (i < 0) i = 0;
				if (i >= branch.length) i = branch.length - 1;
				if (!bounced) break;
				// Final infinite loop guard: if bounceCount did not increment, break
				if (bounceCount === prevBounceCount) break;
				if (bounceCount >= maxBounces) break;
			}
			if (pulseBuffer > 0 && this.crystalHeartId !== null) {
				const heart = this.nodes[this.crystalHeartId];
				if (heart && !heart.corroded) {
					heart.energy += pulseBuffer;
					heart.highlight = Math.max(heart.highlight || 0, 40);
				}
			}
		}
		// Crystal heart pulse: every crystalHeartPulseInterval steps
		if (this.crystalHeartId !== null && this.step % this.crystalHeartPulseInterval === 0) {
			const heart = this.nodes[this.crystalHeartId];
			const pulseStep = Math.floor(this.step / this.crystalHeartPulseInterval);
			this.lastHeartPulseStep = pulseStep;
			// Mark chain lightning active for a short window to drive zap drone
			this.chainLightningActive = true;
			this.lastChainLightningAt = Date.now();

			// --- Heart beat visual ---
			if (heart) {
				heart.highlight = 300; // strong highlight for visibility
				heart.color = 'hsl(340, 100%, 65%)'; // vivid magenta/pink for heart beat
			}

			// Find all branch tips (ends)
			const tips: Node[] = [];
			for (let b = 0; b < this.branches.length; b++) {
				const branch = this.branches[b];
				if (branch.length === 0) continue;
				const tipNode = this.nodes[branch[branch.length - 1]];
				if (tipNode && !tipNode.corroded) {
					tips.push(tipNode);
				}
			}

			// Chain lightning and nodal clusters at chain ends
			for (const tip of tips) {
				tip.highlight = 120 + Math.floor(Math.random() * 40);
				tip.color = 'hsl(60, 100%, 80%)';
				tip.energy += 18 + Math.random() * 12;
				// No short zaps; drone will represent electricity during chain lightning

				// --- Structured neural growth at tip ---
				const clusterSize = Math.min(2 + Math.floor(Math.random() * 2), 4); // 2-3 nodes per cluster, max 4
				let prevNode = tip;
				let chainEndNode = tip;
				let accumulatedDistance = 0;
				for (let c = 0; c < clusterSize; c++) {
					// Check cycle limit before each potential generation
					if (!this.canGenerateNode()) {
						break;
					}
					
					// Use fractional generation for structured growth
					const chainProbability = 0.8; // High probability for structured growth
					if (this.canGenerateFractionalNode(chainProbability)) {
						// Structured positioning - extend along the branch direction
						const branchDirection = this.calculateBranchDirection(tip, this.branches.find(b => b.includes(tip.id)) || []);
							const distance = 35 + c * 15; // Structured spacing
						const x = prevNode.x + branchDirection.x * distance;
						const y = prevNode.y + branchDirection.y * distance;
						const z = prevNode.z + branchDirection.z * distance * 0.3; // Small Z variation
						
						const minDist = 18;
						let overlaps = false;
						for (const node of this.nodes) {
							const d = Math.hypot(node.x - x, node.y - y) + Math.abs(node.z - z) * 0.5;
							if (d < minDist) {
								overlaps = true;
								break;
							}
						}
						if (!overlaps) {
							// Increment counter and create node
							if (this.incrementNodeCounter()) {
								const value = Math.min(1, Math.max(0, tip.value + (Math.random() - 0.5) * 0.1));
								const energy = Math.random() * 1.5 + 0.5 - c * 0.2;
								const color = 'hsl(60, 100%, 80%)';
								const id = this.nodes.length;
								const newNode = { id, x, y, z, value, energy, color, highlight: 100, vz: 0 };
								this.nodes.push(newNode);
								this.edges.push({ from: prevNode.id, to: id, weight: 0.8 + Math.random() * 0.2 });
								this.branches.push([id]);
								if (this.onNodeGrowth) this.onNodeGrowth(1, color);
								prevNode = newNode;
								chainEndNode = newNode;
									accumulatedDistance += distance;
								if (this.onCrystalHeartPulse) this.onCrystalHeartPulse('buzz');
							}
						}
					}
				}

				// --- Spawn nodal cluster at chain end as energetic expense ---
				const nodalClusterSize = Math.min(2 + Math.floor(Math.random() * 3), 8); // 2-4 nodes per cluster, max 8
				for (let n = 0; n < nodalClusterSize; n++) {
					// Check cycle limit before each potential generation
					if (!this.canGenerateNode()) {
						break;
					}
					
					// Use fractional generation for nodal clusters
					const clusterProbability = 0.8; // High probability for clusters
					if (this.canGenerateFractionalNode(clusterProbability)) {
						const angle = Math.random() * Math.PI * 2;
						const distance = 32 + Math.random() * 24;
						const x = chainEndNode.x + Math.cos(angle) * distance;
						const y = chainEndNode.y + Math.sin(angle) * distance;
						const minDist = 22;
						let overlaps = false;
						for (const node of this.nodes) {
							const d = Math.hypot(node.x - x, node.y - y);
							if (d < minDist) {
								overlaps = true;
								break;
							}
						}
						if (!overlaps) {
							// Increment counter and create node
							if (this.incrementNodeCounter()) {
								const value = Math.min(1, Math.max(0, chainEndNode.value + (Math.random() - 0.5) * 0.2));
								const energy = Math.random() * 1.2 + 0.3;
								const color = 'hsl(55, 100%, 75%)'; // slightly different yellow
								const id = this.nodes.length;
								const newNode = { id, x, y, z: Math.random() * 100 - 50, value, energy, color, highlight: 80, vz: 0 };
								this.nodes.push(newNode);
								this.edges.push({ from: chainEndNode.id, to: id, weight: Math.random() });
								this.branches.push([id]);
								if (this.onNodeGrowth) this.onNodeGrowth(1, color);
							}
						}
					}
				}
			}

			// Also energize all nodes and branches as before
			for (let b = 0; b < this.branches.length; b++) {
				const branch = this.branches[b];
				if (this.branchSelfSustaining[b]) continue;
				for (const nodeId of branch) {
					const node = this.nodes[nodeId];
					if (node && !node.corroded) {
						node.energy += 10;
						node.highlight = 80;
					}
				}
			}
			for (const node of this.nodes) {
				if (node.id !== this.crystalHeartId && !node.corroded) {
					node.energy += 2;
					node.highlight = Math.max(node.highlight || 0, 30);
				}
			}
			// Trigger crystal heart pulse sound
			if (this.onCrystalHeartPulse) this.onCrystalHeartPulse('heartbeat');
		}
	}

	// Add this property to track recent nodes
	// recentNodeIds: number[] = [];

	// Update recentNodeIds whenever nodes grow
	addRecentNode(id: number) {
		this.recentNodeIds.push(id);
		if (this.recentNodeIds.length > this.recentNodeWindow) {
			this.recentNodeIds = this.recentNodeIds.slice(-this.recentNodeWindow);
		}
	}

	// Call this after every node growth
	bufferNode(id: number) {
		this.nodeBuffer.push(id);
		if (this.nodeBuffer.length > this.maxBufferSize) {
			const removeId = this.nodeBuffer.shift();
			if (typeof removeId === 'number') {
				this.removeNode(removeId);
			}
		}
	}

	// Remove node and clean up edges/branches
	removeNode(id: number) {
		// Remove from nodes
		this.nodes = this.nodes.filter(n => n.id !== id);
		// Remove from branches
		for (const branch of this.branches) {
			const idx = branch.indexOf(id);
			if (idx !== -1) branch.splice(idx, 1);
		}
		// Remove from recentNodeIds
		this.recentNodeIds = this.recentNodeIds.filter(nid => nid !== id);
		// Remove edges
		this.edges = this.edges.filter(e => e.from !== id && e.to !== id);
	}

	startSimulation(): void {
		// Reset node generation counter for this cycle
		this.resetNodeGenerationCounter();
		
		// Update creature state based on energy levels
		this.updateCreatureState();
		
		// Only grow if in growing state and haven't reached node limit
		if ((this.creatureState === 'growing' || this.creatureState === 'stabilizing') && !this.hasReachedNodeLimit) {
			this.simulateWavePatterns();
			this.applyPsychonautCreatureInfluence();
		}
		
		this.chaosStep(); // <-- inject chaos
		this.updateStabilityMetric(); // <-- update stability metric
		this.runPlugins();
		
		// Add sensing behavior when not growing
		if (this.creatureState === 'sensing' || this.creatureState === 'exploring') {
			this.performSensingBehavior();
		}
	}

	cycle() {
		this.startSimulation();
		this.applyPhysics();
		this.mergeBranchesByColorProximity();
		this.simulateEvolution();
		this.simulateWavePatterns();
		this.runPlugins();
		// Update continuous audio cues if available
		if (this.audioContext && this.audioContext.state === 'running') {
			this.updateZapDrone(this.audioContext.currentTime);
		}
		// Update archetypal cognition summary from current signals
		if (this.archetypeEngine) {
            // Bridge conceptual abstraction archetypes as external boosts when available
            if (this.conceptualAbstraction) {
                const boosts = computeArchetypeBoostsFromConceptual(this.conceptualAbstraction);
                this.archetypeEngine.setExternalBoosts(boosts, 0.4);
            }
			this.archetypeEngine.updateFromNetwork(this);
			this.archetypeMetrics = this.archetypeEngine.getSummary();
		}
		this.step++; // Increment simulation step
		return {
			nodes: this.nodes,
			edges: this.edges,
			branches: this.branches,
			waves: this.waves,
			step: this.step
		};
	}

	simulateWavePatterns() {
		const maxSimulationSteps = 100000;
		if (this.step > maxSimulationSteps) {
			this.simulationFailed = true;
			return;
		}
		this.wavePatterns = this.nodes.map((node, idx) =>
			Math.sin(this.step / 10 + idx / 3)
		);
		if (typeof this.spawnSpaceEnergy === 'function') this.spawnSpaceEnergy();
		if (typeof this.updateSpaceEnergies === 'function') this.updateSpaceEnergies();
		if (typeof this.energizeNodes === 'function') this.energizeNodes();
		if (typeof this.emitWaves === 'function') this.emitWaves();
		if (typeof this.updateWaves === 'function') this.updateWaves();
		if (typeof this.simulateEvolution === 'function') this.simulateEvolution();
		for (const node of this.nodes) {
			if (node.highlight && node.highlight > 0) node.highlight--;
		}
		if (typeof this.removeCorrodedNodes === 'function') this.removeCorrodedNodes();
		this.step++;
	}

	spawnSpaceEnergy() {
		if (Math.random() < 0.03) {
			this.spaceEnergies.push({
				x: Math.random() * this.spaceSize.width,
				y: Math.random() * this.spaceSize.height,
				energy: 0.3 + Math.random() * 0.4,
				lifetime: 120 + Math.random() * 80
			});
		}
	}

	updateSpaceEnergies() {
		for (const se of this.spaceEnergies) {
			se.lifetime--;
		}
		this.spaceEnergies = this.spaceEnergies.filter(se => se.lifetime > 0);
		for (const node of this.nodes) {
			if (node.corroded) continue;
			const isLone = !this.edges.some(e => e.from === node.id || e.to === node.id);
			if (!isLone) continue;
			for (const se of this.spaceEnergies) {
				const dist = Math.hypot(node.x - se.x, node.y - se.y);
				if (dist < 30) {
					const absorb = se.energy;
					const maxStorage = 2.0;
					if (node.energy + absorb > 1) {
						const excess = node.energy + absorb - 1;
						node.energy = 1;
						node.energyStorage = Math.min(maxStorage, (node.energyStorage || 0) + excess);
					} else {
						node.energy = Math.min(1, node.energy + absorb);
					}
					if (node.energy > 0.85 && this.canGenerateNode()) {
						const angle = Math.random() * 2 * Math.PI;
						const distance = 40 + Math.random() * 40;
						const x = node.x + Math.cos(angle) * distance;
						const y = node.y + Math.sin(angle) * distance;
						const minDist = 22;
						let overlaps = false;
						for (const other of this.nodes) {
							const d = Math.hypot(other.x - x, other.y - y);
							if (d < minDist) {
								overlaps = true;
								break;
							}
						}
						if (!overlaps) {
							// Increment counter and create node
							if (this.incrementNodeCounter()) {
								const value = Math.min(1, Math.max(0, node.value + (Math.random() - 0.5) * 0.2));
								const energy = Math.random();
								const color = `hsl(${Math.floor(value * 360)}, 70%, 60%)`;
								const id = this.nodes.length;
								const newNode = { id, x, y, z: Math.random() * 100 - 50, value, energy, color, highlight: 30, vz: 0 };
								this.nodes.push(newNode);
								this.edges.push({ from: node.id, to: id, weight: Math.random() });
								this.branches.push([id]);
							}
						}
					}
					se.lifetime = 0;
				}
			}
		}
	}

	emitWaves() {
		let didPulse = false;
		if (this.step % this.pulseInterval === 0) {
			for (const branch of this.branches) {
				for (const nodeId of branch) {
					const node = this.nodes[nodeId];
					if (!node) continue;
					if (node.energy > 0.7) {
						this.waves.push({
							originId: node.id,
							x: node.x,
							y: node.y,
							radius: 10,
							speed: 0.8,
							active: true,
							color: node.color
						});
						didPulse = true;
					}
				}
			}
		}
		if (didPulse) {
			this.evolutionaryGrowthAfterPulse();
		}
	}

	updateWaves() {
		for (const wave of this.waves) {
			if (!wave.active) continue;
			wave.radius += wave.speed;
			if (wave.radius > 300) wave.active = false;
		}
		for (let i = 0; i < this.waves.length; i++) {
			const w1 = this.waves[i];
			if (!w1.active) continue;
			for (let j = i + 1; j < this.waves.length; j++) {
				const w2 = this.waves[j];
				if (!w2.active) continue;
				const dist = Math.hypot(w1.x - w2.x, w1.y - w2.y);
				if (Math.abs(w1.radius - w2.radius) < 8 && dist < w1.radius + w2.radius) {
					w1.active = false;
					w2.active = false;
				}
			}
		}
	}

	private clampRange(value: number, min: number, max: number): number {
		return Math.max(min, Math.min(max, value));
	}

	private initializeMetaArchitecture(): void {
		const domains: MetaDomain[] = ['network', 'breathing', 'voice', 'limb', 'energy', 'organ', 'language', 'conceptual', 'musical'];
		for (const domain of domains) {
			this.metaDomains.set(domain, {
				activation: 0.5,
				pressure: 0.5,
				novelty: 0.2,
				entropy: 0.3,
				lastDelta: 0
			});
		}

		this.proceduralProfiles = [
			{
				id: 'homeostatic-weave',
				name: 'Homeostatic Weave',
				emphasis: { network: 0.9, energy: 0.9, breathing: 0.7, language: 0.5, conceptual: 0.5 },
				cadence: { breathing: 0.95, limb: 0.7, language: 0.75, conceptual: 0.7, voiceEvolution: 1.0, morphology: 0.85 },
				mutationBias: 1.0,
				rewiringBias: 1.0
			},
			{
				id: 'respiratory-cascade',
				name: 'Respiratory Cascade',
				emphasis: { breathing: 1.0, voice: 0.95, language: 0.8, energy: 0.65 },
				cadence: { breathing: 1.0, limb: 0.55, language: 0.82, conceptual: 0.65, voiceEvolution: 1.3, morphology: 0.6 },
				mutationBias: 0.85,
				rewiringBias: 1.1
			},
			{
				id: 'limb-exploration-lattice',
				name: 'Limb Exploration Lattice',
				emphasis: { limb: 1.0, energy: 0.8, organ: 0.75, network: 0.7 },
				cadence: { breathing: 0.75, limb: 0.95, language: 0.55, conceptual: 0.6, voiceEvolution: 0.8, morphology: 0.9 },
				mutationBias: 1.1,
				rewiringBias: 1.2
			},
			{
				id: 'semantic-transcendence',
				name: 'Semantic Transcendence',
				emphasis: { language: 1.0, conceptual: 0.95, musical: 0.75, voice: 0.7 },
				cadence: { breathing: 0.72, limb: 0.45, language: 0.96, conceptual: 0.95, voiceEvolution: 1.15, morphology: 0.55 },
				mutationBias: 0.8,
				rewiringBias: 1.25
			},
			{
				id: 'morphogenic-surge',
				name: 'Morphogenic Surge',
				emphasis: { network: 1.0, energy: 0.95, organ: 0.8, limb: 0.75, musical: 0.5 },
				cadence: { breathing: 0.65, limb: 0.82, language: 0.52, conceptual: 0.62, voiceEvolution: 0.75, morphology: 1.0 },
				mutationBias: 1.25,
				rewiringBias: 1.35
			}
		];
	}

	private sampleSystemSnapshot(): SystemSnapshot {
		const activeNodes = this.nodes.filter(node => node && !node.corroded);
		const activeNodeCount = activeNodes.length;
		const currentLimit = this.getCurrentNodeLimit();
		const avgEnergy = activeNodeCount > 0 ? activeNodes.reduce((sum, node) => sum + (node.energy || 0), 0) / activeNodeCount : 0;
		const breathing = this.breathingSystem.getBreathingState();
		const limbCount = this.limbSystem.getLimbCount();
		const organCount = this.organSystem ? this.organSystem.getOrgans().length : 0;
		const ecology = this.livingLanguage.getLanguageEcology();
		const conceptCount = this.conceptualAbstraction.getAbstractConcepts().length;
		const totalEnergy = this.energySystem.getTotalEnergy();

		return {
			activeNodeCount,
			nodePressure: activeNodeCount / Math.max(1, currentLimit),
			avgEnergy,
			stability: this.clampRange(this.stabilityMetric, 0, 1),
			breathingMastery: this.clampRange((breathing as any).breathingMastery ?? 0, 0, 1),
			voiceUnlocked: (breathing as any).voiceUnlocked ? 1 : 0,
			limbCount,
			organCount,
			languageCreativity: this.clampRange((ecology as any).creativity ?? 0.5, 0, 1),
			conceptCount,
			musicalComplexity: this.clampRange(this.musicalComplexity / 10, 0, 1),
			totalEnergy
		};
	}

	private deriveDomainSignal(domain: MetaDomain, snapshot: SystemSnapshot): number {
		const currentLimit = this.getCurrentNodeLimit();
		switch (domain) {
			case 'network':
				return this.clampRange((1 - snapshot.nodePressure) * 0.4 + snapshot.stability * 0.3 + this.globalPlasticityScale * 0.3, 0, 1);
			case 'breathing':
				return snapshot.breathingMastery;
			case 'voice':
				return this.clampRange(snapshot.voiceUnlocked * 0.6 + snapshot.breathingMastery * 0.4, 0, 1);
			case 'limb':
				return this.clampRange(snapshot.limbCount / 24, 0, 1);
			case 'energy':
				return this.clampRange(snapshot.totalEnergy / Math.max(1, currentLimit), 0, 1);
			case 'organ':
				return this.clampRange(snapshot.organCount / 16, 0, 1);
			case 'language':
				return snapshot.languageCreativity;
			case 'conceptual':
				return this.clampRange(snapshot.conceptCount / 120, 0, 1);
			case 'musical':
				return snapshot.musicalComplexity;
		}
	}

	private updateMetaDomains(snapshot: SystemSnapshot): void {
		const pressureBase = this.clampRange(snapshot.nodePressure * 0.6 + (1 - snapshot.stability) * 0.4, 0, 1);
		for (const [domain, state] of this.metaDomains.entries()) {
			const signal = this.deriveDomainSignal(domain, snapshot);
			const delta = signal - state.activation;
			state.activation += delta * 0.12;
			state.pressure = this.clampRange(state.pressure * 0.7 + pressureBase * 0.3, 0, 1);
			state.novelty = this.clampRange(state.novelty * 0.82 + Math.abs(delta) * 0.55 + Math.random() * 0.02, 0, 1);
			state.entropy = this.clampRange(state.entropy * 0.86 + state.novelty * 0.14, 0, 1);
			state.lastDelta = delta;
		}

		const linkPairs: Array<[MetaDomain, MetaDomain]> = [
			['breathing', 'voice'],
			['voice', 'language'],
			['language', 'conceptual'],
			['limb', 'energy'],
			['organ', 'energy'],
			['musical', 'language'],
			['network', 'limb']
		];

		for (const [a, b] of linkPairs) {
			const key = `${a}->${b}`;
			const aState = this.metaDomains.get(a);
			const bState = this.metaDomains.get(b);
			if (!aState || !bState) continue;
			const coupling = this.clampRange((aState.activation + bState.activation) * 0.5 - Math.abs(aState.activation - bState.activation), 0, 1);
			const previous = this.metaLinks.get(key) ?? 0.5;
			this.metaLinks.set(key, this.clampRange(previous * 0.85 + coupling * 0.15, 0, 1));
		}
	}

	private chooseProceduralProfile(): void {
		if (this.proceduralProfiles.length === 0) return;

		let best: ProceduralProfile | null = null;
		let bestScore = -Infinity;
		for (const profile of this.proceduralProfiles) {
			let score = 0;
			for (const [domain, weight] of Object.entries(profile.emphasis) as Array<[MetaDomain, number]>) {
				const state = this.metaDomains.get(domain);
				if (!state) continue;
				score += state.activation * weight + state.novelty * 0.25;
			}
			score += (Math.random() - 0.5) * 0.06;
			if (score > bestScore) {
				bestScore = score;
				best = profile;
			}
		}

		if (best && (Math.random() > 0.12 || this.activeProceduralProfileId === best.id)) {
			this.activeProceduralProfileId = best.id;
		} else {
			const randomProfile = this.proceduralProfiles[Math.floor(Math.random() * this.proceduralProfiles.length)];
			this.activeProceduralProfileId = randomProfile.id;
		}
	}

	private getActiveProceduralProfile(): ProceduralProfile {
		const found = this.proceduralProfiles.find(profile => profile.id === this.activeProceduralProfileId);
		return found ?? this.proceduralProfiles[0];
	}

	private computeProceduralCadence(snapshot: SystemSnapshot): ProceduralCadence {
		const profile = this.getActiveProceduralProfile();
		const softCap = this.computeSoftCapacity();
		const capacityPressure = snapshot.activeNodeCount / Math.max(1, softCap);
		const noveltyMean = Array.from(this.metaDomains.values()).reduce((sum, state) => sum + state.novelty, 0) / Math.max(1, this.metaDomains.size);

		const pressureBrake = this.clampRange(1 - Math.max(0, capacityPressure - 0.8) * 0.9, 0.25, 1);
		const noveltyBoost = this.clampRange(0.85 + noveltyMean * 0.4, 0.85, 1.25);

		this.structuralMutationBias = this.clampRange(profile.mutationBias * pressureBrake * this.globalPlasticityScale, 0.35, 1.65);
		this.structuralRewiringBias = this.clampRange(profile.rewiringBias * (0.8 + capacityPressure * 0.5) * noveltyBoost, 0.65, 2.25);
		this.rewireCooldown = Math.max(4, Math.floor(14 / this.structuralRewiringBias));

		const targetLimbRate = this.clampRange(profile.cadence.limb * pressureBrake, 0.05, 0.95);
		this.limbGrowthRate += (targetLimbRate - this.limbGrowthRate) * 0.08;

		return {
			breathingChance: this.clampRange(profile.cadence.breathing * pressureBrake * noveltyBoost, 0.4, 1),
			limbChance: this.clampRange(profile.cadence.limb * pressureBrake * noveltyBoost, 0.35, 1),
			languageChance: this.clampRange(profile.cadence.language * noveltyBoost, 0.35, 1),
			conceptualChance: this.clampRange(profile.cadence.conceptual * noveltyBoost, 0.3, 1),
			voiceEvolutionScale: this.clampRange(profile.cadence.voiceEvolution * noveltyBoost, 0.4, 1.8),
			morphologyChance: this.clampRange(profile.cadence.morphology * pressureBrake * noveltyBoost, 0.25, 1)
		};
	}

	private updateMetaArchitecture(): ProceduralCadence {
		const snapshot = this.sampleSystemSnapshot();
		this.updateMetaDomains(snapshot);

		if (this.step - this.lastMetaMorphStep >= this.metaMorphInterval) {
			this.chooseProceduralProfile();
			this.lastMetaMorphStep = this.step;
		}

		const cadence = this.computeProceduralCadence(snapshot);
		if (this.step % 180 === 0) {
			const profile = this.getActiveProceduralProfile();
			console.log(`[Meta Architecture] Profile=${profile.name}, morph=${cadence.morphologyChance.toFixed(2)}, lang=${cadence.languageChance.toFixed(2)}, concept=${cadence.conceptualChance.toFixed(2)}, mutationBias=${this.structuralMutationBias.toFixed(2)}, rewireBias=${this.structuralRewiringBias.toFixed(2)}`);
		}

		return cadence;
	}

	private getCurrentFractalStage(): FractalUpgradeStage {
		return this.fractalUpgradeStages[Math.max(0, Math.min(this.currentFractalUpgradeStageIndex, this.fractalUpgradeStages.length - 1))];
	}

	private getCurrentNodeLimit(): number {
		const stage = this.getCurrentFractalStage();
		const expanded = Math.floor(this.maxNodesPerCreature * stage.multiplier);
		return Math.max(this.maxNodesPerCreature, Math.min(this.absoluteNodeLimit, expanded));
	}

	private maybeAdvanceFractalUpgrade(): void {
		if (this.currentFractalUpgradeStageIndex >= this.fractalUpgradeStages.length - 1) {
			return;
		}

		const nextStage = this.fractalUpgradeStages[this.currentFractalUpgradeStageIndex + 1];
		const activeNodes = this.nodes.filter(node => node && !node.corroded);
		const avgEnergy = activeNodes.length > 0
			? activeNodes.reduce((sum, node) => sum + (node.energy || 0), 0) / activeNodes.length
			: 0;
		const conceptCount = this.conceptualAbstraction ? this.conceptualAbstraction.getAbstractConcepts().length : 0;
		const languageCreativity = this.clampRange(this.livingLanguage.getLanguageEcology().creativity ?? 0, 0, 1);

		const canUpgrade = this.step >= nextStage.minStep &&
			this.stabilityMetric >= nextStage.minStability &&
			avgEnergy >= nextStage.minAvgEnergy &&
			conceptCount >= nextStage.minConcepts &&
			languageCreativity >= nextStage.minLanguageCreativity;

		if (!canUpgrade) {
			return;
		}

		this.currentFractalUpgradeStageIndex++;
		this.lastFractalUpgradeStep = this.step;
		const previous = this.fractalUpgradeStages[this.currentFractalUpgradeStageIndex - 1];
		const current = this.getCurrentFractalStage();
		const previousLimit = Math.min(this.absoluteNodeLimit, Math.floor(this.maxNodesPerCreature * previous.multiplier));
		const currentLimit = this.getCurrentNodeLimit();
		console.log(`[Fractal Upgrade] ${previous.name} -> ${current.name} | limit ${previousLimit} -> ${currentLimit} (x${current.multiplier.toFixed(3)})`);
	}

	// Helper method to check if we can generate more nodes this cycle (fractional)
	canGenerateNode(): boolean {
		// Check both per-cycle limit AND total creature hard limit
		const currentNodeCount = this.nodes.filter(node => !node.corroded).length;
		const currentLimit = this.getCurrentNodeLimit();
		if (currentNodeCount >= currentLimit) {
			return false;
		}

		const softCap = this.computeSoftCapacity();
		const capacityPressure = currentNodeCount / Math.max(1, softCap);
		const phaseScale = this.evolutionPhase === 'complexify' ? 1.0 : this.evolutionPhase === 'rebalance' ? 0.65 : 0.35;
		const dynamicCycleLimit = this.maxNodesPerCycle * phaseScale * this.getHomeostaticGrowthFactor();

		if (this.evolutionPhase === 'simplify' && capacityPressure > 0.75) {
			return false;
		}

		return this.nodesGeneratedThisCycle < dynamicCycleLimit;
	}

	// Helper method to check fractional node generation with smooth accumulation
	canGenerateFractionalNode(probability: number = 1.0): boolean {
		// First check if we can generate any nodes at all
		if (!this.canGenerateNode()) {
			return false;
		}
		
		const adjustedProbability = probability * 0.369 * this.structuralMutationBias;
		this.nodeGenerationAccumulator += adjustedProbability;
		
		if (this.nodeGenerationAccumulator >= 1.0 && this.canGenerateNode()) {
			this.nodeGenerationAccumulator -= 1.0;
			return true;
		}
		return false;
	}

	// Helper method to increment the node generation counter
	incrementNodeCounter(): boolean {
		if (!this.canGenerateNode()) {
			return false;
		}

		const currentNodeCount = this.nodes.filter(node => !node.corroded).length;
		const softCap = this.computeSoftCapacity();
		const capacityPressure = currentNodeCount / Math.max(1, softCap);

		// Near saturation, prioritize rewiring over unlimited additive growth.
		if (capacityPressure >= 1.0) {
			this.maybeSwapRewireNearCapacity();
			return false;
		}

		const currentLimit = this.getCurrentNodeLimit();
		const functionalScore = Math.max(0, Math.min(1, ((this.totalNetworkEnergy / Math.max(1, currentLimit * 2)) + this.stabilityMetric) * 0.5));
		const diversityScore = Math.max(0, Math.min(1, this.musicalComplexity / 10));
		const score = functionalScore * 0.7 + diversityScore * 0.3;
		const cost = 0.4 + capacityPressure * 0.6;
		const acceptance = Math.max(0.08, Math.min(1, (score / Math.max(0.001, cost)) * this.globalPlasticityScale * this.structuralMutationBias));

		this.lastMutationScore = score;
		this.lastMutationCost = cost;
		this.lastMutationAccepted = Math.random() < acceptance;

		if (!this.lastMutationAccepted) {
			return false;
		}

		this.nodesGeneratedThisCycle++;
		if (currentNodeCount >= currentLimit) {
			this.hasReachedNodeLimit = true;
			console.warn(`[Creature ${this.creatureId}] Node limit exceeded! Current: ${currentNodeCount}, Limit: ${currentLimit}`);
			return false;
		}
		return true;
	}

	private computeSoftCapacity(): number {
		return Math.floor(this.getCurrentNodeLimit() * this.adaptiveSoftCapRatio);
	}

	private computeSoftCapTargetRatio(): number {
		const avgEnergy = this.nodes.length > 0
			? this.nodes.reduce((sum, node) => sum + (node.energy || 0), 0) / this.nodes.length
			: 0;
		const normalizedEnergy = Math.max(0, Math.min(1, avgEnergy / 1.5));
		const normalizedStability = Math.max(0, Math.min(1, this.stabilityMetric));
		const diversity = Math.max(0, Math.min(1, this.musicalComplexity / 10));

		return this.minSoftCapRatio + ((normalizedEnergy * 0.45) + (normalizedStability * 0.35) + (diversity * 0.20)) * (this.maxSoftCapRatio - this.minSoftCapRatio);
	}

	private updateEvolutionPhase(): void {
		if (this.step - this.lastPhaseShiftStep < this.phaseDurationSteps) {
			return;
		}

		const softCap = this.computeSoftCapacity();
		const currentNodeCount = this.nodes.filter(node => !node.corroded).length;
		const pressure = currentNodeCount / Math.max(1, softCap);

		if (pressure > 0.92) {
			this.evolutionPhase = 'simplify';
		} else if (pressure > 0.75) {
			this.evolutionPhase = 'rebalance';
		} else {
			this.evolutionPhase = 'complexify';
		}

		this.lastPhaseShiftStep = this.step;
	}

	private updateHomeostaticPlasticity(): void {
		const activeNodes = this.nodes.filter(node => node && !node.corroded);
		if (activeNodes.length === 0) {
			return;
		}

		const avgEnergy = activeNodes.reduce((sum, node) => sum + (node.energy || 0), 0) / activeNodes.length;
		const error = this.homeostaticTargetEnergy - avgEnergy;
		this.globalPlasticityScale += error * this.homeostaticGain * 0.05;
		this.globalPlasticityScale = Math.max(0.5, Math.min(1.5, this.globalPlasticityScale));

		const targetRatio = this.computeSoftCapTargetRatio();
		this.adaptiveSoftCapRatio += (targetRatio - this.adaptiveSoftCapRatio) * 0.05;
		this.adaptiveSoftCapRatio = Math.max(this.minSoftCapRatio, Math.min(this.maxSoftCapRatio, this.adaptiveSoftCapRatio));
	}

	private getHomeostaticGrowthFactor(): number {
		const phaseFactor = this.evolutionPhase === 'complexify' ? 1.0 : this.evolutionPhase === 'rebalance' ? 0.8 : 0.55;
		return Math.max(0.35, Math.min(1.25, this.globalPlasticityScale * phaseFactor));
	}

	private maybeSwapRewireNearCapacity(): void {
		if (this.step - this.lastRewireStep < this.rewireCooldown || this.edges.length < 2 || this.nodes.length < 3) {
			return;
		}

		let weakEdgeIndex = -1;
		let minWeight = Infinity;
		for (let i = 0; i < this.edges.length; i++) {
			const edge = this.edges[i];
			if (edge.weight < minWeight) {
				minWeight = edge.weight;
				weakEdgeIndex = i;
			}
		}

		if (weakEdgeIndex < 0) {
			return;
		}

		const removed = this.edges.splice(weakEdgeIndex, 1)[0];
		let added = false;
		const maxAttempts = Math.max(8, Math.floor(10 + this.structuralRewiringBias * 8));
		for (let attempts = 0; attempts < maxAttempts; attempts++) {
			const a = this.nodes[Math.floor(Math.random() * this.nodes.length)];
			const b = this.nodes[Math.floor(Math.random() * this.nodes.length)];
			if (!a || !b || a.id === b.id || a.corroded || b.corroded) {
				continue;
			}

			const exists = this.edges.some(e => (e.from === a.id && e.to === b.id) || (e.from === b.id && e.to === a.id));
			if (exists) {
				continue;
			}

			const dist = Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2 + ((a.z || 0) - (b.z || 0)) ** 2);
			if (dist > 180) {
				continue;
			}

			this.edges.push({ from: a.id, to: b.id, weight: 0.4 + Math.random() * 0.6 });
			added = true;
			break;
		}

		if (!added) {
			this.edges.push(removed);
		}

		this.lastRewireStep = this.step;
	}

	// Reset node generation counter for each simulation cycle
	resetNodeGenerationCounter(): void {
		this.nodesGeneratedThisCycle = 0;
		// Keep accumulator for smooth fractional generation across cycles
	}
	
	// Calculate total network energy and update creature state
	updateCreatureState(): void {
		// Check if creature has reached node limit
		const currentNodeCount = this.nodes.filter(node => !node.corroded).length;
		this.maybeAdvanceFractalUpgrade();
		const currentLimit = this.getCurrentNodeLimit();
		this.hasReachedNodeLimit = currentNodeCount >= currentLimit;
		this.updateEvolutionPhase();
		this.updateHomeostaticPlasticity();

		const softCap = this.computeSoftCapacity();
		if (currentNodeCount >= softCap && this.evolutionPhase !== 'complexify') {
			this.maybeSwapRewireNearCapacity();
		}
		
		// Safety check: if we've exceeded the limit, force stop all growth
		if (currentNodeCount > currentLimit) {
			console.error(`[Creature ${this.creatureId}] CRITICAL: Node limit exceeded! Current: ${currentNodeCount}, Limit: ${currentLimit}`);
			this.hasReachedNodeLimit = true;
			// Force creature into sensing mode to stop all growth
			this.creatureState = 'sensing';
		}
		
		// Debug logging every 100 steps
		if (this.step % 100 === 0) {
			const stage = this.getCurrentFractalStage();
			console.log(`[Creature ${this.creatureId}] Step ${this.step}: ${currentNodeCount}/${currentLimit} nodes, soft cap: ${softCap}, fractal stage: ${stage.name}, phase: ${this.evolutionPhase}, limit reached: ${this.hasReachedNodeLimit}, state: ${this.creatureState}`);
		}

		if (this.step % 200 === 0) {
			console.log(`[Creature ${this.creatureId}] Mutation gate score=${this.lastMutationScore.toFixed(3)} cost=${this.lastMutationCost.toFixed(3)} accepted=${this.lastMutationAccepted}`);
		}
		
		if (this.hasReachedNodeLimit) {
			// Force creature into sensing/exploring mode when at node limit
			if (this.creatureState === 'growing' || this.creatureState === 'stabilizing') {
				this.creatureState = 'sensing';
				console.log(`[Creature ${this.creatureId}] Reached node limit (${currentNodeCount}/${currentLimit}) - Transitioning to SENSING`);
			}
		}
		
		// Calculate total network energy (excluding crystal heart)
		let totalEnergy = 0;
		let nodeCount = 0;
		
		for (const node of this.nodes) {
			if (node && !node.corroded && node.id !== this.crystalHeartId) {
				totalEnergy += node.energy || 0;
				nodeCount++;
			}
		}
		
		this.totalNetworkEnergy = totalEnergy;
		const averageEnergy = nodeCount > 0 ? totalEnergy / nodeCount : 0;
		
		// MATHEMATICAL MODEL: Calculate Shannon entropy for network complexity
		// Higher entropy = more diverse/distributed energy = higher complexity
		if (nodeCount > 1) {
			const energyLevels = this.nodes
				.filter(node => !node.corroded && node.id !== this.crystalHeartId)
				.map(node => node.energy || 0);
			
			// Create energy distribution histogram
			const bins = 10;
			const maxEnergy = Math.max(...energyLevels) || 1;
			const binSize = maxEnergy / bins;
			const counts = new Array(bins).fill(0);
			
			for (const energy of energyLevels) {
				const bin = Math.min(bins - 1, Math.floor(energy / binSize));
				counts[bin]++;
			}
			
			// Calculate probabilities
			const probabilities = counts.map(count => count / energyLevels.length);
			
			// Calculate Shannon entropy: H(X) = -Σp(x)log₂p(x)
			const entropy = this.mathEngine.informationTheoryModel.calculateEntropy(probabilities);
			
			// Use entropy to scale network complexity
			this.musicalComplexity = Math.min(10, entropy * 5); // Scale entropy to complexity
		}
		
		// State transition logic based on energy levels
		const currentTime = performance.now();
		if (currentTime - this.lastStateChange < this.stateChangeCooldown) {
			return; // Respect cooldown between state changes
		}
		
		switch (this.creatureState) {
			case 'growing':
				if (averageEnergy >= this.energyStabilityThreshold && totalEnergy >= this.energyStabilityThreshold * 2) {
					this.creatureState = 'stabilizing';
					this.lastStateChange = currentTime;
					console.log(`[Creature] Transitioning to STABILIZING - Energy: ${averageEnergy.toFixed(2)}, Total: ${totalEnergy.toFixed(2)}`);
				}
				break;
				
			case 'stabilizing':
				if (averageEnergy >= this.energyStabilityThreshold * 1.2) {
					this.creatureState = 'sensing';
					this.lastStateChange = currentTime;
					console.log(`[Creature] Transitioning to SENSING - Energy: ${averageEnergy.toFixed(2)}, Total: ${totalEnergy.toFixed(2)}`);
				} else if (averageEnergy < this.energyGrowthThreshold) {
					this.creatureState = 'growing';
					this.lastStateChange = currentTime;
					console.log(`[Creature] Transitioning back to GROWING - Energy: ${averageEnergy.toFixed(2)}, Total: ${totalEnergy.toFixed(2)}`);
				}
				break;
				
			case 'sensing':
				if (averageEnergy >= this.energyStabilityThreshold * 1.5) {
					this.creatureState = 'exploring';
					this.lastStateChange = currentTime;
					console.log(`[Creature] Transitioning to EXPLORING - Energy: ${averageEnergy.toFixed(2)}, Total: ${totalEnergy.toFixed(2)}`);
				} else if (averageEnergy < this.energyGrowthThreshold) {
					this.creatureState = 'growing';
					this.lastStateChange = currentTime;
					console.log(`[Creature] Transitioning back to GROWING - Energy: ${averageEnergy.toFixed(2)}, Total: ${totalEnergy.toFixed(2)}`);
				}
				break;
				
			case 'exploring':
				if (averageEnergy < this.energyGrowthThreshold) {
					this.creatureState = 'growing';
					this.lastStateChange = currentTime;
					console.log(`[Creature] Transitioning back to GROWING - Energy: ${averageEnergy.toFixed(2)}, Total: ${totalEnergy.toFixed(2)}`);
				}
				break;
		}
	}
	
	// Perform sensing behavior when creature is stable
	performSensingBehavior(): void {
		if (this.crystalHeartId === null || this.crystalHeartId === undefined) return;
		const heart = this.nodes[this.crystalHeartId];
		if (!heart) return;
		
		// Sense environmental energy sources
		const sensedObjects: { x: number; y: number; z: number; energy: number; type: string }[] = [];
		
		// Simulate sensing nearby energy sources
		for (let i = 0; i < 5; i++) {
			const angle = (this.step + i * 72) * 0.01; // Rotate sensing direction
			const distance = this.sensingRadius * (0.5 + Math.random() * 0.5);
			const sensedX = heart.x + Math.cos(angle) * distance;
			const sensedY = heart.y + Math.sin(angle) * distance;
			const sensedZ = heart.z + (Math.random() - 0.5) * 100;
			
			// Simulate environmental energy detection
			const environmentalEnergy = Math.sin(this.step * 0.02 + i) * 50 + 100;
			if (environmentalEnergy > 120) {
				sensedObjects.push({
					x: sensedX,
					y: sensedY,
					z: sensedZ,
					energy: environmentalEnergy,
					type: 'energy_source'
				});
			}
		}
		
		// React to sensed objects
		if (sensedObjects.length > 0 && this.creatureState === 'exploring') {
			// Move nodes toward interesting energy sources
			const targetObject = sensedObjects[0]; // Focus on strongest signal
			const moveForce = 0.1;
			
			for (const node of this.nodes) {
				if (!node || node.corroded || node.id === this.crystalHeartId) continue;
				
				const dx = targetObject.x - node.x;
				const dy = targetObject.y - node.y;
				const dz = targetObject.z - node.z;
				const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);
				
				if (distance < this.sensingRadius && distance > 10) {
					// Gentle movement toward sensed object
					node.vx = (node.vx || 0) + (dx / distance) * moveForce * 0.1;
					node.vy = (node.vy || 0) + (dy / distance) * moveForce * 0.1;
					node.vz = (node.vz || 0) + (dz / distance) * moveForce * 0.05;
				}
			}
		}
		
		// Update sensing radius based on energy level
		const energyFactor = Math.min(1, this.totalNetworkEnergy / 1000);
		this.sensingRadius = 150 + energyFactor * 100; // 150-250 range
	}
	
	// Prepare for multi-creature communication (future feature)
	detectOtherCreatures(): { creatureId: string; distance: number; energy: number }[] {
		// This will be used when multiple creatures exist in the environment
		// For now, return empty array as we only have one creature
		return [];
	}
	
	// Get creature's communication range and capabilities
	getCommunicationCapabilities(): {
		creatureId: string;
		position: { x: number; y: number; z: number };
		energy: number;
		state: string;
		sensingRadius: number;
		nodeCount: number;
	} {
		const heart = this.crystalHeartId !== null && this.crystalHeartId !== undefined ? this.nodes[this.crystalHeartId] : null;
		const position = heart ? { x: heart.x, y: heart.y, z: heart.z } : { x: 0, y: 0, z: 0 };
		
		return {
			creatureId: this.creatureId,
			position,
			energy: this.totalNetworkEnergy,
			state: this.creatureState,
			sensingRadius: this.sensingRadius,
			nodeCount: this.nodes.filter(node => !node.corroded).length
		};
	}

	// --- Linguistic Communication Methods ---
	
	// Discover new linguistic elements - DISABLED (replaced with musical patterns)
	discoverLinguisticElement(): void {
		// DISABLED: Old linguistic discovery replaced with musical pattern discovery
		// This method is kept for compatibility but does nothing
		return;
	}
	
	// Determine what type of linguistic element to discover next
	determineElementTypeToDiscover(): string {
		const totalElements = this.linguisticElements.size;
		
		// Early stages: focus on basic sounds
		if (totalElements < 5) {
			return Math.random() < 0.7 ? 'vowel' : 'consonant';
		}
		// Mid stages: start forming syllables
		else if (totalElements < 15) {
			const rand = Math.random();
			if (rand < 0.4) return 'vowel';
			if (rand < 0.7) return 'consonant';
			return 'syllable';
		}
		// Advanced stages: form words and semantic elements
		else {
			const rand = Math.random();
			if (rand < 0.2) return 'vowel';
			if (rand < 0.4) return 'consonant';
			if (rand < 0.7) return 'syllable';
			return 'word';
		}
	}
	
	// Generate a new vowel
	generateVowel(): LinguisticElement {
		const vowels = ['a', 'e', 'i', 'o', 'u', 'y', 'ai', 'ei', 'ou', 'au'];
		const availableVowels = vowels.filter(v => !this.discoveredVowels.includes(v));
		
		if (availableVowels.length === 0) {
			// Create a complex vowel combination
			const baseVowels = ['a', 'e', 'i', 'o', 'u'];
			const vowel1 = baseVowels[Math.floor(Math.random() * baseVowels.length)];
			const vowel2 = baseVowels[Math.floor(Math.random() * baseVowels.length)];
			const phoneticValue = vowel1 + vowel2;
			this.discoveredVowels.push(phoneticValue);
			
		// Use harmonic intervals based on root frequency or last note
		const frequency = this.getHarmonicFrequency(this.rootFrequency, true);
		const vowelLength = 200 + Math.random() * 600; // 200-800ms
		
		// Update last musical note for interval calculation
		this.lastMusicalNote = frequency;
		
		return {
			phoneticValue,
			type: 'vowel',
			frequency: frequency,
			complexity: 3 + Math.random() * 2,
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			// Musical properties
			musicalPitch: frequency,
			vowelLength: vowelLength,
			musicalInterval: this.calculateMusicalInterval(this.lastMusicalNote, frequency),
			rhythmPattern: this.getRhythmFromDuration(vowelLength),
			melodicContext: frequency > this.lastMusicalNote ? 'ascending' : 'descending'
		};
		}
		
		const phoneticValue = availableVowels[Math.floor(Math.random() * availableVowels.length)];
		this.discoveredVowels.push(phoneticValue);
		
		// Use harmonic intervals based on root frequency or last note
		const frequency = this.getHarmonicFrequency(this.rootFrequency, true);
		const vowelLength = 200 + Math.random() * 600; // 200-800ms
		
		// Update last musical note for interval calculation
		this.lastMusicalNote = frequency;
		
		return {
			phoneticValue,
			type: 'vowel',
			frequency: frequency,
			complexity: 1 + Math.random() * 2,
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			// Musical properties
			musicalPitch: frequency,
			vowelLength: vowelLength,
			musicalInterval: this.calculateMusicalInterval(this.lastMusicalNote, frequency),
			rhythmPattern: this.getRhythmFromDuration(vowelLength),
			melodicContext: frequency > this.lastMusicalNote ? 'ascending' : 'descending'
		};
	}
	
	// Generate a new consonant
	generateConsonant(): LinguisticElement {
		const consonants = ['b', 'c', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm', 'n', 'p', 'q', 'r', 's', 't', 'v', 'w', 'x', 'z'];
		const articulations = ['bilabial', 'labiodental', 'dental', 'alveolar', 'postalveolar', 'retroflex', 'palatal', 'velar', 'uvular', 'pharyngeal', 'glottal'];
		const availableConsonants = consonants.filter(c => !this.discoveredConsonants.includes(c));
		
		if (availableConsonants.length === 0) {
			// Create a consonant cluster
			const baseConsonants = ['b', 'c', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm', 'n', 'p', 'r', 's', 't'];
			const cons1 = baseConsonants[Math.floor(Math.random() * baseConsonants.length)];
			const cons2 = baseConsonants[Math.floor(Math.random() * baseConsonants.length)];
			const phoneticValue = cons1 + cons2;
			this.discoveredConsonants.push(phoneticValue);
			
			return {
				phoneticValue,
				type: 'consonant',
				articulation: articulations[Math.floor(Math.random() * articulations.length)],
				complexity: 4 + Math.random() * 2,
				discoveryStep: this.step,
				usageCount: 0,
				learningPhase: 'discovery'
			};
		}
		
		const phoneticValue = availableConsonants[Math.floor(Math.random() * availableConsonants.length)];
		this.discoveredConsonants.push(phoneticValue);
		
		return {
			phoneticValue,
			type: 'consonant',
			articulation: articulations[Math.floor(Math.random() * articulations.length)],
			complexity: 1 + Math.random() * 2,
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery'
		};
	}
	
	// Generate a new syllable
	generateSyllable(): LinguisticElement {
		if (this.discoveredVowels.length === 0 || this.discoveredConsonants.length === 0) {
			return this.generateVowel(); // Fallback to vowel if no consonants available
		}
		
		const vowel = this.discoveredVowels[Math.floor(Math.random() * this.discoveredVowels.length)];
		const consonant = this.discoveredConsonants[Math.floor(Math.random() * this.discoveredConsonants.length)];
		
		// Create syllable patterns: CV, VC, CVC, VCV
		const patterns = ['CV', 'VC', 'CVC', 'VCV'];
		const pattern = patterns[Math.floor(Math.random() * patterns.length)];
		
		let phoneticValue: string;
		switch (pattern) {
			case 'CV':
				phoneticValue = consonant + vowel;
				break;
			case 'VC':
				phoneticValue = vowel + consonant;
				break;
			case 'CVC':
				const consonant2 = this.discoveredConsonants[Math.floor(Math.random() * this.discoveredConsonants.length)];
				phoneticValue = consonant + vowel + consonant2;
				break;
			case 'VCV':
				const vowel2 = this.discoveredVowels[Math.floor(Math.random() * this.discoveredVowels.length)];
				phoneticValue = vowel + consonant + vowel2;
				break;
			default:
				phoneticValue = consonant + vowel;
		}
		
		const syllable: SyllableStructure = {
			vowel,
			consonants: [consonant],
			complexity: phoneticValue.length,
			pronunciation: phoneticValue
		};
		
		this.discoveredSyllables.push(syllable);
		
		return {
			phoneticValue,
			type: 'syllable',
			complexity: 3 + Math.random() * 3,
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery'
		};
	}
	
	// Generate a new word
	generateWord(): LinguisticElement {
		if (this.discoveredSyllables.length === 0) {
			return this.generateSyllable(); // Fallback to syllable if none available
		}
		
		// Combine 1-3 syllables to form a word
		const syllableCount = 1 + Math.floor(Math.random() * 3);
		const selectedSyllables: SyllableStructure[] = [];
		
		for (let i = 0; i < syllableCount; i++) {
			const syllable = this.discoveredSyllables[Math.floor(Math.random() * this.discoveredSyllables.length)];
			selectedSyllables.push(syllable);
		}
		
		const phoneticValue = selectedSyllables.map(s => s.pronunciation).join('');
		const meanings = ['light', 'dark', 'water', 'fire', 'earth', 'air', 'life', 'death', 'love', 'fear', 'joy', 'sadness', 'big', 'small', 'fast', 'slow'];
		const meaning = meanings[Math.floor(Math.random() * meanings.length)];
		
		const word: WordFormation = {
			syllables: selectedSyllables,
			meaning,
			complexity: selectedSyllables.reduce((sum, s) => sum + s.complexity, 0),
			discoveryStep: this.step,
			usageCount: 0
		};
		
		this.discoveredWords.push(word);
		
		return {
			phoneticValue,
			type: 'word',
			meaning,
			complexity: 5 + Math.random() * 4,
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery'
		};
	}
	
	// Create a neural node for a linguistic element
	createLinguisticNode(element: LinguisticElement): void {
		if (!this.canGenerateNode()) return;
		if (!this.incrementNodeCounter()) return;
		
		// Find a suitable location for the linguistic node
		const centerX = 300 + (Math.random() - 0.5) * 200;
		const centerY = 200 + (Math.random() - 0.5) * 200;
		const centerZ = (Math.random() - 0.5) * 100;
		
		const id = this.nodes.length;
		const node: Node = {
			id,
			x: centerX,
			y: centerY,
			z: centerZ,
			value: element.complexity / 10,
			energy: 0.5 + Math.random() * 0.5,
			energyStorage: 0,
			color: this.getLinguisticColor(element.type),
			highlight: 0,
			corroded: false,
			vz: 0,
			nodeType: element.type as any,
			phoneticValue: element.phoneticValue,
			linguisticType: element.type as any,
			frequency: element.frequency,
			articulation: element.articulation,
			meaning: element.meaning,
			usageCount: 0,
			learningPhase: 'discovery'
		};
		
		this.nodes.push(node);
		
		// Update root frequency based on node color (zodiac type)
		const newRootFreq = this.getRootFrequencyFromColor(node.color);
		this.rootFrequency = newRootFreq; // Update root for harmonic generation
		
		// Connect to nearby linguistic nodes
		this.connectLinguisticNode(node);
		
		if (this.onNodeGrowth) {
			this.onNodeGrowth(1, node.color);
		}
	}
	
	// Get color for linguistic node type
	getLinguisticColor(type: string): string {
		const colors = {
			vowel: 'hsl(60, 90%, 70%)',      // Yellow for vowels
			consonant: 'hsl(240, 90%, 70%)',  // Blue for consonants
			syllable: 'hsl(120, 90%, 70%)',   // Green for syllables
			word: 'hsl(300, 90%, 70%)',       // Magenta for words
			semantic: 'hsl(0, 90%, 70%)'      // Red for semantic elements
		};
		return colors[type as keyof typeof colors] || 'hsl(180, 70%, 60%)';
	}
	
	// Connect linguistic node to nearby linguistic nodes
	connectLinguisticNode(node: Node): void {
		const maxDistance = 80;
		const linguisticNodes = this.nodes.filter(n => n.linguisticType && n.id !== node.id);
		
		for (const otherNode of linguisticNodes) {
			const dx = otherNode.x - node.x;
			const dy = otherNode.y - node.y;
			const dz = otherNode.z - node.z;
			const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);
			
			if (distance < maxDistance) {
				// Higher probability for connecting similar linguistic types
				const typeMatch = node.linguisticType === otherNode.linguisticType ? 0.8 : 0.3;
				const distanceFactor = 1 - (distance / maxDistance);
				const connectionProbability = (typeMatch + distanceFactor) * 0.5;
				
				if (Math.random() < connectionProbability) {
					this.edges.push({
						from: node.id,
						to: otherNode.id,
						weight: 0.6 + Math.random() * 0.4
					});
				}
			}
		}
	}
	
	// Attempt verbal communication
	attemptVerbalCommunication(): void {
		// DISABLED: Old verbal communication replaced with musical pattern communication
		// This method is kept for compatibility but does nothing
		return;
	}
	
	// Calculate communication success rate
	calculateCommunicationSuccessRate(elements: LinguisticElement[]): number {
		const masteryCount = elements.filter(e => e.learningPhase === 'mastery' || e.learningPhase === 'teaching').length;
		const practiceCount = elements.filter(e => e.learningPhase === 'practice').length;
		const discoveryCount = elements.filter(e => e.learningPhase === 'discovery').length;
		
		const baseSuccess = 0.3;
		const masteryBonus = masteryCount * 0.3;
		const practiceBonus = practiceCount * 0.15;
		const discoveryPenalty = discoveryCount * 0.1;
		
		return Math.min(0.95, baseSuccess + masteryBonus + practiceBonus - discoveryPenalty);
	}
	
	// Update linguistic complexity based on discoveries - DISABLED
	updateLinguisticComplexity(): void {
		// DISABLED: Old linguistic complexity replaced with musical complexity
		// This method is kept for compatibility but does nothing
		return;
	}
	
	// --- Musical Pattern Discovery Methods ---
	
	// Discover new musical patterns and melodies
	discoverMusicalPattern(): void {
		if (Math.random() > 0.08) return; // 8% chance per step for more frequent musical discovery
		
		console.log('[Musical Discovery] Discovering new musical pattern...');
		const patternType = this.determineMusicalPatternType();
		let newPattern: MusicalPattern;
		
		switch (patternType) {
			case 'melody':
				newPattern = this.generateMelody();
				break;
			case 'rhythm':
				newPattern = this.generateRhythmPattern();
				break;
			case 'interval':
				newPattern = this.generateIntervalPattern();
				break;
			case 'harmony':
				newPattern = this.generateHarmonicPattern();
				break;
			case 'improvisation':
				newPattern = this.generateImprovisationPattern();
				break;
			case 'call_response':
				newPattern = this.generateCallResponsePattern();
				break;
			case 'scale_run':
				newPattern = this.generateScaleRunPattern();
				break;
			default:
				return;
		}
		
		this.musicalPatterns.set(newPattern.id, newPattern);
		this.discoveredMelodies.push(newPattern);
		
		// Synthesize the musical pattern
		this.synthesizeMusicalPattern(newPattern);
		
		console.log(`[Musical Discovery] New ${patternType}: "${newPattern.name}" (Complexity: ${newPattern.complexity})`);
	}
	
	// Attempt musical communication using existing patterns
	attemptMusicalCommunication(): void {
		if (this.isSinging || this.discoveredMelodies.length === 0) return;
		
		// 15% chance to spontaneously sing an existing pattern
		if (Math.random() < 0.15) {
			const randomPattern = this.discoveredMelodies[Math.floor(Math.random() * this.discoveredMelodies.length)];
			
			// Don't repeat the same pattern too frequently
			if (randomPattern.usageCount < 20) {
				console.log(`[Musical Communication] Spontaneously singing: ${randomPattern.name}`);
				this.synthesizeMusicalPattern(randomPattern);
			}
		}
		
		// 5% chance to create an improvisational variation
		if (Math.random() < 0.05 && this.discoveredMelodies.length > 2) {
			const basePattern = this.discoveredMelodies[Math.floor(Math.random() * this.discoveredMelodies.length)];
			const improvisation = this.createImprovisationalVariation(basePattern);
			
			console.log(`[Musical Communication] Improvising variation of: ${basePattern.name}`);
			this.synthesizeMusicalPattern(improvisation);
		}
	}
	
	// Create an improvisational variation of an existing pattern
	createImprovisationalVariation(basePattern: MusicalPattern): MusicalPattern {
		const variation: MusicalPattern = {
			...basePattern,
			id: `improvisation_${basePattern.id}_${Date.now()}`,
			name: `Improvised ${basePattern.name}`,
			notes: basePattern.notes.map(note => ({
				...note,
				pitch: note.pitch * (1 + (Math.random() - 0.5) * 0.15), // ±7.5% pitch variation
				duration: note.duration * (1 + (Math.random() - 0.5) * 0.3), // ±15% duration variation
				velocity: Math.max(0.3, Math.min(1.0, note.velocity + (Math.random() - 0.5) * 0.3)),
				ornamentation: this.getRandomOrnamentation()
			})),
			rhythm: {
				...basePattern.rhythm,
				tempo: basePattern.rhythm.tempo * (1 + (Math.random() - 0.5) * 0.2), // ±10% tempo variation
				syncopation: Math.min(0.8, (basePattern.rhythm.syncopation || 0) + (Math.random() - 0.5) * 0.2)
			},
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery'
		};
		
		// Store the variation
		this.musicalPatterns.set(variation.id, variation);
		this.discoveredMelodies.push(variation);
		
		return variation;
	}
	
	// Update musical complexity based on learning and experience
	updateMusicalComplexity(): void {
		const totalPatterns = this.musicalPatterns.size;
		const totalUsage = Array.from(this.musicalPatterns.values()).reduce((sum, pattern) => sum + pattern.usageCount, 0);
		
		// Increase complexity based on patterns learned and used
		this.musicalComplexity = Math.min(10, 1 + (totalPatterns * 0.3) + (totalUsage * 0.05));
		this.harmonicComplexity = Math.min(10, 1 + (totalPatterns * 0.2) + (totalUsage * 0.03));
		this.improvisationLevel = Math.min(1.0, 0.1 + (totalPatterns * 0.05) + (totalUsage * 0.02));
		
		// Update current musical mode based on complexity
		if (this.musicalComplexity > 7) {
			const modes: ('major' | 'minor' | 'pentatonic' | 'blues' | 'dorian' | 'mixolydian')[] = 
				['major', 'minor', 'pentatonic', 'blues', 'dorian', 'mixolydian'];
			this.currentMusicalMode = modes[Math.floor(Math.random() * modes.length)];
		}
		
		// Log complexity updates occasionally
		if (this.step % 100 === 0 && totalPatterns > 0) {
			console.log(`[Musical Complexity] Patterns: ${totalPatterns}, Usage: ${totalUsage}, Complexity: ${this.musicalComplexity.toFixed(1)}, Mode: ${this.currentMusicalMode}`);
		}
	}
	
	// Determine what type of musical pattern to discover
	determineMusicalPatternType(): string {
		const totalPatterns = this.musicalPatterns.size;
		
		// Early stages: focus on simple melodies and improvisation
		if (totalPatterns < 3) {
			return Math.random() < 0.7 ? 'melody' : 'improvisation';
		}
		// Mid stages: add rhythm patterns and call-response
		else if (totalPatterns < 8) {
			const rand = Math.random();
			if (rand < 0.3) return 'melody';
			if (rand < 0.5) return 'improvisation';
			if (rand < 0.7) return 'call_response';
			if (rand < 0.9) return 'rhythm';
			return 'harmony';
		}
		// Advanced stages: complex harmonies, intervals, and scale runs
		else if (totalPatterns < 15) {
			const rand = Math.random();
			if (rand < 0.3) return 'improvisation';
			if (rand < 0.5) return 'harmony';
			if (rand < 0.7) return 'call_response';
			if (rand < 0.85) return 'melody';
			return 'scale_run';
		}
		// Expert stages: all types with focus on complex patterns
		else {
			const rand = Math.random();
			if (rand < 0.25) return 'improvisation';
			if (rand < 0.45) return 'harmony';
			if (rand < 0.6) return 'call_response';
			if (rand < 0.75) return 'scale_run';
			if (rand < 0.9) return 'melody';
			return 'interval';
		}
	}
	
	// Generate a simple melody pattern
	generateMelody(): MusicalPattern {
		const melodyLength = Math.min(4, 2 + Math.floor(Math.random() * 3)); // 2-4 notes
		const notes: MusicalNote[] = [];
		let currentPitch = 220; // Start at A3
		
		// Musical scales (C major pentatonic for simplicity)
		const scale = [261.63, 293.66, 329.63, 392.00, 440.00, 523.25]; // C4, D4, E4, G4, A4, C5
		
		for (let i = 0; i < melodyLength; i++) {
			// Choose next note from scale
			const scaleIndex = Math.floor(Math.random() * scale.length);
			const targetPitch = scale[scaleIndex];
			
			// Calculate musical interval
			const interval = this.calculateMusicalInterval(currentPitch, targetPitch);
			
			// Determine melodic context
			let melodicContext: 'ascending' | 'descending' | 'stable';
			if (targetPitch > currentPitch * 1.05) {
				melodicContext = 'ascending';
			} else if (targetPitch < currentPitch * 0.95) {
				melodicContext = 'descending';
			} else {
				melodicContext = 'stable';
			}
			
			// Choose rhythm pattern
			const rhythmPattern = this.rhythmPatterns[Math.floor(Math.random() * this.rhythmPatterns.length)];
			const duration = this.getDurationFromRhythm(rhythmPattern);
			
			// Find associated vowel for this note
			const vowelElement = this.findVowelForPitch(targetPitch);
			
			const note: MusicalNote = {
				pitch: targetPitch,
				duration: duration,
				velocity: 0.7 + Math.random() * 0.3,
				phoneticValue: vowelElement?.phoneticValue || 'ah',
				musicalInterval: interval,
				harmonicFunction: this.determineHarmonicFunction(i, melodyLength)
			};
			
			notes.push(note);
			currentPitch = targetPitch;
		}
		
		const pattern: MusicalPattern = {
			id: `melody_${Date.now()}`,
			name: `Melody ${this.discoveredMelodies.length + 1}`,
			notes: notes,
			rhythm: {
				pattern: notes.map(n => this.getRhythmFromDuration(n.duration)).join(','),
				beatsPerMeasure: 4,
				beatValue: 4,
				tempo: 120
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(10, notes.length + Math.random() * 2),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'melody'
		};
		
		return pattern;
	}
	
	// Generate a rhythm pattern
	generateRhythmPattern(): MusicalPattern {
		// Use complex rhythm patterns for more interesting rhythms
		if (Math.random() < 0.6) {
			return this.generateComplexRhythmPattern();
		}
		
		// Simple rhythm patterns for early learning
		const rhythmLength = 2 + Math.floor(Math.random() * 3); // 2-4 beats
		const notes: MusicalNote[] = [];
		const basePitch = 440; // A4
		
		for (let i = 0; i < rhythmLength; i++) {
			const rhythmPattern = this.rhythmPatterns[Math.floor(Math.random() * this.rhythmPatterns.length)];
			const duration = this.getDurationFromRhythm(rhythmPattern);
			
			const note: MusicalNote = {
				pitch: basePitch + (Math.random() - 0.5) * 100, // Slight pitch variation
				duration: duration,
				velocity: 0.8 + Math.random() * 0.2,
				phoneticValue: 'ah',
				harmonicFunction: 'tonic',
				harmonicRole: 'rhythm'
			};
			
			notes.push(note);
		}
		
		const pattern: MusicalPattern = {
			id: `rhythm_${Date.now()}`,
			name: `Rhythm Pattern ${this.discoveredMelodies.length + 1}`,
			notes: notes,
			rhythm: {
				pattern: notes.map(n => this.getRhythmFromDuration(n.duration)).join(','),
				beatsPerMeasure: rhythmLength,
				beatValue: 4,
				tempo: 120 + Math.random() * 40,
				syncopation: Math.random() * 0.3
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(8, rhythmLength + Math.random() * 2),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'melody',
			musicalMode: 'pentatonic'
		};
		
		return pattern;
	}
	
	// Generate an interval pattern (focus on harmonic intervals)
	generateIntervalPattern(): MusicalPattern {
		const intervalTypes = [2, 3, 4, 5, 7, 12]; // Major second, minor third, major third, perfect fourth, perfect fifth, octave
		const selectedInterval = intervalTypes[Math.floor(Math.random() * intervalTypes.length)];
		const basePitch = 220 + Math.random() * 200; // Random starting pitch
		const notes: MusicalNote[] = [];
		
		// Create 3-4 notes with the selected interval
		const numNotes = 3 + Math.floor(Math.random() * 2);
		
		for (let i = 0; i < numNotes; i++) {
			const pitch = basePitch * Math.pow(2, (selectedInterval * i) / 12);
			const duration = this.getDurationFromRhythm('quarter');
			
			const note: MusicalNote = {
				pitch: pitch,
				duration: duration,
				velocity: 0.7 + Math.random() * 0.3,
				phoneticValue: this.findVowelForPitch(pitch)?.phoneticValue || 'ah',
				musicalInterval: i === 0 ? 0 : selectedInterval,
				harmonicFunction: this.determineHarmonicFunction(i, numNotes)
			};
			
			notes.push(note);
		}
		
		const pattern: MusicalPattern = {
			id: `interval_${Date.now()}`,
			name: `${selectedInterval}th Interval Pattern`,
			notes: notes,
			rhythm: {
				pattern: 'quarter,quarter,quarter',
				beatsPerMeasure: 4,
				beatValue: 4,
				tempo: 120
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(10, selectedInterval + Math.random() * 2),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'melody'
		};
		
		return pattern;
	}
	
	// Calculate musical interval between two pitches in semitones
	calculateMusicalInterval(pitch1: number, pitch2: number): number {
		return Math.round(12 * Math.log2(pitch2 / pitch1));
	}
	
	// Get base frequency from node color (zodiac type mapping)
	getRootFrequencyFromColor(color: string): number {
		const hueMatch = color.match(/hsl\((\d+)/);
		if (!hueMatch) return 220; // Default A3
		
		const hue = parseInt(hueMatch[1]);
		
		// Map hue ranges to musical root frequencies
		// Using frequencies that correspond to musical notes (rounded to nearest)
		if (hue < 30) return 220; // A3 - Red/Fire
		if (hue < 60) return 246.94; // B3 - Orange
		if (hue < 90) return 261.63; // C4 - Yellow/Earth
		if (hue < 120) return 277.18; // C#4 - Yellow-Green
		if (hue < 150) return 293.66; // D4 - Green/Air
		if (hue < 180) return 311.13; // D#4 - Cyan
		if (hue < 210) return 329.63; // E4 - Blue/Water
		if (hue < 240) return 349.23; // F4 - Blue-Violet
		if (hue < 270) return 369.99; // F#4 - Violet/Spirit
		if (hue < 300) return 392.00; // G4 - Magenta
		if (hue < 330) return 415.30; // G#4 - Pink
		return 440.00; // A4 - Rose/Warm
	}
	
	// Get a harmonic interval relative to a base frequency
	getHarmonicFrequency(baseFreq: number, useLastNote: boolean = false): number {
		// Common harmonic intervals in semitones (consonant and pleasant)
		const harmonicIntervals = [0, 3, 4, 5, 7, 9, 12, 15, 16, 19]; // Unison, m3, M3, P4, P5, M6, Octave, etc.
		
		// If we have a last note and want to use it, create intervals from it
		let referenceFreq = this.rootFrequency;
		if (useLastNote && this.lastMusicalNote > 0) {
			// 50/50 chance to use last note or root for more musical variation
			referenceFreq = Math.random() < 0.5 ? this.lastMusicalNote : this.rootFrequency;
		}
		
		// Select a random harmonic interval
		const intervalSemitones = harmonicIntervals[Math.floor(Math.random() * harmonicIntervals.length)];
		
		// Calculate the new frequency: multiply by 2^(semitones/12)
		const newFreq = referenceFreq * Math.pow(2, intervalSemitones / 12);
		
		// Constrain to reasonable audible range (100-1000 Hz)
		return Math.max(100, Math.min(1000, newFreq));
	}
	
	// Get duration in milliseconds from rhythm pattern name
	getDurationFromRhythm(rhythmPattern: string): number {
		switch (rhythmPattern) {
			case 'whole': return 2000; // 2 seconds
			case 'half': return 1000; // 1 second
			case 'dotted_half': return 1500; // 1.5 seconds
			case 'quarter': return 500; // 0.5 seconds
			case 'dotted_quarter': return 750; // 0.75 seconds
			case 'eighth': return 250; // 0.25 seconds
			case 'dotted_eighth': return 375; // 0.375 seconds
			case 'triplet': return 167; // ~0.167 seconds (for eighth note triplets)
			default: return 500;
		}
	}
	
	// Get rhythm pattern name from duration
	getRhythmFromDuration(duration: number): string {
		if (duration >= 1800) return 'whole';
		if (duration >= 800) return 'half';
		if (duration >= 300) return 'quarter';
		return 'eighth';
	}
	
	// Find a vowel element that matches a given pitch
	findVowelForPitch(pitch: number): LinguisticElement | null {
		const vowels = Array.from(this.linguisticElements.values()).filter(e => e.type === 'vowel');
		if (vowels.length === 0) return null;
		
		// Find vowel with closest frequency
		return vowels.reduce((closest, current) => {
			const currentDiff = Math.abs((current.frequency || 300) - pitch);
			const closestDiff = Math.abs((closest.frequency || 300) - pitch);
			return currentDiff < closestDiff ? current : closest;
		});
	}
	
	// Determine harmonic function based on position in pattern
	determineHarmonicFunction(position: number, totalLength: number): 'tonic' | 'dominant' | 'subdominant' | 'passing' | 'neighbor' {
		if (position === 0) return 'tonic';
		if (position === totalLength - 1) return 'tonic';
		if (position === Math.floor(totalLength / 2)) return 'dominant';
		return Math.random() < 0.5 ? 'passing' : 'neighbor';
	}
	
	// Synthesize a musical pattern with enhanced musicality
	synthesizeMusicalPattern(pattern: MusicalPattern): void {
		console.log(`[Musical Synthesis] Playing pattern: ${pattern.name} (${pattern.patternType})`);
		
		this.isSinging = true;
		this.currentSingingPattern = pattern;
		
		// Calculate timing based on rhythm pattern
		const baseTiming = 60000 / pattern.rhythm.tempo; // Base note duration in ms
		let currentTime = 0;
		
		// Play melody notes
		pattern.notes.forEach((note, index) => {
			const noteTiming = currentTime;
			const noteDuration = note.duration;
			
			// Add slight timing variations for natural feel
			const timingVariation = (Math.random() - 0.5) * 50; // ±25ms variation
			const actualTiming = noteTiming + timingVariation;
			
			setTimeout(() => {
				this.synthesizeMusicalNote(note);
			}, Math.max(0, actualTiming));
			
			// Advance time based on note duration with some overlap for smoothness
			currentTime += noteDuration * 0.8; // 80% overlap for smoother transitions
		});
		
		// Play harmony notes if present (slightly delayed and softer)
		if (pattern.harmony && pattern.harmony.length > 0) {
			pattern.harmony.forEach((harmonyNote, index) => {
				const harmonyTiming = currentTime * 0.3 + index * (baseTiming * 0.5); // Delayed and spread out
				
				setTimeout(() => {
					this.synthesizeHarmonicNote(harmonyNote);
				}, Math.max(0, harmonyTiming));
			});
		}
		
		// Create natural phrase endings with breath pauses
		const totalDuration = pattern.notes.reduce((sum, note) => sum + note.duration, 0);
		const phraseEndTime = totalDuration + 500; // Add breath pause
		
		setTimeout(() => {
			this.isSinging = false;
			this.currentSingingPattern = null;
			
			// Add natural breathing sounds between phrases
			if (Math.random() < 0.3) {
				this.synthesizeBreathSound();
			}
		}, phraseEndTime);
		
		// Update usage count and learning phase
		pattern.usageCount++;
		if (pattern.usageCount > 10) {
			pattern.learningPhase = 'mastery';
		} else if (pattern.usageCount > 5) {
			pattern.learningPhase = 'practice';
		}
		
		// Evolve the pattern slightly with usage
		if (pattern.usageCount > 3 && Math.random() < 0.2) {
			this.evolveMusicalPattern(pattern);
		}
	}
	
	// Synthesize a harmonic note (softer, supporting role)
	synthesizeHarmonicNote(note: MusicalNote): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			// Create oscillator for harmonic pitch
			const oscillator = this.audioContext!.createOscillator();
			const gainNode = this.audioContext!.createGain();
			
			oscillator.connect(gainNode);
			gainNode.connect(this.getAudioOutput());
			
			// Set frequency to harmonic pitch
			oscillator.frequency.setValueAtTime(note.pitch, this.audioContext!.currentTime);
			oscillator.type = 'sine';
			
			// Softer volume for harmony
			gainNode.gain.setValueAtTime(0, this.audioContext!.currentTime);
			gainNode.gain.linearRampToValueAtTime(0.03, this.audioContext!.currentTime + 0.01); // Reduced to prevent clipping
			gainNode.gain.exponentialRampToValueAtTime(0.03, this.audioContext!.currentTime + note.duration / 1000);
			
			oscillator.start(this.audioContext!.currentTime);
			oscillator.stop(this.audioContext!.currentTime + note.duration / 1000);
			
			// Synthesize harmony vowel sound (softer)
			this.synthesizeVowelWithMusicalPitch(note.phoneticValue || 'oh', note.pitch, note.duration);
			
		} catch (error) {
			console.warn('[Harmonic Note] Failed to synthesize harmony note:', error);
		}
	}
	
	// Synthesize natural breathing sounds between phrases
	synthesizeBreathSound(): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			// Create soft noise for breath sound
			const bufferSize = this.audioContext!.sampleRate * 0.5; // 0.5 second
			const buffer = this.audioContext!.createBuffer(1, bufferSize, this.audioContext!.sampleRate);
			const data = buffer.getChannelData(0);
			
			for (let i = 0; i < bufferSize; i++) {
				data[i] = (Math.random() - 0.5) * 0.1; // Soft noise
			}
			
			const source = this.audioContext!.createBufferSource();
			const gainNode = this.audioContext!.createGain();
			const filterNode = this.audioContext!.createBiquadFilter();
			
			source.buffer = buffer;
			filterNode.type = 'lowpass';
			filterNode.frequency.setValueAtTime(1000, this.audioContext!.currentTime);
			
			source.connect(filterNode);
			filterNode.connect(gainNode);
			gainNode.connect(this.getAudioOutput());
			
			// Soft breath envelope
			gainNode.gain.setValueAtTime(0, this.audioContext!.currentTime);
			gainNode.gain.linearRampToValueAtTime(0.001, this.audioContext!.currentTime + 0.1); // Reduced to prevent clipping
			gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext!.currentTime + 0.5);
			
			source.start(this.audioContext!.currentTime);
			source.stop(this.audioContext!.currentTime + 0.5);
			
		} catch (error) {
			console.warn('[Breath Sound] Failed to synthesize breath:', error);
		}
	}
	
	// Evolve a musical pattern to create variations
	evolveMusicalPattern(pattern: MusicalPattern): void {
		// Create a variation of the existing pattern
		const evolvedPattern = { ...pattern };
		evolvedPattern.id = `evolved_${pattern.id}_${Date.now()}`;
		evolvedPattern.name = `Evolved ${pattern.name}`;
		
		// Slightly modify notes
		evolvedPattern.notes = pattern.notes.map(note => ({
			...note,
			pitch: note.pitch * (1 + (Math.random() - 0.5) * 0.1), // ±5% pitch variation
			duration: note.duration * (1 + (Math.random() - 0.5) * 0.2), // ±10% duration variation
			velocity: Math.max(0.3, Math.min(1.0, note.velocity + (Math.random() - 0.5) * 0.2))
		}));
		
		// Evolve harmony if present
		if (evolvedPattern.harmony && pattern.harmony) {
			evolvedPattern.harmony = pattern.harmony.map(harmonyNote => ({
				...harmonyNote,
				pitch: harmonyNote.pitch * (1 + (Math.random() - 0.5) * 0.08),
				velocity: Math.max(0.2, Math.min(0.8, harmonyNote.velocity + (Math.random() - 0.5) * 0.15))
			}));
		}
		
		// Increase complexity slightly
		evolvedPattern.complexity = Math.min(10, pattern.complexity + 0.5);
		evolvedPattern.discoveryStep = this.step;
		evolvedPattern.usageCount = 0;
		evolvedPattern.learningPhase = 'discovery';
		
		// Store the evolved pattern
		this.musicalPatterns.set(evolvedPattern.id, evolvedPattern);
		this.discoveredMelodies.push(evolvedPattern);
		
		console.log(`[Musical Evolution] Evolved pattern: ${pattern.name} -> ${evolvedPattern.name}`);
	}
	
	// Synthesize a single musical note with vowel sound
	synthesizeMusicalNote(note: MusicalNote): void {
		if (!this.initializeAudioContext()) return;
		
		console.log(`[Musical Note] Playing ${note.phoneticValue} at ${note.pitch.toFixed(1)}Hz for ${note.duration}ms`);
		
		try {
			// Initialize physical synthesizer if not already done
			if (!this.physicalSynthesizer && this.audioContext) {
				this.physicalSynthesizer = createPhysicalSoundSynthesizer(this.audioContext, this);
				console.log('[Musical Note] Physical synthesizer initialized');
			}
			
			// Use PHYSICAL MODELING if synthesizer is available
			if (this.physicalSynthesizer && note.phoneticValue) {
				console.log(`[Musical Note] Using PHYSICAL MODELING for ${note.phoneticValue}`);
				
				try {
					// Generate formant-based musical note
					const buffer = this.physicalSynthesizer.synthesizeMusicalNote(
						note.pitch,
						note.phoneticValue,
						note.duration / 1000,
						note.velocity
					);
					
					// Play the buffer
					const source = this.audioContext!.createBufferSource();
					source.buffer = buffer;
					source.connect(this.getAudioOutput());
					source.start(this.audioContext!.currentTime);
					
					// Still synthesize vowel sound for richer texture
					this.synthesizeVowelWithMusicalPitch(note.phoneticValue, note.pitch, note.duration);
					
					return; // Success with physical modeling
				} catch (physicalError) {
					console.warn('[Musical Note] Physical modeling failed, using fallback:', physicalError);
				}
			}
			
			// Fallback to simple oscillator + speech synthesis
			// Create oscillator for the musical pitch
			const oscillator = this.audioContext!.createOscillator();
			const gainNode = this.audioContext!.createGain();
			
			oscillator.connect(gainNode);
			gainNode.connect(this.getAudioOutput());
			
			// Set frequency to musical pitch
			oscillator.frequency.setValueAtTime(note.pitch, this.audioContext!.currentTime);
			oscillator.type = 'sine'; // Pure tone for musical notes
			
			// Set volume envelope
			gainNode.gain.setValueAtTime(0, this.audioContext!.currentTime);
			gainNode.gain.linearRampToValueAtTime(0.001, this.audioContext!.currentTime + 0.01); // Reduced to prevent clipping
			gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext!.currentTime + note.duration / 1000);
			
			// Start and stop the note
			oscillator.start(this.audioContext!.currentTime);
			oscillator.stop(this.audioContext!.currentTime + note.duration / 1000);
			
			// Also synthesize the vowel sound using speech synthesis
			if (note.phoneticValue) {
				this.synthesizeVowelWithMusicalPitch(note.phoneticValue, note.pitch, note.duration);
			}
			
		} catch (error) {
			console.warn('[Musical Note] Failed to synthesize note:', error);
		}
	}
	
	// Synthesize vowel with musical pitch using speech synthesis
	synthesizeVowelWithMusicalPitch(phoneticValue: string, pitch: number, duration: number): void {
		if (!('speechSynthesis' in window)) return;
		
		try {
			const utterance = new SpeechSynthesisUtterance(phoneticValue);
			
			// Convert musical pitch to speech synthesis pitch (0.5-2.0 range)
			const speechPitch = Math.max(0.5, Math.min(2.0, (pitch - 100) / 400));
			utterance.pitch = speechPitch;
			
			// Adjust rate based on duration
			const speechRate = Math.max(0.3, Math.min(1.5, 1000 / duration));
			utterance.rate = speechRate;
			
			utterance.volume = 0.8;
			
			// Get a good voice
			const voices = speechSynthesis.getVoices();
			if (voices.length > 0) {
				utterance.voice = voices.find(v => v.default) || voices[0];
			}
			
			speechSynthesis.speak(utterance);
			
		} catch (error) {
			console.warn('[Musical Vowel] Failed to synthesize vowel with pitch:', error);
		}
	}
	
	// Initialize musical scales for the creature (only once)
	private musicalScalesInitialized: boolean = false;
	
	initializeMusicalScales(): void {
		// Only initialize once
		if (this.musicalScalesInitialized) return;
		// C Major Pentatonic (most natural for beginners)
		this.availableScales.push({
			name: 'C Major Pentatonic',
			intervals: [0, 2, 4, 7, 9], // C, D, E, G, A
			root: 261.63, // C4
			mode: 'pentatonic'
		});
		
		// A Minor Pentatonic (blues feel)
		this.availableScales.push({
			name: 'A Minor Pentatonic',
			intervals: [0, 3, 5, 7, 10], // A, C, D, E, G
			root: 220.00, // A3
			mode: 'pentatonic'
		});
		
		// C Major Scale
		this.availableScales.push({
			name: 'C Major',
			intervals: [0, 2, 4, 5, 7, 9, 11], // C, D, E, F, G, A, B
			root: 261.63, // C4
			mode: 'major'
		});
		
		// A Minor Scale
		this.availableScales.push({
			name: 'A Minor',
			intervals: [0, 2, 3, 5, 7, 8, 10], // A, B, C, D, E, F, G
			root: 220.00, // A3
			mode: 'minor'
		});
		
		// Blues Scale
		this.availableScales.push({
			name: 'C Blues',
			intervals: [0, 3, 5, 6, 7, 10], // C, Eb, F, Gb, G, Bb
			root: 261.63, // C4
			mode: 'blues'
		});
		
		// Dorian Mode (mysterious feel)
		this.availableScales.push({
			name: 'D Dorian',
			intervals: [0, 2, 3, 5, 7, 9, 10], // D, E, F, G, A, B, C
			root: 293.66, // D4
			mode: 'dorian'
		});
		
		this.musicalScalesInitialized = true;
		console.log(`[Musical Scales] Initialized ${this.availableScales.length} musical scales`);
	}
	
	// Generate a harmonic singing pattern with multiple simultaneous notes
	generateHarmonicPattern(): MusicalPattern {
		const melodyLength = 3 + Math.floor(Math.random() * 4); // 3-6 notes
		const melodyNotes: MusicalNote[] = [];
		const harmonyNotes: MusicalNote[] = [];
		
		// Choose a scale for this pattern
		const scale = this.getRandomScale();
		let currentPitch = scale.root;
		
		for (let i = 0; i < melodyLength; i++) {
			// Choose melody note from scale
			const intervalIndex = Math.floor(Math.random() * scale.intervals.length);
			const melodyPitch = scale.root * Math.pow(2, scale.intervals[intervalIndex] / 12);
			
			// Create melody note
			const melodyNote: MusicalNote = {
				pitch: melodyPitch,
				duration: this.getDurationFromRhythm(this.rhythmPatterns[Math.floor(Math.random() * this.rhythmPatterns.length)]),
				velocity: 0.7 + Math.random() * 0.3,
				phoneticValue: this.findVowelForPitch(melodyPitch)?.phoneticValue || 'ah',
				musicalInterval: this.calculateMusicalInterval(currentPitch, melodyPitch),
				harmonicFunction: this.determineHarmonicFunction(i, melodyLength),
				ornamentation: Math.random() < 0.3 ? 'vibrato' : 'none',
				harmonicRole: 'melody'
			};
			
			// Create harmony note (typically a third or fifth below)
			const harmonyInterval = Math.random() < 0.5 ? 3 : 7; // Minor third or perfect fifth
			const harmonyPitch = melodyPitch * Math.pow(2, -harmonyInterval / 12);
			
			const harmonyNote: MusicalNote = {
				pitch: harmonyPitch,
				duration: melodyNote.duration,
				velocity: melodyNote.velocity * 0.6, // Softer harmony
				phoneticValue: this.findVowelForPitch(harmonyPitch)?.phoneticValue || 'oh',
				harmonicRole: 'harmony'
			};
			
			melodyNotes.push(melodyNote);
			harmonyNotes.push(harmonyNote);
			currentPitch = melodyPitch;
		}
		
		const pattern: MusicalPattern = {
			id: `harmony_${Date.now()}`,
			name: `Harmonic Pattern ${this.discoveredMelodies.length + 1}`,
			notes: melodyNotes,
			harmony: harmonyNotes,
			rhythm: {
				pattern: melodyNotes.map(n => this.getRhythmFromDuration(n.duration)).join(','),
				beatsPerMeasure: 4,
				beatValue: 4,
				tempo: 100 + Math.random() * 40, // 100-140 BPM
				syncopation: Math.random() * 0.3 // 0-30% syncopation
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(10, melodyLength + Math.random() * 3),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'harmony',
			musicalMode: scale.mode === 'chromatic' ? 'major' : scale.mode
		};
		
		return pattern;
	}
	
	// Generate an improvisational singing pattern (less structured, more flowing)
	generateImprovisationPattern(): MusicalPattern {
		const phraseLength = 4 + Math.floor(Math.random() * 6); // 4-9 notes
		const notes: MusicalNote[] = [];
		const scale = this.getRandomScale();
		let currentPitch = scale.root;
		
		// Create flowing, improvisational melody
		for (let i = 0; i < phraseLength; i++) {
			// More random note selection for improvisation
			const intervalIndex = Math.floor(Math.random() * scale.intervals.length);
			const targetPitch = scale.root * Math.pow(2, scale.intervals[intervalIndex] / 12);
			
			// Add some pitch bending or sliding for more expression
			const ornamentation = this.getRandomOrnamentation();
			const duration = this.getImprovisationalDuration();
			
			const note: MusicalNote = {
				pitch: targetPitch,
				duration: duration,
				velocity: 0.6 + Math.random() * 0.4,
				phoneticValue: this.findVowelForPitch(targetPitch)?.phoneticValue || 'ah',
				musicalInterval: this.calculateMusicalInterval(currentPitch, targetPitch),
				ornamentation: ornamentation,
				harmonicRole: 'melody'
			};
			
			notes.push(note);
			currentPitch = targetPitch;
		}
		
		const pattern: MusicalPattern = {
			id: `improvisation_${Date.now()}`,
			name: `Improvisation ${this.discoveredMelodies.length + 1}`,
			notes: notes,
			rhythm: {
				pattern: notes.map(n => this.getRhythmFromDuration(n.duration)).join(','),
				beatsPerMeasure: 4,
				beatValue: 4,
				tempo: 80 + Math.random() * 60, // 80-140 BPM, more variation
				syncopation: 0.2 + Math.random() * 0.4 // 20-60% syncopation for improvisation
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(10, phraseLength + Math.random() * 4),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'improvisation',
			musicalMode: scale.mode === 'chromatic' ? 'pentatonic' : scale.mode
		};
		
		return pattern;
	}
	
	// Generate a call-and-response singing pattern
	generateCallResponsePattern(): MusicalPattern {
		const callLength = 2 + Math.floor(Math.random() * 3); // 2-4 notes for call
		const responseLength = 2 + Math.floor(Math.random() * 3); // 2-4 notes for response
		const callNotes: MusicalNote[] = [];
		const responseNotes: MusicalNote[] = [];
		const scale = this.getRandomScale();
		
		// Generate call (higher, more assertive)
		let currentPitch = scale.root * 1.5; // Start higher
		for (let i = 0; i < callLength; i++) {
			const intervalIndex = Math.floor(Math.random() * scale.intervals.length);
			const targetPitch = scale.root * Math.pow(2, (scale.intervals[intervalIndex] + 12) / 12); // Higher octave
			
			const note: MusicalNote = {
				pitch: targetPitch,
				duration: this.getDurationFromRhythm('quarter'),
				velocity: 0.8 + Math.random() * 0.2, // Stronger for call
				phoneticValue: this.findVowelForPitch(targetPitch)?.phoneticValue || 'ah',
				musicalInterval: this.calculateMusicalInterval(currentPitch, targetPitch),
				ornamentation: 'none',
				harmonicRole: 'melody'
			};
			
			callNotes.push(note);
			currentPitch = targetPitch;
		}
		
		// Generate response (lower, more contemplative)
		currentPitch = scale.root * 0.75; // Start lower
		for (let i = 0; i < responseLength; i++) {
			const intervalIndex = Math.floor(Math.random() * scale.intervals.length);
			const targetPitch = scale.root * Math.pow(2, scale.intervals[intervalIndex] / 12); // Lower octave
			
			const note: MusicalNote = {
				pitch: targetPitch,
				duration: this.getDurationFromRhythm('half'),
				velocity: 0.5 + Math.random() * 0.3, // Softer for response
				phoneticValue: this.findVowelForPitch(targetPitch)?.phoneticValue || 'oh',
				musicalInterval: this.calculateMusicalInterval(currentPitch, targetPitch),
				ornamentation: 'vibrato',
				harmonicRole: 'melody'
			};
			
			responseNotes.push(note);
			currentPitch = targetPitch;
		}
		
		// Combine call and response
		const allNotes = [...callNotes, ...responseNotes];
		
		const pattern: MusicalPattern = {
			id: `call_response_${Date.now()}`,
			name: `Call & Response ${this.discoveredMelodies.length + 1}`,
			notes: allNotes,
			rhythm: {
				pattern: allNotes.map(n => this.getRhythmFromDuration(n.duration)).join(','),
				beatsPerMeasure: 8, // Longer pattern
				beatValue: 4,
				tempo: 90 + Math.random() * 30,
				syncopation: 0.1 + Math.random() * 0.2
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(10, callLength + responseLength + Math.random() * 3),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'call_response',
			musicalMode: scale.mode === 'chromatic' ? 'pentatonic' : scale.mode
		};
		
		return pattern;
	}
	
	// Generate a scale run pattern (ascending or descending scales)
	generateScaleRunPattern(): MusicalPattern {
		const scale = this.getRandomScale();
		const runLength = Math.min(scale.intervals.length, 5 + Math.floor(Math.random() * 3)); // 5-8 notes
		const isAscending = Math.random() < 0.7; // 70% ascending
		const notes: MusicalNote[] = [];
		
		const startIndex = isAscending ? 0 : scale.intervals.length - 1;
		const direction = isAscending ? 1 : -1;
		
		for (let i = 0; i < runLength; i++) {
			const intervalIndex = startIndex + (i * direction);
			const interval = scale.intervals[intervalIndex % scale.intervals.length];
			const pitch = scale.root * Math.pow(2, interval / 12);
			
			const note: MusicalNote = {
				pitch: pitch,
				duration: this.getDurationFromRhythm('eighth'), // Faster for scale runs
				velocity: 0.6 + Math.random() * 0.3,
				phoneticValue: this.findVowelForPitch(pitch)?.phoneticValue || 'ah',
				musicalInterval: i === 0 ? 0 : Math.abs(direction),
				ornamentation: 'none',
				harmonicRole: 'melody'
			};
			
			notes.push(note);
		}
		
		const pattern: MusicalPattern = {
			id: `scale_run_${Date.now()}`,
			name: `${isAscending ? 'Ascending' : 'Descending'} Scale Run`,
			notes: notes,
			rhythm: {
				pattern: 'eighth,eighth,eighth,eighth,eighth',
				beatsPerMeasure: 4,
				beatValue: 4,
				tempo: 120 + Math.random() * 40,
				syncopation: 0.1
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(10, runLength + Math.random() * 2),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'scale_run',
			musicalMode: scale.mode === 'chromatic' ? 'major' : scale.mode
		};
		
		return pattern;
	}
	
	// Helper methods for enhanced musical patterns
	getRandomScale(): MusicalScale {
		return this.availableScales[Math.floor(Math.random() * this.availableScales.length)];
	}
	
	getRandomOrnamentation(): 'trill' | 'vibrato' | 'bend' | 'slide' | 'none' {
		const ornamentations: ('trill' | 'vibrato' | 'bend' | 'slide' | 'none')[] = 
			['none', 'none', 'none', 'vibrato', 'bend', 'slide']; // Weighted toward 'none'
		return ornamentations[Math.floor(Math.random() * ornamentations.length)];
	}
	
	getImprovisationalDuration(): number {
		const durations = [125, 250, 375, 500, 750]; // Various durations for improvisation
		return durations[Math.floor(Math.random() * durations.length)];
	}
	
	// Generate a longer musical phrase with natural flow
	generateMusicalPhrase(): MusicalPhrase {
		const phraseLength = 6 + Math.floor(Math.random() * 8); // 6-13 notes for longer phrases
		const melodyNotes: MusicalNote[] = [];
		const harmonyNotes: MusicalNote[] = [];
		const scale = this.getRandomScale();
		
		// Choose emotional tone for the phrase
		const emotionalTones: ('joyful' | 'contemplative' | 'energetic' | 'melancholy' | 'mysterious')[] = 
			['joyful', 'contemplative', 'energetic', 'melancholy', 'mysterious'];
		const emotionalTone = emotionalTones[Math.floor(Math.random() * emotionalTones.length)];
		
		let currentPitch = scale.root;
		
		// Create flowing phrase with emotional character
		for (let i = 0; i < phraseLength; i++) {
			// Choose notes based on emotional tone
			let targetPitch: number;
			let ornamentation: 'trill' | 'vibrato' | 'bend' | 'slide' | 'none';
			let duration: number;
			
			switch (emotionalTone) {
				case 'joyful':
					// Higher pitches, faster rhythms, more ornamentation
					const joyInterval = scale.intervals[Math.floor(Math.random() * scale.intervals.length)];
					targetPitch = scale.root * Math.pow(2, (joyInterval + 12) / 12); // Higher octave
					ornamentation = Math.random() < 0.4 ? 'vibrato' : 'none';
					duration = this.getDurationFromRhythm(['eighth', 'quarter', 'triplet'][Math.floor(Math.random() * 3)]);
					break;
					
				case 'contemplative':
					// Slower, more sustained notes
					const contemplativeInterval = scale.intervals[Math.floor(Math.random() * scale.intervals.length)];
					targetPitch = scale.root * Math.pow(2, contemplativeInterval / 12);
					ornamentation = 'none';
					duration = this.getDurationFromRhythm(['half', 'whole', 'dotted_half'][Math.floor(Math.random() * 3)]);
					break;
					
				case 'energetic':
					// Fast, rhythmic, with bends
					const energeticInterval = scale.intervals[Math.floor(Math.random() * scale.intervals.length)];
					targetPitch = scale.root * Math.pow(2, energeticInterval / 12);
					ornamentation = Math.random() < 0.3 ? 'bend' : 'none';
					duration = this.getDurationFromRhythm(['eighth', 'triplet', 'dotted_eighth'][Math.floor(Math.random() * 3)]);
					break;
					
				case 'melancholy':
					// Lower pitches, minor intervals, slides
					const melancholyInterval = scale.intervals[Math.floor(Math.random() * scale.intervals.length)];
					targetPitch = scale.root * Math.pow(2, melancholyInterval / 12) * 0.75; // Lower octave
					ornamentation = Math.random() < 0.4 ? 'slide' : 'none';
					duration = this.getDurationFromRhythm(['quarter', 'dotted_quarter', 'half'][Math.floor(Math.random() * 3)]);
					break;
					
				case 'mysterious':
					// Unusual intervals, varied ornamentation
					const mysteriousInterval = scale.intervals[Math.floor(Math.random() * scale.intervals.length)];
					targetPitch = scale.root * Math.pow(2, mysteriousInterval / 12) * (0.8 + Math.random() * 0.4);
					const ornamentationOptions: ('trill' | 'vibrato' | 'bend' | 'slide' | 'none')[] = ['trill', 'vibrato', 'bend', 'slide', 'none'];
					ornamentation = ornamentationOptions[Math.floor(Math.random() * ornamentationOptions.length)];
					duration = this.getDurationFromRhythm(this.rhythmPatterns[Math.floor(Math.random() * this.rhythmPatterns.length)]);
					break;
			}
			
			const note: MusicalNote = {
				pitch: targetPitch,
				duration: duration,
				velocity: 0.6 + Math.random() * 0.4,
				phoneticValue: this.findVowelForPitch(targetPitch)?.phoneticValue || 'ah',
				musicalInterval: this.calculateMusicalInterval(currentPitch, targetPitch),
				ornamentation: ornamentation,
				harmonicRole: 'melody'
			};
			
			melodyNotes.push(note);
			currentPitch = targetPitch;
			
			// Add harmony note occasionally
			if (Math.random() < 0.4) {
				const harmonyInterval = Math.random() < 0.5 ? 3 : 7; // Third or fifth
				const harmonyPitch = targetPitch * Math.pow(2, -harmonyInterval / 12);
				
				const harmonyNote: MusicalNote = {
					pitch: harmonyPitch,
					duration: duration * 1.2,
					velocity: note.velocity * 0.5,
					phoneticValue: this.findVowelForPitch(harmonyPitch)?.phoneticValue || 'oh',
					harmonicRole: 'harmony'
				};
				
				harmonyNotes.push(harmonyNote);
			}
		}
		
		const phrase: MusicalPhrase = {
			id: `phrase_${Date.now()}`,
			notes: melodyNotes,
			harmony: harmonyNotes,
			rhythm: {
				pattern: melodyNotes.map(n => this.getRhythmFromDuration(n.duration)).join(','),
				beatsPerMeasure: 8,
				beatValue: 4,
				tempo: 90 + Math.random() * 50,
				syncopation: 0.1 + Math.random() * 0.3
			},
			emotionalTone: emotionalTone,
			complexity: Math.min(10, phraseLength + Math.random() * 3),
			repetition: Math.random() < 0.6 // 60% chance of being repeatable
		};
		
		return phrase;
	}
	
	// Enhanced rhythm pattern generation with more complex patterns
	generateComplexRhythmPattern(): MusicalPattern {
		const rhythmTypes = [
			'straight', 'swung', 'syncopated', 'polyrhythmic', 'mixed'
		];
		const rhythmType = rhythmTypes[Math.floor(Math.random() * rhythmTypes.length)];
		const notes: MusicalNote[] = [];
		const basePitch = 440;
		
		switch (rhythmType) {
			case 'straight':
				// Regular quarter/eighth note patterns
				for (let i = 0; i < 4; i++) {
					const duration = this.getDurationFromRhythm(['quarter', 'eighth'][Math.floor(Math.random() * 2)]);
					const note: MusicalNote = {
						pitch: basePitch + (Math.random() - 0.5) * 100,
						duration: duration,
						velocity: 0.7 + Math.random() * 0.3,
						phoneticValue: 'ah',
						harmonicRole: 'rhythm'
					};
					notes.push(note);
				}
				break;
				
			case 'swung':
				// Swung eighth notes
				for (let i = 0; i < 6; i++) {
					const duration = this.getDurationFromRhythm('eighth') * (i % 2 === 0 ? 1.3 : 0.7); // Swing feel
					const note: MusicalNote = {
						pitch: basePitch + (Math.random() - 0.5) * 50,
						duration: duration,
						velocity: 0.6 + Math.random() * 0.4,
						phoneticValue: 'ah',
						harmonicRole: 'rhythm'
					};
					notes.push(note);
				}
				break;
				
			case 'syncopated':
				// Syncopated rhythms with off-beat emphasis
				for (let i = 0; i < 5; i++) {
					const duration = this.getDurationFromRhythm(['eighth', 'quarter', 'dotted_eighth'][Math.floor(Math.random() * 3)]);
					const note: MusicalNote = {
						pitch: basePitch + (Math.random() - 0.5) * 80,
						duration: duration,
						velocity: i % 2 === 1 ? 0.8 + Math.random() * 0.2 : 0.5 + Math.random() * 0.3, // Off-beat emphasis
						phoneticValue: 'ah',
						harmonicRole: 'rhythm'
					};
					notes.push(note);
				}
				break;
				
			case 'polyrhythmic':
				// Multiple simultaneous rhythms
				for (let i = 0; i < 8; i++) {
					const duration = this.getDurationFromRhythm(['triplet', 'eighth', 'quarter'][Math.floor(Math.random() * 3)]);
					const note: MusicalNote = {
						pitch: basePitch + (Math.random() - 0.5) * 120,
						duration: duration,
						velocity: 0.5 + Math.random() * 0.5,
						phoneticValue: 'ah',
						harmonicRole: 'rhythm'
					};
					notes.push(note);
				}
				break;
				
			case 'mixed':
				// Mixed rhythm patterns
				for (let i = 0; i < 7; i++) {
					const duration = this.getDurationFromRhythm(this.rhythmPatterns[Math.floor(Math.random() * this.rhythmPatterns.length)]);
					const note: MusicalNote = {
						pitch: basePitch + (Math.random() - 0.5) * 150,
						duration: duration,
						velocity: 0.4 + Math.random() * 0.6,
						phoneticValue: 'ah',
						harmonicRole: 'rhythm'
					};
					notes.push(note);
				}
				break;
		}
		
		const pattern: MusicalPattern = {
			id: `complex_rhythm_${Date.now()}`,
			name: `${rhythmType.charAt(0).toUpperCase() + rhythmType.slice(1)} Rhythm`,
			notes: notes,
			rhythm: {
				pattern: notes.map(n => this.getRhythmFromDuration(n.duration)).join(','),
				beatsPerMeasure: 4,
				beatValue: 4,
				tempo: 100 + Math.random() * 60,
				syncopation: rhythmType === 'syncopated' ? 0.4 + Math.random() * 0.3 : Math.random() * 0.2,
				polyrhythm: rhythmType === 'polyrhythmic' ? [3, 2] : undefined
			},
			key: this.currentMusicalKey,
			timeSignature: '4/4',
			complexity: Math.min(10, notes.length + Math.random() * 3),
			discoveryStep: this.step,
			usageCount: 0,
			learningPhase: 'discovery',
			patternType: 'phrase',
			musicalMode: 'pentatonic'
		};
		
		return pattern;
	}
	
	// --- Limb System Integration ---
	
	updateLimbSystem(): void {
		// Update energy system
		if (this.energySystem) {
			this.energySystem.update(0.016); // Fixed timestep (60fps)
		}
		
		// Update organ system
		if (this.organSystem) {
			this.organSystem.updateOrgans();
			this.organSystem.developOrgans();
		}
		
		// Update existing limbs
		this.limbSystem.updateLimbs();
		
		// Check for new limb development
		if (this.step - this.lastLimbDevelopment >= this.limbDevelopmentInterval) {
			if (Math.random() < this.limbGrowthRate) {
				this.limbSystem.developLimbs();
				this.lastLimbDevelopment = this.step;
			}
		}
	}
	
	// Autonomous mouth exploration and voice learning
	updateMouthExploration(): void {
		if (!this.mouthSystem || !this.creatureVoice) return;
		
		const dt = 0.016; // Fixed timestep
		const avgEnergy = this.nodes.length > 0
			? this.nodes.reduce((sum, n) => sum + (n.energy || 0), 0) / this.nodes.length
			: 0.5;
		const growthProgress = Math.min(1, this.nodes.length / Math.max(1, this.getCurrentNodeLimit()));
		const normalizedEnergy = Math.max(0, Math.min(1, avgEnergy / 5));
		const complexityProgress = Math.max(0, Math.min(1, this.linguisticComplexity / 10));

		// Keep mouth articulation coupled to simulation growth rather than UI controls.
		this.mouthSystem.updateProceduralState({
			growthProgress,
			energyLevel: normalizedEnergy,
			linguisticComplexity: complexityProgress,
			step: this.step,
			growthVarianceSeed: this.growthVarianceSeed
		});
		
		// Update mouth exploration state (curiosity, phases)
		this.mouthSystem.updateExploration(dt, avgEnergy, this.linguisticComplexity);
		
		// Try autonomous phonation if conditions allow
		const mouth = this.mouthSystem;
		const attempted = mouth.attemptAutonomousPhonation(
			() => this.creatureVoice.canPhonate(), // Check if can phonate
			(state) => {
				// Execute phonation with the given mouth state
				this.creatureVoice.phonateWithMouth(600, undefined, state);
			},
			() => {
				// Get network response after phonation
				return this.creatureVoice.getNetworkResponseToPhonation();
			}
		);
		
		if (attempted) {
			const exploration = mouth.getExplorationState();
			if (exploration.totalPhonations % 10 === 0) {
				console.log(`[Mouth Exploration] Total: ${exploration.totalPhonations}, Successful: ${exploration.successfulPhonations}, Learned: ${exploration.learnedConfigs.size}, Curiosity: ${(exploration.curiosity * 100).toFixed(1)}%`);
			}
		}
	}
	
	// Limb system control methods
	toggleLimbDevelopment(): void {
		this.limbDevelopmentEnabled = !this.limbDevelopmentEnabled;
		console.log(`[Creature ${this.creatureId}] Limb development ${this.limbDevelopmentEnabled ? 'enabled' : 'disabled'}`);
	}
	
	setLimbGrowthRate(rate: number): void {
		this.limbGrowthRate = Math.max(0, Math.min(1, rate));
		console.log(`[Creature ${this.creatureId}] Limb growth rate set to ${this.limbGrowthRate}`);
	}
	
	getLimbCount(): number {
		return this.limbSystem.getLimbCount();
	}
	
	getLimbsByType(type: LimbType): Limb[] {
		return this.limbSystem.getLimbsByType(type);
	}
	
	getAllLimbs(): Limb[] {
		return this.limbSystem.getLimbs();
	}
	
// --- Audio Synthesis Methods ---
	
	// Initialize audio context for synthesis
	initializeAudioContext(): boolean {
		try {
			// Prefer shared UI AudioContext if available; otherwise adopt AudioEngine context
			if (!this.audioContext || this.audioContext.state === 'closed') {
				const engine = getAudioEngine();
				this.audioContext = engine.context;
			}
			
			// Don't try to resume suspended context - wait for user interaction
			// The UI will handle resume after user interaction
			
			// Ensure master gain exists and is connected
			if (!this.masterGain) {
				this.masterGain = this.audioContext.createGain();
				this.masterGain.gain.setValueAtTime(0, this.audioContext.currentTime);
				// Route simulator master into centralized AudioEngine ambience bus
				try {
					const engine = getAudioEngine();
					// Only connect if contexts match (they should, see adoption above)
					if (engine.context === this.audioContext) {
						this.masterGain.connect(engine.getBusGain('ambience'));
					} else {
						// Fallback: connect directly (should be rare)
						this.masterGain.connect(this.audioContext.destination);
					}
				} catch {
					this.masterGain.connect(this.audioContext.destination);
				}
			}
			
			// Initialize reverb node if not already done and context is running
			if (this.audioContext.state === 'running' && !this.reverbNode) {
				this.initializeReverbNode();
			}
			
			return this.audioContext.state === 'running';
		} catch (error) {
			console.warn('[Audio] Failed to initialize audio context:', error);
			return false;
		}
	}
	
	// Initialize reverb node for enhanced audio
	initializeReverbNode(): void {
		if (!this.audioContext) return;
		
		try {
			const length = this.audioContext.sampleRate * 0.5; // 0.5 second reverb
			const impulse = this.audioContext.createBuffer(2, length, this.audioContext.sampleRate);
			
			for (let c = 0; c < 2; c++) {
				const channel = impulse.getChannelData(c);
				for (let i = 0; i < length; i++) {
					channel[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.0);
				}
			}
			
			this.reverbNode = this.audioContext.createConvolver();
			this.reverbNode.buffer = impulse;
		} catch (error) {
			console.warn('[Audio] Failed to initialize reverb node:', error);
		}
	}

// Energizing zap drone setup (lively, upward, gentle)
private ensureZapDrone(): void {
    if (!this.audioContext || !this.enableZapAudio) return;
    const now = this.audioContext.currentTime;
    if (!this.zapGain) {
        this.zapGain = this.audioContext.createGain();
        this.zapGain.gain.setValueAtTime(0, now);
			if (this.reverbNode) { this.zapGain.connect(this.reverbNode); this.reverbNode.connect(this.getAudioOutput()); }
			else { this.zapGain.connect(this.getAudioOutput()); }
    }
    if (!this.zapOscillator) {
        this.zapOscillator = this.audioContext.createOscillator();
        this.zapOscillator.type = 'sawtooth';
        this.zapOscillator.frequency.setValueAtTime(0, now);
        this.zapOscillator.connect(this.zapGain);
        this.zapOscillator.start(now);
    }
}

	// Play a single short zap for a chain tip on a specific pulse (guarded)
	private tryPlayChainZap(tipNodeId: number, pulseStep: number): void {
		if (!this.audioContext || this.audioContext.state !== 'running' || !this.enableZapAudio || !this.enableZapOneShots) return;
		const last = this.lastZapPulseByTip.get(tipNodeId);
		if (last === pulseStep) return;
		this.lastZapPulseByTip.set(tipNodeId, pulseStep);
		try {
			const ctx = this.audioContext;
			const now = ctx.currentTime;
			// Compute local metrics at the tip
			const connections = this.edges.filter(e => e.from === tipNodeId || e.to === tipNodeId);
			let avgDist = 0;
			for (const e of connections) {
				const a = this.nodes[e.from];
				const b = this.nodes[e.to];
				if (!a || !b) continue;
				const dx = b.x - a.x; const dy = b.y - a.y; const dz = (b.z||0) - (a.z||0);
				avgDist += Math.sqrt(dx*dx + dy*dy + dz*dz);
			}
			avgDist = connections.length > 0 ? avgDist / connections.length : 60;
			// Map distance and connectivity to physical parameters
			const edgesHere = connections.length;
			const baseFreq = 118 + Math.min(14, edgesHere) * 30 + Math.max(0, 120 - Math.min(120, avgDist));
			const duration = 0.14 + Math.min(0.08, avgDist / 800);
			const excitement = Math.min(1, 0.3 + edgesHere * 0.05);
			// Prefer physical modal synthesis if available
            if (this.physicalSynthesizer) {
                const buffer = this.physicalSynthesizer.synthesizeDiscoverySound(baseFreq, duration, excitement);
                const src = ctx.createBufferSource();
                src.buffer = buffer;
                const hpf = ctx.createBiquadFilter();
                hpf.type = 'highpass';
                hpf.frequency.setValueAtTime(180, now);
                hpf.Q.setValueAtTime(0.707, now);
                const g = ctx.createGain();
                g.gain.value = 0.02; // lower zap level so harmonics dominate
                src.connect(hpf);
                hpf.connect(g);
                if (this.reverbNode) { g.connect(this.reverbNode); this.reverbNode.connect(this.getAudioOutput()); }
                else { g.connect(this.getAudioOutput()); }
                src.start(now);
            } else {
				// Fallback to simple oscillator zap
				const osc = ctx.createOscillator();
				osc.type = 'square';
                const hpf = ctx.createBiquadFilter();
                hpf.type = 'highpass';
                hpf.frequency.setValueAtTime(200, now);
                hpf.Q.setValueAtTime(0.707, now);
                const gain = ctx.createGain();
				osc.frequency.setValueAtTime(baseFreq, now);
				osc.frequency.exponentialRampToValueAtTime(Math.max(120, baseFreq * 0.6), now + duration * 0.9);
				gain.gain.setValueAtTime(0, now);
                gain.gain.linearRampToValueAtTime(0.03, now + 0.01);
				gain.gain.exponentialRampToValueAtTime(0.0008, now + duration);
                osc.connect(hpf);
                hpf.connect(gain);
                if (this.reverbNode) { gain.connect(this.reverbNode); this.reverbNode.connect(this.getAudioOutput()); }
                else { gain.connect(this.getAudioOutput()); }
				osc.start(now);
				osc.stop(now + duration);
			}
		} catch {}
	}

// Drive lively upward chirps based on energy, stability, and breath
private updateZapDrone(now: number): void {
    if (!this.audioContext || !this.enableZapAudio) return;
    // Auto-deactivate after a short window from last lightning
    if (this.chainLightningActive && Date.now() - this.lastChainLightningAt > 600) {
        this.chainLightningActive = false;
    }
    // If no lightning, fade drone out and return
    if (!this.chainLightningActive) {
        if (this.zapGain) {
            this.zapGain.gain.cancelScheduledValues(now);
            this.zapGain.gain.setValueAtTime(this.zapGain.gain.value || 0, now);
            this.zapGain.gain.exponentialRampToValueAtTime(0.0005, now + 0.2);
        }
        return;
    }

    this.ensureZapDrone();
    if (!this.zapOscillator || !this.zapGain) return;

    const energy = Math.max(0, Math.min(1.5, (this.totalNetworkEnergy || 0) / 15000));
    const breath = Math.max(0, Math.min(1, (this.breathingSystem as any)?.masteryLevel || 0));

    const baseFreq = 260 + 320 * energy; // brighter during lightning
    const targetFreq = baseFreq * (1.6 + 0.6 * breath);

    this.zapOscillator.frequency.cancelScheduledValues(now);
    this.zapOscillator.frequency.setValueAtTime(baseFreq, now);
    this.zapOscillator.frequency.exponentialRampToValueAtTime(Math.max(1, targetFreq), now + 0.25);

    const maxGain = 0.05 + 0.05 * energy + 0.03 * breath; // stronger presence during lightning
    this.zapGain.gain.cancelScheduledValues(now);
    this.zapGain.gain.setValueAtTime(this.zapGain.gain.value || 0, now);
    this.zapGain.gain.linearRampToValueAtTime(maxGain, now + 0.05);

    if (Math.random() < 0.15) {
        const over = this.audioContext.createOscillator();
        over.type = 'triangle';
        over.frequency.setValueAtTime(targetFreq * 1.5, now);
        const overGain = this.audioContext.createGain();
        overGain.gain.setValueAtTime(0, now);
        overGain.gain.linearRampToValueAtTime(maxGain * 0.35, now + 0.02);
        overGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
        over.connect(overGain);
        overGain.connect(this.zapGain);
        over.start(now);
        over.stop(now + 0.3);
    }
}
	
	// Pre-populate comprehensive linguistic elements for immediate audio (only once)
	private basicLinguisticElementsInitialized: boolean = false;
	
	initializeBasicLinguisticElements(): void {
		// Only initialize once
		if (this.basicLinguisticElementsInitialized) return;
		
		console.log('[Audio Init] Initializing comprehensive phonetic system...');
		
		// Initialize all vowel types
		this.initializeVowels();
		
		// Initialize all consonant types
		this.initializeConsonants();
		
		// Initialize diphthongs
		this.initializeDiphthongs();
		
		// Initialize tone and stress patterns
		this.initializeToneAndStress();
		
		// Add complex syllables
		this.initializeComplexSyllables();
		
		console.log(`[Audio Init] Pre-populated ${this.linguisticElements.size} linguistic elements for comprehensive audio synthesis`);
		
		// Create linguistic nodes for pre-populated elements
		Array.from(this.linguisticElements.values()).forEach(element => {
			this.createLinguisticNode(element);
		});
		
		// Set up user interaction handler for audio (only once)
		this.setupAudioInteractionHandler();
		
		this.basicLinguisticElementsInitialized = true;
	}
	
	// Initialize comprehensive vowel system
	initializeVowels(): void {
		const vowelData = [
			// Basic vowels
			{ symbol: 'a', ipa: 'a', height: 'open', backness: 'central', roundedness: 'unrounded', frequency: 200 },
			{ symbol: 'e', ipa: 'e', height: 'close_mid', backness: 'front', roundedness: 'unrounded', frequency: 250 },
			{ symbol: 'i', ipa: 'i', height: 'close', backness: 'front', roundedness: 'unrounded', frequency: 300 },
			{ symbol: 'o', ipa: 'o', height: 'close_mid', backness: 'back', roundedness: 'rounded', frequency: 180 },
			{ symbol: 'u', ipa: 'u', height: 'close', backness: 'back', roundedness: 'rounded', frequency: 150 },
			
			// Extended vowels
			{ symbol: 'æ', ipa: 'æ', height: 'near_open', backness: 'front', roundedness: 'unrounded', frequency: 220 },
			{ symbol: 'ɑ', ipa: 'ɑ', height: 'open', backness: 'back', roundedness: 'unrounded', frequency: 190 },
			{ symbol: 'ɛ', ipa: 'ɛ', height: 'open_mid', backness: 'front', roundedness: 'unrounded', frequency: 240 },
			{ symbol: 'ɪ', ipa: 'ɪ', height: 'near_close', backness: 'front', roundedness: 'unrounded', frequency: 280 },
			{ symbol: 'ɔ', ipa: 'ɔ', height: 'open_mid', backness: 'back', roundedness: 'rounded', frequency: 170 },
			{ symbol: 'ʊ', ipa: 'ʊ', height: 'near_close', backness: 'back', roundedness: 'rounded', frequency: 160 },
			{ symbol: 'ʌ', ipa: 'ʌ', height: 'open_mid', backness: 'central', roundedness: 'unrounded', frequency: 210 },
			{ symbol: 'ə', ipa: 'ə', height: 'mid', backness: 'central', roundedness: 'unrounded', frequency: 230 },
			
			// Rounded vowels
			{ symbol: 'y', ipa: 'y', height: 'close', backness: 'front', roundedness: 'rounded', frequency: 290 },
			{ symbol: 'ø', ipa: 'ø', height: 'close_mid', backness: 'front', roundedness: 'rounded', frequency: 260 },
			{ symbol: 'œ', ipa: 'œ', height: 'open_mid', backness: 'front', roundedness: 'rounded', frequency: 250 },
			{ symbol: 'ɯ', ipa: 'ɯ', height: 'close', backness: 'back', roundedness: 'unrounded', frequency: 140 },
			{ symbol: 'ɤ', ipa: 'ɤ', height: 'close_mid', backness: 'back', roundedness: 'unrounded', frequency: 175 },
			{ symbol: 'ɐ', ipa: 'ɐ', height: 'near_open', backness: 'central', roundedness: 'unrounded', frequency: 200 },
		];
		
		vowelData.forEach((vowel, index) => {
			const element: LinguisticElement = {
				phoneticValue: vowel.symbol,
				ipaSymbol: vowel.ipa,
				type: 'vowel',
				frequency: vowel.frequency,
				vowelHeight: vowel.height as any,
				vowelBackness: vowel.backness as any,
				vowelRoundedness: vowel.roundedness as any,
				complexity: 1,
				discoveryStep: 0,
				usageCount: 0,
				learningPhase: 'discovery'
			};
			this.linguisticElements.set(vowel.symbol, element);
			this.discoveredVowels.push(vowel.symbol);
		});
	}
	
	// Initialize comprehensive consonant system
	initializeConsonants(): void {
		const consonantData = [
			// Plosives
			{ symbol: 'p', ipa: 'p', place: 'bilabial', manner: 'plosive', voicing: 'voiceless' },
			{ symbol: 'b', ipa: 'b', place: 'bilabial', manner: 'plosive', voicing: 'voiced' },
			{ symbol: 't', ipa: 't', place: 'alveolar', manner: 'plosive', voicing: 'voiceless' },
			{ symbol: 'd', ipa: 'd', place: 'alveolar', manner: 'plosive', voicing: 'voiced' },
			{ symbol: 'k', ipa: 'k', place: 'velar', manner: 'plosive', voicing: 'voiceless' },
			{ symbol: 'g', ipa: 'g', place: 'velar', manner: 'plosive', voicing: 'voiced' },
			{ symbol: 'q', ipa: 'q', place: 'uvular', manner: 'plosive', voicing: 'voiceless' },
			{ symbol: 'ɢ', ipa: 'ɢ', place: 'uvular', manner: 'plosive', voicing: 'voiced' },
			{ symbol: 'ʔ', ipa: 'ʔ', place: 'glottal', manner: 'plosive', voicing: 'voiceless' },
			
			// Nasals
			{ symbol: 'm', ipa: 'm', place: 'bilabial', manner: 'nasal', voicing: 'voiced' },
			{ symbol: 'n', ipa: 'n', place: 'alveolar', manner: 'nasal', voicing: 'voiced' },
			{ symbol: 'ŋ', ipa: 'ŋ', place: 'velar', manner: 'nasal', voicing: 'voiced' },
			{ symbol: 'ɲ', ipa: 'ɲ', place: 'palatal', manner: 'nasal', voicing: 'voiced' },
			{ symbol: 'ɴ', ipa: 'ɴ', place: 'uvular', manner: 'nasal', voicing: 'voiced' },
			
			// Fricatives
			{ symbol: 'f', ipa: 'f', place: 'labiodental', manner: 'fricative', voicing: 'voiceless' },
			{ symbol: 'v', ipa: 'v', place: 'labiodental', manner: 'fricative', voicing: 'voiced' },
			{ symbol: 'θ', ipa: 'θ', place: 'dental', manner: 'fricative', voicing: 'voiceless' },
			{ symbol: 'ð', ipa: 'ð', place: 'dental', manner: 'fricative', voicing: 'voiced' },
			{ symbol: 's', ipa: 's', place: 'alveolar', manner: 'fricative', voicing: 'voiceless' },
			{ symbol: 'z', ipa: 'z', place: 'alveolar', manner: 'fricative', voicing: 'voiced' },
			{ symbol: 'ʃ', ipa: 'ʃ', place: 'postalveolar', manner: 'fricative', voicing: 'voiceless' },
			{ symbol: 'ʒ', ipa: 'ʒ', place: 'postalveolar', manner: 'fricative', voicing: 'voiced' },
			{ symbol: 'x', ipa: 'x', place: 'velar', manner: 'fricative', voicing: 'voiceless' },
			{ symbol: 'ɣ', ipa: 'ɣ', place: 'velar', manner: 'fricative', voicing: 'voiced' },
			{ symbol: 'h', ipa: 'h', place: 'glottal', manner: 'fricative', voicing: 'voiceless' },
			{ symbol: 'ɦ', ipa: 'ɦ', place: 'glottal', manner: 'fricative', voicing: 'voiced' },
			
			// Approximants
			{ symbol: 'j', ipa: 'j', place: 'palatal', manner: 'approximant', voicing: 'voiced' },
			{ symbol: 'w', ipa: 'w', place: 'velar', manner: 'approximant', voicing: 'voiced' },
			{ symbol: 'ɹ', ipa: 'ɹ', place: 'alveolar', manner: 'approximant', voicing: 'voiced' },
			{ symbol: 'l', ipa: 'l', place: 'alveolar', manner: 'lateral_approximant', voicing: 'voiced' },
			{ symbol: 'ɭ', ipa: 'ɭ', place: 'retroflex', manner: 'lateral_approximant', voicing: 'voiced' },
			
			// Trills and taps
			{ symbol: 'r', ipa: 'r', place: 'alveolar', manner: 'trill', voicing: 'voiced' },
			{ symbol: 'ɾ', ipa: 'ɾ', place: 'alveolar', manner: 'tap', voicing: 'voiced' },
			{ symbol: 'ʀ', ipa: 'ʀ', place: 'uvular', manner: 'trill', voicing: 'voiced' },
		];
		
		consonantData.forEach((consonant, index) => {
			const element: LinguisticElement = {
				phoneticValue: consonant.symbol,
				ipaSymbol: consonant.ipa,
				type: 'consonant',
				placeOfArticulation: consonant.place as any,
				mannerOfArticulation: consonant.manner as any,
				voicing: consonant.voicing as any,
				complexity: 1,
				discoveryStep: 0,
				usageCount: 0,
				learningPhase: 'discovery'
			};
			this.linguisticElements.set(consonant.symbol, element);
			this.discoveredConsonants.push(consonant.symbol);
		});
	}
	
	// Initialize diphthongs
	initializeDiphthongs(): void {
		const diphthongData = [
			{ symbol: 'ai', ipa: 'aɪ', startVowel: 'a', endVowel: 'i', complexity: 3 },
			{ symbol: 'au', ipa: 'aʊ', startVowel: 'a', endVowel: 'u', complexity: 3 },
			{ symbol: 'ei', ipa: 'eɪ', startVowel: 'e', endVowel: 'i', complexity: 3 },
			{ symbol: 'oi', ipa: 'ɔɪ', startVowel: 'ɔ', endVowel: 'i', complexity: 3 },
			{ symbol: 'ou', ipa: 'oʊ', startVowel: 'o', endVowel: 'u', complexity: 3 },
			{ symbol: 'ia', ipa: 'iə', startVowel: 'i', endVowel: 'ə', complexity: 3 },
			{ symbol: 'ua', ipa: 'uə', startVowel: 'u', endVowel: 'ə', complexity: 3 },
			{ symbol: 'ɛə', ipa: 'ɛə', startVowel: 'ɛ', endVowel: 'ə', complexity: 3 },
		];
		
		diphthongData.forEach((diphthong, index) => {
			const element: LinguisticElement = {
				phoneticValue: diphthong.symbol,
				ipaSymbol: diphthong.ipa,
				type: 'diphthong',
				frequency: 200 + index * 30,
				complexity: diphthong.complexity,
				discoveryStep: 0,
				usageCount: 0,
				learningPhase: 'discovery'
			};
			this.linguisticElements.set(diphthong.symbol, element);
		});
	}
	
	// Initialize tone and stress patterns
	initializeToneAndStress(): void {
		// Tone levels (1-5 scale)
		for (let i = 1; i <= 5; i++) {
			const element: LinguisticElement = {
				phoneticValue: `tone${i}`,
				type: 'tone',
				tone: i,
				frequency: 100 + i * 50,
				complexity: 2,
				discoveryStep: 0,
				usageCount: 0,
				learningPhase: 'discovery'
			};
			this.linguisticElements.set(`tone${i}`, element);
		}
		
		// Stress patterns
		const stressTypes = ['primary', 'secondary', 'unstressed'];
		stressTypes.forEach((stress, index) => {
			const element: LinguisticElement = {
				phoneticValue: stress,
				type: 'stress',
				stress: stress as any,
				frequency: 150 + index * 100,
				complexity: 2,
				discoveryStep: 0,
				usageCount: 0,
				learningPhase: 'discovery'
			};
			this.linguisticElements.set(stress, element);
		});
	}
	
	// Initialize complex syllables
	initializeComplexSyllables(): void {
		const complexSyllables = [
			// CV syllables
			'ba', 'da', 'ga', 'ka', 'ma', 'na', 'pa', 'ta', 'fa', 'va', 'sa', 'za',
			'be', 'de', 'ge', 'ke', 'me', 'ne', 'pe', 'te', 'fe', 've', 'se', 'ze',
			'bi', 'di', 'gi', 'ki', 'mi', 'ni', 'pi', 'ti', 'fi', 'vi', 'si', 'zi',
			'bo', 'do', 'go', 'ko', 'mo', 'no', 'po', 'to', 'fo', 'vo', 'so', 'zo',
			'bu', 'du', 'gu', 'ku', 'mu', 'nu', 'pu', 'tu', 'fu', 'vu', 'su', 'zu',
			
			// CCV syllables
			'bra', 'dra', 'gra', 'kra', 'pra', 'tra', 'fla', 'sla', 'sna', 'spa', 'sta',
			'bre', 'dre', 'gre', 'kre', 'pre', 'tre', 'fle', 'sle', 'sne', 'spe', 'ste',
			'bri', 'dri', 'gri', 'kri', 'pri', 'tri', 'fli', 'sli', 'sni', 'spi', 'sti',
			'bro', 'dro', 'gro', 'kro', 'pro', 'tro', 'flo', 'slo', 'sno', 'spo', 'sto',
			'bru', 'dru', 'gru', 'kru', 'pru', 'tru', 'flu', 'slu', 'snu', 'spu', 'stu',
			
			// CVC syllables
			'bat', 'bad', 'bag', 'bak', 'bam', 'ban', 'bap', 'bar', 'bas', 'bav',
			'bet', 'bed', 'beg', 'bek', 'bem', 'ben', 'bep', 'ber', 'bes', 'bev',
			'bit', 'bid', 'big', 'bik', 'bim', 'bin', 'bip', 'bir', 'bis', 'biv',
			'bot', 'bod', 'bog', 'bok', 'bom', 'bon', 'bop', 'bor', 'bos', 'bov',
			'but', 'bud', 'bug', 'buk', 'bum', 'bun', 'bup', 'bur', 'bus', 'buv',
		];
		
		complexSyllables.forEach((syllable, index) => {
			const element: LinguisticElement = {
				phoneticValue: syllable,
				type: 'syllable',
				complexity: syllable.length > 2 ? 3 : 2,
				discoveryStep: 0,
				usageCount: 0,
				learningPhase: 'discovery'
			};
			this.linguisticElements.set(syllable, element);
			
			// Add to syllable structures
			const syllableStruct: SyllableStructure = {
				vowel: syllable.match(/[aeiou]/)?.[0] || 'a',
				consonants: syllable.split(/[aeiou]/).filter(c => c.length > 0),
				complexity: syllable.length > 2 ? 3 : 2,
				pronunciation: syllable,
				discoveryStep: 0
			};
			this.discoveredSyllables.push(syllableStruct);
		});
	}
	
	// Set up user interaction handler for audio (only once)
	setupAudioInteractionHandler(): void {
		// Only set up once
		if (this.audioInteractionHandlerSetup) {
			return;
		}
		
		let audioEnabled = false;
		
		const enableAudio = () => {
			if (audioEnabled) return;
			audioEnabled = true;
			
			// Check if AudioContext is available from UI
			if (this.audioContext && this.initializeAudioContext()) {
				console.log('[Audio] Audio enabled by user interaction');
			} else {
				// Throttle logging - only log once every 5 seconds
				const now = Date.now();
				if (now - this.audioWaitingLogTime > 5000) {
					console.log('[Audio] Waiting for UI to provide AudioContext');
					this.audioWaitingLogTime = now;
				}
			}
		};
		
		// Listen for user interactions
		document.addEventListener('click', enableAudio, { once: true });
		document.addEventListener('keydown', enableAudio, { once: true });
		document.addEventListener('touchstart', enableAudio, { once: true });
		document.addEventListener('mousedown', enableAudio, { once: true });
		
		this.audioInteractionHandlerSetup = true;
		console.log('[Audio] Waiting for user interaction to enable audio');
	}
	
	// One-time guard for continuous synthesis
	private continuousSynthesisStarted: boolean = false;

	// Return the current audio output node (prefer simulator master -> AudioEngine)
	getAudioOutput(): AudioNode {
		if (this.masterGain) {
			return this.masterGain;
		}
		try {
			const engine = getAudioEngine();
			return engine.getBusGain('ambience');
		} catch {}
		if (!this.audioContext) {
			throw new Error('AudioContext not initialized');
		}
		return this.audioContext.destination;
	}

	// Fade in the master gain over the specified duration in seconds
	fadeInMasterGain(durationSeconds: number = 1.5): void {
		if (!this.audioContext || !this.masterGain) return;
		const now = this.audioContext.currentTime;
		this.masterGain.gain.cancelScheduledValues(now);
		this.masterGain.gain.setValueAtTime(0, now);
		this.masterGain.gain.linearRampToValueAtTime(1, now + durationSeconds);
	}

	// Start continuous audio synthesis without sequential tests
	startContinuousAudioSynthesis(): void {
		// Only start once
		if (this.continuousSynthesisStarted) return;
		this.continuousSynthesisStarted = true;
		
		if (!this.initializeAudioContext()) {
			console.log('[Audio] Audio context not available for continuous synthesis');
			return;
		}
		
		try {
			// Ensure we have comprehensive linguistic elements
			if (this.linguisticElements.size === 0) {
				console.log('[Audio] No linguistic elements available, creating comprehensive ones');
				this.initializeBasicLinguisticElements();
			}
			
			console.log('[Audio] Starting continuous audio synthesis');
			// Start synthesizing elements immediately without delays
			this.synthesizeRandomElement();
			
			// Continue synthesizing elements at random intervals
			this.startContinuousSynthesisLoop();
			
		} catch (error) {
			console.warn('[Audio] Failed to start continuous audio synthesis:', error);
		}
	}
	
	// Start a loop for continuous synthesis
	startContinuousSynthesisLoop(): void {
		// Start with sequential learning of all elements
		this.startSequentialLearning();
		
		// Then continue with random synthesis for reinforcement
		const synthesizeNext = () => {
			if (this.audioContext && this.audioContext.state === 'running') {
				this.synthesizeRandomElement();
				
				// Adjust synthesis frequency based on energy throughput
				const energy = this.getCurrentEnergyThroughput();
				// Higher energy = more frequent synthesis, lower energy = less frequent
				// Energy range: 0.1 - 2.0, delay range: 2000ms - 200ms
				const baseDelay = 2000;
				const energyMultiplier = 2.0 - energy; // Higher energy = lower multiplier
				const nextDelay = Math.max(200, baseDelay * energyMultiplier);
				
				console.log(`[Synthesis] Next synthesis in ${nextDelay.toFixed(0)}ms (energy: ${energy.toFixed(3)})`);
				setTimeout(synthesizeNext, nextDelay);
			}
		};
		
		// Start the random loop after sequential learning completes
		setTimeout(synthesizeNext, 5000); // Give time for sequential learning
		
		// Also start periodic re-learning of all elements
		this.startPeriodicRelearning();
	}
	
	// Start sequential learning of all linguistic elements
	startSequentialLearning(): void {
		const elements = Array.from(this.linguisticElements.values());
		if (elements.length === 0) {
			console.log('[Sequential Learning] No elements to learn');
			return;
		}
		
		console.log(`[Sequential Learning] Starting to vocalize all ${elements.length} linguistic elements`);
		
		// Sort elements by type and complexity for logical learning order
		const sortedElements = elements.sort((a, b) => {
			// First by type priority: vowels, consonants, syllables, words, etc.
			const typePriority = { 'vowel': 1, 'consonant': 2, 'syllable': 3, 'word': 4, 'semantic': 5, 'diphthong': 6, 'tone': 7, 'stress': 8 };
			const aPriority = typePriority[a.type] || 9;
			const bPriority = typePriority[b.type] || 9;
			
			if (aPriority !== bPriority) {
				return aPriority - bPriority;
			}
			
			// Then by complexity (simpler first)
			return a.complexity - b.complexity;
		});
		
		// Vocalize each element with appropriate timing
		sortedElements.forEach((element, index) => {
			const delay = index * 800; // 800ms between each element
			
			setTimeout(() => {
				console.log(`[Sequential Learning] Learning element ${index + 1}/${sortedElements.length}: "${element.phoneticValue}" (${element.type})`);
				this.synthesizeLinguisticElement(element);
			}, delay);
		});
		
		console.log(`[Sequential Learning] Scheduled ${sortedElements.length} elements for vocalization`);
	}

	// Start periodic re-learning of all elements to reinforce learning
	startPeriodicRelearning(): void {
		const relearnAll = () => {
			if (this.audioContext && this.audioContext.state === 'running') {
				console.log('[Periodic Relearning] Re-vocalizing all elements for reinforcement');
				this.startSequentialLearning();
				
				// Schedule next re-learning session (every 30 seconds)
				setTimeout(relearnAll, 30000);
			}
		};
		
		// Start first re-learning session after initial learning completes
		setTimeout(relearnAll, 10000); // 10 seconds after initial learning
	}

	// Synthesize a random linguistic element
	synthesizeRandomElement(): void {
		const elements = Array.from(this.linguisticElements.values());
		if (elements.length > 0) {
			const randomElement = elements[Math.floor(Math.random() * elements.length)];
			this.synthesizeLinguisticElement(randomElement);
		}
	}
	
	// Calculate current energy throughput from neural network activity
	getCurrentEnergyThroughput(): number {
		if (this.nodes.length === 0) return 0.1; // Minimum energy
		
		// Calculate total activation energy
		let totalEnergy = 0;
		let activeNodes = 0;
		
		for (const node of this.nodes) {
			if (node.energy > 0.1) { // Only count significantly active nodes
				totalEnergy += node.energy;
				activeNodes++;
			}
		}
		
		// Calculate average energy per active node
		const avgEnergy = activeNodes > 0 ? totalEnergy / activeNodes : 0;
		
		// Calculate energy density (energy per node)
		const energyDensity = totalEnergy / this.nodes.length;
		
		// Combine both metrics for comprehensive energy measurement
		const energyThroughput = (avgEnergy * 0.7) + (energyDensity * 0.3);
		
		// Normalize to 0.1 - 2.0 range
		const normalizedEnergy = Math.max(0.1, Math.min(2.0, energyThroughput));
		
		console.log(`[Energy] Throughput: ${normalizedEnergy.toFixed(3)} (${activeNodes}/${this.nodes.length} active nodes)`);
		return normalizedEnergy;
	}
	
	// Get voice duration based on energy throughput
	getEnergyBasedDuration(baseDuration: number): number {
		const energy = this.getCurrentEnergyThroughput();
		
		// Higher energy = slightly longer duration, but keep everything short
		// Energy range: 0.1 - 2.0, duration multiplier: 0.5 - 1.5 (much smaller range)
		const durationMultiplier = 0.5 + (energy * 0.5); // Maps 0.1-2.0 to 0.5-1.5
		
		const adjustedDuration = baseDuration * durationMultiplier;
		
		console.log(`[Voice] Energy: ${energy.toFixed(3)}, Duration: ${adjustedDuration.toFixed(3)}s (${durationMultiplier.toFixed(2)}x)`);
		return adjustedDuration;
	}
	
	// Legacy method for manual testing
	startImmediateAudioSynthesis(): void {
		this.startContinuousAudioSynthesis();
	}
	
	// --- Language Learning System ---
	
	// Initialize language learning system (only once)
	private languageLearningInitialized: boolean = false;
	
	initializeLanguageLearning(): void {
		// Only initialize once
		if (this.languageLearningInitialized) return;
		
		console.log('[Language] Initializing language learning system...');
		
		// Create initial basic patterns
		this.createInitialLanguagePatterns();
		
		// Start pattern recognition learning
		this.startPatternRecognitionLearning();
		
		this.languageLearningInitialized = true;
		console.log('[Language] Language learning system initialized');
	}
	
	// Create initial language patterns (only once)
	private initialPatternsCreated: boolean = false;
	
	createInitialLanguagePatterns(): void {
		// Only create once
		if (this.initialPatternsCreated) return;
		
		const basicPatterns = [
			{ pattern: 'a', meaning: 'existence', complexity: 1 },
			{ pattern: 'e', meaning: 'energy', complexity: 1 },
			{ pattern: 'i', meaning: 'self', complexity: 1 },
			{ pattern: 'o', meaning: 'other', complexity: 1 },
			{ pattern: 'u', meaning: 'unity', complexity: 1 },
			{ pattern: 'ma', meaning: 'mother', complexity: 2 },
			{ pattern: 'pa', meaning: 'father', complexity: 2 },
			{ pattern: 'ba', meaning: 'being', complexity: 2 },
			{ pattern: 'da', meaning: 'data', complexity: 2 },
			{ pattern: 'ga', meaning: 'growth', complexity: 2 }
		];
		
		basicPatterns.forEach(({ pattern, meaning, complexity }) => {
			const languagePattern: LanguagePattern = {
				id: `pattern_${pattern}`,
				pattern,
				frequency: 0,
				strength: 0.5,
				lastUsed: 0,
				relatedPatterns: [],
				phoneticElements: pattern.split(''),
				meaning,
				complexity
			};
			
			this.languagePatterns.set(pattern, languagePattern);
			this.learnedPhonemes.add(pattern);
			this.phonemeFrequency.set(pattern, 0);
		});
		
		this.initialPatternsCreated = true;
		console.log(`[Language] Created ${basicPatterns.length} initial language patterns`);
	}
	
	// Start pattern recognition learning (only once)
	private patternRecognitionStarted: boolean = false;
	
	startPatternRecognitionLearning(): void {
		// Only start once
		if (this.patternRecognitionStarted) return;
		
		// Learn from existing linguistic elements
		this.learnFromLinguisticElements();
		
		// Start continuous learning
		this.startContinuousLanguageLearning();
		
		this.patternRecognitionStarted = true;
	}
	
	// Learn from existing linguistic elements (only once)
	private linguisticElementsLearned: boolean = false;
	
	learnFromLinguisticElements(): void {
		// Only learn once
		if (this.linguisticElementsLearned) return;
		
		console.log('[Language] Learning from existing linguistic elements...');
		
		for (const [key, element] of this.linguisticElements) {
			this.learnFromPhoneme(element.phoneticValue, element.type);
		}
		
		// Build familial connections
		this.buildFamilialConnections();
		
		this.linguisticElementsLearned = true;
	}
	
	// Learn from a phoneme
	learnFromPhoneme(phoneme: string, type: string): void {
		const now = Date.now();
		
		// Update frequency
		const currentFreq = this.phonemeFrequency.get(phoneme) || 0;
		this.phonemeFrequency.set(phoneme, currentFreq + 1);
		
		// Create or update language memory
		const memory: LanguageMemory = {
			phoneme,
			context: type,
			emotionalState: this.voiceCharacteristics.emotionalState,
			success: true,
			timestamp: now,
			relatedPhonemes: this.findRelatedPhonemes(phoneme),
			patternStrength: this.calculatePatternStrength(phoneme)
		};
		
		this.languageMemory.set(`${phoneme}_${now}`, memory);
		
		// Update pattern if exists
		if (this.languagePatterns.has(phoneme)) {
			const pattern = this.languagePatterns.get(phoneme)!;
			pattern.frequency += 1;
			pattern.strength = Math.min(1.0, pattern.strength + 0.1);
			pattern.lastUsed = now;
		} else {
			// Create new pattern
			const newPattern: LanguagePattern = {
				id: `pattern_${phoneme}`,
				pattern: phoneme,
				frequency: 1,
				strength: 0.3,
				lastUsed: now,
				relatedPatterns: [],
				phoneticElements: phoneme.split(''),
				meaning: this.generateMeaning(phoneme),
				complexity: this.calculateComplexity(phoneme)
			};
			
			this.languagePatterns.set(phoneme, newPattern);
		}
		
		this.learnedPhonemes.add(phoneme);
	}
	
	// Find related phonemes based on similarity
	findRelatedPhonemes(phoneme: string): string[] {
		const related: string[] = [];
		
		for (const learned of this.learnedPhonemes) {
			if (learned !== phoneme) {
				const similarity = this.calculatePhonemeSimilarity(phoneme, learned);
				if (similarity > 0.3) {
					related.push(learned);
				}
			}
		}
		
		return related;
	}
	
	// Calculate similarity between two phonemes
	calculatePhonemeSimilarity(phoneme1: string, phoneme2: string): number {
		// Simple character-based similarity
		let matches = 0;
		const maxLength = Math.max(phoneme1.length, phoneme2.length);
		
		for (let i = 0; i < maxLength; i++) {
			if (phoneme1[i] === phoneme2[i]) {
				matches++;
			}
		}
		
		return matches / maxLength;
	}
	
	// Calculate pattern strength based on frequency and recency
	calculatePatternStrength(phoneme: string): number {
		const frequency = this.phonemeFrequency.get(phoneme) || 0;
		const now = Date.now();
		const pattern = this.languagePatterns.get(phoneme);
		const recency = pattern ? (now - pattern.lastUsed) / 1000 : 1000; // seconds
		
		// Strength based on frequency and recency
		const frequencyStrength = Math.min(1.0, frequency / 10);
		const recencyStrength = Math.max(0.1, 1.0 - (recency / 3600)); // Decay over 1 hour
		
		return (frequencyStrength + recencyStrength) / 2;
	}
	
	// Generate meaning for a phoneme
	generateMeaning(phoneme: string): string {
		const meanings = [
			'energy', 'flow', 'connection', 'growth', 'change',
			'stability', 'movement', 'pattern', 'rhythm', 'harmony',
			'discovery', 'creation', 'transformation', 'evolution', 'synthesis'
		];
		
		// Use phoneme characteristics to select meaning
		const index = phoneme.length + phoneme.charCodeAt(0) % meanings.length;
		return meanings[index % meanings.length];
	}
	
	// Calculate complexity of a phoneme
	calculateComplexity(phoneme: string): number {
		let complexity = 1;
		
		// Length affects complexity
		complexity += phoneme.length * 0.5;
		
		// Special characters increase complexity
		if (/[^a-zA-Z]/.test(phoneme)) {
			complexity += 0.5;
		}
		
		// Vowel-consonant combinations
		if (/[aeiou]/.test(phoneme) && /[bcdfghjklmnpqrstvwxyz]/.test(phoneme)) {
			complexity += 0.3;
		}
		
		return Math.min(5, complexity);
	}
	
	// Build familial connections between phonemes (only once)
	private familialConnectionsBuilt: boolean = false;
	
	buildFamilialConnections(): void {
		// Only build once
		if (this.familialConnectionsBuilt) return;
		
		console.log('[Language] Building familial connections...');
		
		for (const phoneme of this.learnedPhonemes) {
			const related = this.findRelatedPhonemes(phoneme);
			
			if (related.length > 0) {
				this.familialConnections.set(phoneme, related);
				
				// Update pattern relationships
				const pattern = this.languagePatterns.get(phoneme);
				if (pattern) {
					pattern.relatedPatterns = related;
				}
			}
		}
		
		this.familialConnectionsBuilt = true;
		console.log(`[Language] Built familial connections for ${this.familialConnections.size} phonemes`);
	}
	
	// Start continuous language learning
	startContinuousLanguageLearning(): void {
		const learnNext = () => {
			if (this.languagePatterns.size > 0) {
				this.evolveLanguagePatterns();
				this.updatePatternRecognitionWeights();
				this.languageEvolutionStep++;
			}
			
			// Continue learning every 5 seconds
			setTimeout(learnNext, 5000);
		};
		
		// Start learning after 2 seconds
		setTimeout(learnNext, 2000);
	}
	
	// Evolve language patterns
	evolveLanguagePatterns(): void {
		const patternsToEvolve = Array.from(this.languagePatterns.values())
			.filter(pattern => pattern.frequency > 2)
			.slice(0, 3); // Evolve top 3 patterns
		
		for (const pattern of patternsToEvolve) {
			this.evolvePattern(pattern);
		}
	}
	
	// Evolve a specific pattern
	evolvePattern(pattern: LanguagePattern): void {
		// Create variations of the pattern
		const variations = this.generatePatternVariations(pattern.pattern);
		
		for (const variation of variations) {
			if (!this.languagePatterns.has(variation)) {
				const newPattern: LanguagePattern = {
					id: `pattern_${variation}`,
					pattern: variation,
					frequency: 0,
					strength: pattern.strength * 0.7, // Inherit some strength
					lastUsed: Date.now(),
					relatedPatterns: [pattern.pattern],
					phoneticElements: variation.split(''),
					meaning: this.generateMeaning(variation),
					complexity: this.calculateComplexity(variation)
				};
				
				this.languagePatterns.set(variation, newPattern);
				this.learnedPhonemes.add(variation);
				this.phonemeFrequency.set(variation, 0);
				
				console.log(`[Language] Evolved pattern: ${pattern.pattern} -> ${variation}`);
			}
		}
	}
	
	// Generate variations of a pattern
	generatePatternVariations(pattern: string): string[] {
		const variations: string[] = [];
		
		// Add prefix variations
		const prefixes = ['a', 'e', 'i', 'o', 'u'];
		for (const prefix of prefixes) {
			variations.push(prefix + pattern);
		}
		
		// Add suffix variations
		const suffixes = ['a', 'e', 'i', 'o', 'u', 'n', 'm', 'r'];
		for (const suffix of suffixes) {
			variations.push(pattern + suffix);
		}
		
		// Add vowel substitutions
		const vowels = ['a', 'e', 'i', 'o', 'u'];
		for (let i = 0; i < pattern.length; i++) {
			if (/[aeiou]/.test(pattern[i])) {
				for (const vowel of vowels) {
					if (vowel !== pattern[i]) {
						const variation = pattern.substring(0, i) + vowel + pattern.substring(i + 1);
						variations.push(variation);
					}
				}
			}
		}
		
		return variations.slice(0, 5); // Limit to 5 variations
	}
	
	// Update pattern recognition weights
	updatePatternRecognitionWeights(): void {
		for (const [phoneme, pattern] of this.languagePatterns) {
			const weight = this.calculatePatternStrength(phoneme);
			this.patternRecognitionWeights.set(phoneme, weight);
		}
	}
	
	// Get learned language patterns
	getLearnedPatterns(): LanguagePattern[] {
		return Array.from(this.languagePatterns.values())
			.sort((a, b) => b.strength - a.strength);
	}
	
	// Get familial connections for a phoneme
	getFamilialConnections(phoneme: string): string[] {
		return this.familialConnections.get(phoneme) || [];
	}
	
	// Get language statistics
	getLanguageStatistics(): any {
		return {
			totalPatterns: this.languagePatterns.size,
			learnedPhonemes: this.learnedPhonemes.size,
			evolutionStep: this.languageEvolutionStep,
			topPatterns: this.getLearnedPatterns().slice(0, 5),
			familialConnections: this.familialConnections.size
		};
	}
	
	// Synthesize a vowel sound using Web Audio API
	synthesizeVowel(element: LinguisticElement): void {
		console.log(`[Creature Voice] synthesizeVowel called for "${element.phoneticValue}"`);
		
		// Use Web Audio API synthesis instead of SpeechSynthesisUtterance
		this.synthesizeLinguisticElement(element);
	}
	
	// Get vowel duration based on personality and emotional state
	getVowelDuration(element: LinguisticElement): number {
		// Much shorter base durations for natural speech hits
		let baseDuration = 0.08; // 80ms base - very short
		
		// Adjust based on personality
		switch (this.voiceCharacteristics.personality) {
			case 'energetic':
				baseDuration *= 0.6; // Very fast speech
				break;
			case 'calm':
				baseDuration *= 1.4; // Slightly slower but still short
				break;
			case 'playful':
				baseDuration *= 0.7; // Quick and playful
				break;
			case 'wise':
				baseDuration *= 1.2; // More deliberate but still brief
				break;
			case 'mysterious':
				baseDuration *= 1.1; // Slightly longer for mystery
				break;
		}
		
		// Adjust based on emotional state
		switch (this.voiceCharacteristics.emotionalState) {
			case 'excited':
				baseDuration *= 0.5; // Very fast when excited
				break;
			case 'sad':
				baseDuration *= 1.3; // Slightly slower when sad
				break;
			case 'contemplative':
				baseDuration *= 1.1; // Thoughtful but brief
				break;
			case 'curious':
				baseDuration *= 0.8; // Quick and curious
				break;
		}
		
		// Adjust based on vowel type - much shorter variations
		switch (element.phoneticValue.toLowerCase()) {
			case 'i': case 'ɪ': case 'u': case 'ʊ': // Short vowels
				baseDuration *= 0.7;
				break;
			case 'a': case 'ɑ': case 'o': case 'ɔ': // Long vowels
				baseDuration *= 1.3;
				break;
			case 'ə': case 'ɜ': // Schwa (very short)
				baseDuration *= 0.5;
				break;
			case 'e': case 'ɛ': case 'æ': // Mid vowels
				baseDuration *= 0.9;
				break;
		}
		
		// Keep durations very short for natural speech
		return Math.max(0.03, Math.min(0.15, baseDuration));
	}

	// Create short speech phrases with natural rhythm
	createSpeechPhrase(elements: LinguisticElement[], startTime: number): void {
		console.log(`[Audio] Creating speech phrase with ${elements.length} elements`);
		
		let currentTime = startTime;
		const phraseGap = 0.02; // 20ms between elements
		
		for (let i = 0; i < elements.length; i++) {
			const element = elements[i];
			const duration = element.type === 'vowel' ? 
				this.getVowelDuration(element) : 
				this.getConsonantDuration(element);
			
			// Add slight randomization for natural timing
			const timingVariation = (Math.random() - 0.5) * 0.01;
			const actualStartTime = currentTime + timingVariation;
			
			// Synthesize the element using Web Audio API
			setTimeout(() => {
				this.synthesizeLinguisticElement(element);
			}, (actualStartTime - this.audioContext!.currentTime) * 1000);
			
			// Move to next element with natural gap
			currentTime += duration + phraseGap;
		}
	}

	// Generate quick voice hits for immediate feedback
	createQuickVoiceHit(element: LinguisticElement): void {
		console.log(`[Audio] Creating quick voice hit for "${element.phoneticValue}"`);
		
		// Very short duration for quick hits
		const quickDuration = element.type === 'vowel' ? 0.04 : 0.02;
		
		if (!this.initializeAudioContext()) {
			console.log('[Audio] Failed to initialize audio context for quick hit');
			return;
		}
		
		try {
			const now = this.audioContext!.currentTime;
			
			if (element.type === 'vowel') {
				// Quick vowel synthesis (lowered base pitch)
				const glottalPulse = this.createGlottalPulse(element.frequency || 80, quickDuration, now); // Lowered from 120 to 80
				const baseFormants = this.getVowelFormants(element);
				const vocalTractFilters = this.createVocalTractFilter(baseFormants, now);
				
				// Quick envelope (increased volume)
				const envelope = this.audioContext!.createGain();
				envelope.gain.setValueAtTime(0, now);
				envelope.gain.linearRampToValueAtTime(0.6, now + 0.005); // Increased from 0.3 to 0.6
				envelope.gain.linearRampToValueAtTime(0, now + quickDuration);
				
				// Connect and start
				glottalPulse.connect(vocalTractFilters[0]);
				for (let i = 0; i < vocalTractFilters.length - 1; i++) {
					vocalTractFilters[i].connect(vocalTractFilters[i + 1]);
				}
				vocalTractFilters[vocalTractFilters.length - 1].connect(envelope);
				if (this.reverbNode) {
					envelope.connect(this.reverbNode);
					this.reverbNode.connect(this.getAudioOutput());
				} else {
					envelope.connect(this.getAudioOutput());
				}
				
				glottalPulse.start(now);
				glottalPulse.stop(now + quickDuration);
			} else {
				// Quick consonant synthesis
				if (element.mannerOfArticulation === 'plosive') {
					this.synthesizePlosive(element, now, quickDuration);
				} else if (element.mannerOfArticulation === 'fricative') {
					this.synthesizeFricative(element, now, quickDuration);
				} else {
					this.synthesizeConsonant(element);
				}
			}
		} catch (error) {
			console.warn('[Audio] Failed to create quick voice hit:', error);
		}
	}

	// Generate natural speech patterns with short phrases
	generateSpeechPattern(): void {
		console.log('[Audio] Generating natural speech pattern');
		
		// Get available linguistic elements
		const availableElements = Array.from(this.linguisticElements.values());
		if (availableElements.length === 0) return;
		
		// Create short phrases (2-4 elements each)
		const phraseCount = 3 + Math.floor(Math.random() * 3); // 3-5 phrases
		const phraseGap = 0.1 + Math.random() * 0.2; // 100-300ms between phrases
		
		let currentTime = this.audioContext!.currentTime;
		
		for (let p = 0; p < phraseCount; p++) {
			// Create a short phrase
			const phraseLength = 2 + Math.floor(Math.random() * 3); // 2-4 elements
			const phrase: LinguisticElement[] = [];
			
			for (let e = 0; e < phraseLength; e++) {
				// Randomly select an element
				const randomElement = availableElements[Math.floor(Math.random() * availableElements.length)];
				phrase.push({ ...randomElement });
			}
			
			// Schedule the phrase
			setTimeout(() => {
				this.createSpeechPhrase(phrase, this.audioContext!.currentTime);
			}, (currentTime - this.audioContext!.currentTime) * 1000);
			
			// Calculate next phrase time
			const phraseDuration = phrase.reduce((total, element) => {
				const duration = element.type === 'vowel' ? 
					this.getVowelDuration(element) : 
					this.getConsonantDuration(element);
				return total + duration + 0.02; // element duration + gap
			}, 0);
			
			currentTime += phraseDuration + phraseGap;
		}
	}

	// Create rhythmic voice patterns
	createRhythmicPattern(): void {
		console.log('[Audio] Creating rhythmic voice pattern');
		
		const availableElements = Array.from(this.linguisticElements.values());
		if (availableElements.length === 0) return;
		
		// Create a rhythmic pattern with short bursts
		const patternLength = 8 + Math.floor(Math.random() * 8); // 8-15 elements
		const baseInterval = 0.08; // 80ms base interval
		
		for (let i = 0; i < patternLength; i++) {
			const element = availableElements[Math.floor(Math.random() * availableElements.length)];
			const interval = baseInterval + (Math.random() - 0.5) * 0.03; // ±15ms variation
			const startTime = this.audioContext!.currentTime + (i * interval);
			
			setTimeout(() => {
				this.createQuickVoiceHit({ ...element });
			}, i * interval * 1000);
		}
	}
	
	// Get personality and energy-adjusted pitch
	getPersonalityAdjustedPitch(baseFreq: number): number {
		let adjustedFreq = baseFreq;
		
		// Adjust based on age (lowered pitch range)
		switch (this.voiceCharacteristics.age) {
			case 'child':
				adjustedFreq *= 1.2; // Lowered from 1.5 - less high pitch
				break;
			case 'young':
				adjustedFreq *= 1.05; // Lowered from 1.2 - slightly higher
				break;
			case 'elderly':
				adjustedFreq *= 0.7; // Lowered from 0.8 - deeper voice
				break;
		}
		
		// Adjust based on gender (lowered pitch range)
		switch (this.voiceCharacteristics.gender) {
			case 'feminine':
				adjustedFreq *= 1.1; // Lowered from 1.3 - less high pitch
				break;
			case 'masculine':
				adjustedFreq *= 0.6; // Lowered from 0.7 - deeper voice
				break;
		}
		
		// Adjust based on emotional state
		switch (this.voiceCharacteristics.emotionalState) {
			case 'excited':
				adjustedFreq *= 1.2; // Higher when excited
				break;
			case 'sad':
				adjustedFreq *= 0.9; // Lower when sad
				break;
		}
		
		// Energy-based pitch adjustment (lowered overall pitch)
		const energy = this.getCurrentEnergyThroughput();
		// Higher energy = higher pitch, lower energy = lower pitch
		// Energy range: 0.1 - 2.0, pitch multiplier: 0.6 - 1.1 (lowered from 0.8-1.3)
		const energyPitchMultiplier = 0.6 + (energy * 0.25);
		adjustedFreq *= energyPitchMultiplier;
		
		return adjustedFreq;
	}
	
	// Apply intonation patterns
	applyIntonationPattern(oscillator: OscillatorNode, now: number, duration: number, element: LinguisticElement): void {
		const baseFreq = oscillator.frequency.value;
		const intonationRange = this.voiceCharacteristics.intonationRange;
		
		// Add natural pitch variation
		const pitchVariation = (Math.random() - 0.5) * this.voiceCharacteristics.pitchVariation;
		oscillator.frequency.linearRampToValueAtTime(
			baseFreq + pitchVariation, 
			now + duration * 0.5
		);
		
		// Add intonation based on personality
		if (this.voiceCharacteristics.personality === 'playful') {
			// Add playful pitch dips
			oscillator.frequency.linearRampToValueAtTime(
				baseFreq - intonationRange * 0.3,
				now + duration * 0.3
			);
			oscillator.frequency.linearRampToValueAtTime(
				baseFreq + intonationRange * 0.2,
				now + duration * 0.7
			);
		} else if (this.voiceCharacteristics.personality === 'mysterious') {
			// Add mysterious pitch drops
			oscillator.frequency.linearRampToValueAtTime(
				baseFreq - intonationRange * 0.4,
				now + duration * 0.6
			);
		}
	}
	
	// Apply personality to formants
	applyPersonalityToFormants(formants: { f1: number, f2: number, f3: number, f4?: number, f5?: number }): { f1: number, f2: number, f3: number, f4?: number, f5?: number } {
		let f1 = formants.f1, f2 = formants.f2, f3 = formants.f3, f4 = formants.f4, f5 = formants.f5;
		
		// Adjust based on age
		switch (this.voiceCharacteristics.age) {
			case 'child':
				f1 *= 1.2; // Higher formants for children
				f2 *= 1.1;
				break;
			case 'elderly':
				f1 *= 0.9; // Lower formants for elderly
				f2 *= 0.95;
				break;
		}
		
		// Adjust based on gender
		switch (this.voiceCharacteristics.gender) {
			case 'feminine':
				f1 *= 1.1; // Slightly higher formants
				f2 *= 1.05;
				break;
			case 'masculine':
				f1 *= 0.9; // Slightly lower formants
				f2 *= 0.95;
				break;
		}
		
		return { f1, f2, f3, f4, f5 };
	}
	
	// Get fractal dimension for voice enhancement
	getFractalDimension(): number {
		const branchLengths = this.branches.map(b => b.length);
		const avgLength = branchLengths.length > 0 ? branchLengths.reduce((a, b) => a + b, 0) / branchLengths.length : 1;
		const baseDimension = Math.log(this.nodes.length) / Math.log(avgLength || 1.1);
		
		// Adjust based on network complexity and connectivity
		const connectivity = this.edges.length / Math.max(1, this.nodes.length);
		const complexity = this.branches.length / Math.max(1, this.nodes.length);
		
		return baseDimension * (1 + connectivity * 0.3) * (1 + complexity * 0.2);
	}

	// Create realistic glottal pulse (vocal cord vibration)
	createGlottalPulse(fundamentalFreq: number, duration: number, startTime: number): AudioBufferSourceNode {
		const sampleRate = this.audioContext!.sampleRate;
		const bufferLength = Math.floor(sampleRate * duration);
		const buffer = this.audioContext!.createBuffer(1, bufferLength, sampleRate);
		const data = buffer.getChannelData(0);
		
		// Glottal pulse parameters
		const period = sampleRate / fundamentalFreq;
		const openPhase = 0.4; // 40% of period is open
		const closedPhase = 0.6; // 60% of period is closed
		
		for (let i = 0; i < bufferLength; i++) {
			const time = i / sampleRate;
			const phase = (i % period) / period;
			
			let glottalPulse = 0;
			
			if (phase < openPhase) {
				// Open phase - smooth opening
				const openProgress = phase / openPhase;
				glottalPulse = Math.sin(openProgress * Math.PI);
			} else {
				// Closed phase - rapid closure
				const closeProgress = (phase - openPhase) / closedPhase;
				glottalPulse = Math.exp(-closeProgress * 8) * Math.sin(closeProgress * Math.PI);
			}
			
			// Add natural jitter and shimmer
			const jitter = (Math.random() - 0.5) * 0.02;
			const shimmer = Math.sin(time * 5) * 0.01;
			
			// Apply breathiness
			const breathiness = this.voiceCharacteristics.breathiness * 0.3;
			const breathNoise = (Math.random() - 0.5) * breathiness;
			
			data[i] = glottalPulse * (1 + jitter + shimmer) + breathNoise;
		}
		
		const source = this.audioContext!.createBufferSource();
		source.buffer = buffer;
		return source;
	}

	// Create realistic vocal tract filter
	createVocalTractFilter(formants: { f1: number, f2: number, f3: number, f4?: number, f5?: number }, now: number): BiquadFilterNode[] {
		const filters: BiquadFilterNode[] = [];
		
		// F1 - First formant (vowel height)
		const f1Filter = this.audioContext!.createBiquadFilter();
		f1Filter.type = 'peaking';
		f1Filter.frequency.setValueAtTime(formants.f1, now);
		f1Filter.Q.setValueAtTime(8, now);
		f1Filter.gain.setValueAtTime(12, now);
		filters.push(f1Filter);
		
		// F2 - Second formant (vowel backness)
		const f2Filter = this.audioContext!.createBiquadFilter();
		f2Filter.type = 'peaking';
		f2Filter.frequency.setValueAtTime(formants.f2, now);
		f2Filter.Q.setValueAtTime(6, now);
		f2Filter.gain.setValueAtTime(10, now);
		filters.push(f2Filter);
		
		// F3 - Third formant (vowel rounding)
		const f3Filter = this.audioContext!.createBiquadFilter();
		f3Filter.type = 'peaking';
		f3Filter.frequency.setValueAtTime(formants.f3, now);
		f3Filter.Q.setValueAtTime(4, now);
		f3Filter.gain.setValueAtTime(6, now);
		filters.push(f3Filter);
		
		// F4 - Fourth formant (pharyngeal cavity)
		if (formants.f4) {
			const f4Filter = this.audioContext!.createBiquadFilter();
			f4Filter.type = 'peaking';
			f4Filter.frequency.setValueAtTime(formants.f4, now);
			f4Filter.Q.setValueAtTime(3, now);
			f4Filter.gain.setValueAtTime(4, now);
			filters.push(f4Filter);
		}
		
		// F5 - Fifth formant (oral cavity)
		if (formants.f5) {
			const f5Filter = this.audioContext!.createBiquadFilter();
			f5Filter.type = 'peaking';
			f5Filter.frequency.setValueAtTime(formants.f5, now);
			f5Filter.Q.setValueAtTime(2, now);
			f5Filter.gain.setValueAtTime(2, now);
			filters.push(f5Filter);
		}
		
		// Lip radiation filter (high-frequency emphasis)
		const lipFilter = this.audioContext!.createBiquadFilter();
		lipFilter.type = 'highpass';
		lipFilter.frequency.setValueAtTime(800, now);
		lipFilter.Q.setValueAtTime(0.7, now);
		filters.push(lipFilter);
		
		return filters;
	}

	// Create realistic breathing and airflow simulation
	createBreathingNoise(duration: number, now: number): AudioBufferSourceNode {
		const sampleRate = this.audioContext!.sampleRate;
		const bufferLength = Math.floor(sampleRate * duration);
		const buffer = this.audioContext!.createBuffer(1, bufferLength, sampleRate);
		const data = buffer.getChannelData(0);
		
		for (let i = 0; i < bufferLength; i++) {
			const time = i / sampleRate;
			
			// Generate pink noise (more natural than white noise)
			let pinkNoise = 0;
			for (let j = 0; j < 5; j++) {
				pinkNoise += (Math.random() - 0.5) * Math.pow(2, -j);
			}
			pinkNoise *= 0.1;
			
			// Add breathing rhythm
			const breathingRhythm = Math.sin(time * 0.3) * 0.5 + 0.5;
			
			// Add turbulence
			const turbulence = Math.sin(time * 15) * Math.sin(time * 23) * 0.05;
			
			data[i] = pinkNoise * breathingRhythm + turbulence;
		}
		
		const source = this.audioContext!.createBufferSource();
		source.buffer = buffer;
		return source;
	}

	// Create realistic vocal tract resonance
	createVocalTractResonance(now: number): BiquadFilterNode {
		const resonance = this.audioContext!.createBiquadFilter();
		resonance.type = 'lowpass';
		resonance.frequency.setValueAtTime(4000, now);
		resonance.Q.setValueAtTime(1, now);
		return resonance;
	}

	// Create realistic fricative noise based on place of articulation
	createFricativeNoise(element: LinguisticElement, duration: number, now: number): AudioBufferSourceNode {
		const sampleRate = this.audioContext!.sampleRate;
		const bufferLength = Math.floor(sampleRate * duration);
		const buffer = this.audioContext!.createBuffer(1, bufferLength, sampleRate);
		const data = buffer.getChannelData(0);
		
		// Get place-specific noise characteristics
		const placeFreq = this.getPlaceOfArticulationFrequency(element.placeOfArticulation);
		const bandwidth = this.getPlaceBandwidth(element.placeOfArticulation);
		
		for (let i = 0; i < bufferLength; i++) {
			const time = i / sampleRate;
			
			// Generate filtered noise based on place of articulation
			let noise = 0;
			for (let j = 0; j < 10; j++) {
				noise += (Math.random() - 0.5) * Math.pow(2, -j);
			}
			
			// Apply place-specific filtering
			const centerFreq = placeFreq;
			const filterResponse = Math.exp(-Math.pow((time * 1000 - centerFreq) / bandwidth, 2));
			
			// Add turbulence based on manner of articulation
			const turbulence = this.getTurbulenceLevel(element.mannerOfArticulation);
			
			// Add natural variation
			const variation = Math.sin(time * 20) * 0.1 + Math.sin(time * 35) * 0.05;
			
			data[i] = noise * filterResponse * turbulence * (1 + variation);
		}
		
		const source = this.audioContext!.createBufferSource();
		source.buffer = buffer;
		return source;
	}

	// Get place-specific frequency for articulation
	getPlaceOfArticulationFrequency(place?: string): number {
		switch (place) {
			case 'bilabial': return 600;
			case 'labiodental': return 800;
			case 'dental': return 1000;
			case 'alveolar': return 1200;
			case 'postalveolar': return 1600;
			case 'retroflex': return 1400;
			case 'palatal': return 2000;
			case 'velar': return 1800;
			case 'uvular': return 1600;
			case 'pharyngeal': return 1000;
			case 'glottal': return 800;
			default: return 1200;
		}
	}

	// Get bandwidth for place of articulation
	getPlaceBandwidth(place?: string): number {
		switch (place) {
			case 'bilabial': return 400;
			case 'labiodental': return 500;
			case 'dental': return 600;
			case 'alveolar': return 700;
			case 'postalveolar': return 800;
			case 'retroflex': return 750;
			case 'palatal': return 900;
			case 'velar': return 850;
			case 'uvular': return 800;
			case 'pharyngeal': return 600;
			case 'glottal': return 500;
			default: return 700;
		}
	}

	// Get turbulence level for manner of articulation
	getTurbulenceLevel(manner?: string): number {
		switch (manner) {
			case 'fricative': return 0.8;
			case 'lateral_fricative': return 0.7;
			case 'approximant': return 0.3;
			case 'lateral_approximant': return 0.4;
			case 'nasal': return 0.2;
			case 'plosive': return 0.9;
			case 'trill': return 0.6;
			case 'tap': return 0.5;
			default: return 0.5;
		}
	}

	// Get personality and energy-adjusted volume
	getPersonalityAdjustedVolume(): number {
		let volume = this.synthesisConfig.volume * this.voiceCharacteristics.sustainLevel;
		
		// Adjust based on personality
		switch (this.voiceCharacteristics.personality) {
			case 'energetic':
				volume *= 1.2; // Louder
				break;
			case 'calm':
				volume *= 0.8; // Quieter
				break;
			case 'mysterious':
				volume *= 0.9; // Slightly quieter
				break;
		}
		
		// Adjust based on emotional state
		switch (this.voiceCharacteristics.emotionalState) {
			case 'excited':
				volume *= 1.3; // Much louder
				break;
			case 'sad':
				volume *= 0.7; // Much quieter
				break;
		}
		
		// Energy-based volume adjustment
		const energy = this.getCurrentEnergyThroughput();
		// Higher energy = louder volume, lower energy = quieter volume
		// Energy range: 0.1 - 2.0, volume multiplier: 0.5 - 1.5
		const energyVolumeMultiplier = 0.5 + (energy * 0.5);
		volume *= energyVolumeMultiplier;
		
		// Apply global volume boost (increased from previous)
		volume *= 1.0; // Balanced volume - was 2.0
		
		return Math.max(0.5, Math.min(2.0, volume)); // Increased volume range
	}
	
	// Get personality-based noise level with moisture characteristics
	getPersonalityNoiseLevel(): number {
		let noiseLevel = this.voiceCharacteristics.breathiness;
		
		// Adjust based on personality
		switch (this.voiceCharacteristics.personality) {
			case 'warm':
				noiseLevel *= 1.3; // More moist and breathy
				break;
			case 'cold':
				noiseLevel *= 0.4; // Less breathy, drier
				break;
			case 'mysterious':
				noiseLevel *= 1.2; // More moist and mysterious
				break;
			case 'playful':
				noiseLevel *= 1.4; // Very moist and playful
				break;
			case 'wise':
				noiseLevel *= 0.9; // Less breathy, more focused
				break;
			case 'energetic':
				noiseLevel *= 1.1; // Slightly more moist
				break;
		}
		
		// Add moisture based on emotional state
		switch (this.voiceCharacteristics.emotionalState) {
			case 'excited':
				noiseLevel *= 1.15; // More moist when excited
				break;
			case 'contemplative':
				noiseLevel *= 1.25; // More moist and thoughtful
				break;
			case 'curious':
				noiseLevel *= 1.2; // Moist with curiosity
				break;
			case 'sad':
				noiseLevel *= 0.8; // Drier when sad
				break;
		}
		
		return noiseLevel;
	}
	
	// Get formant frequencies for specific vowel with distinct human voice shaping
	getVowelFormants(element: LinguisticElement): { f1: number, f2: number, f3: number, f4?: number, f5?: number } {
		const phoneticValue = element.phoneticValue.toLowerCase();
		
		// Distinct formant frequencies for each vowel (based on acoustic phonetics)
		let f1 = 800, f2 = 1200, f3 = 2500;
		
		// Specific vowel formants for clear differentiation
		switch (phoneticValue) {
			// Front vowels
			case 'i': case 'ɪ': case 'iː': // /i/ as in "beat"
				f1 = 270; f2 = 2290; f3 = 3010;
				break;
			case 'e': case 'ɛ': case 'eː': // /e/ as in "bet"
				f1 = 530; f2 = 1840; f3 = 2480;
				break;
			case 'æ': // /æ/ as in "bat"
				f1 = 660; f2 = 1720; f3 = 2410;
				break;
			case 'a': case 'ɑ': case 'aː': // /ɑ/ as in "father"
				f1 = 730; f2 = 1090; f3 = 2440;
				break;
			
			// Central vowels
			case 'ə': case 'ɜ': case 'ɜː': // /ə/ as in "about"
				f1 = 500; f2 = 1500; f3 = 2500;
				break;
			case 'ɨ': case 'ɯ': // Central high vowels
				f1 = 300; f2 = 1600; f3 = 2800;
				break;
			
			// Back vowels
			case 'u': case 'ʊ': case 'uː': // /u/ as in "boot"
				f1 = 300; f2 = 870; f3 = 2240;
				break;
			case 'o': case 'ɔ': case 'oː': // /o/ as in "boat"
				f1 = 570; f2 = 840; f3 = 2410;
				break;
			case 'ʌ': case 'ɐ': // /ʌ/ as in "but"
				f1 = 640; f2 = 1190; f3 = 2390;
				break;
			
			// Rounded vowels
			case 'y': case 'ʏ': // /y/ as in French "tu"
				f1 = 270; f2 = 2290; f3 = 3010;
				break;
			case 'ø': case 'œ': // /ø/ as in French "peu"
				f1 = 530; f2 = 1840; f3 = 2480;
				break;
			
			// Default based on vowel properties
			default:
				if (element.vowelHeight === 'close') {
					f1 = 300; f2 = 2000; f3 = 3000;
				} else if (element.vowelHeight === 'near_close') {
					f1 = 400; f2 = 1800; f3 = 2800;
				} else if (element.vowelHeight === 'close_mid') {
					f1 = 500; f2 = 1600; f3 = 2600;
				} else if (element.vowelHeight === 'mid') {
					f1 = 600; f2 = 1400; f3 = 2500;
				} else if (element.vowelHeight === 'open_mid') {
					f1 = 700; f2 = 1200; f3 = 2400;
				} else if (element.vowelHeight === 'near_open') {
					f1 = 750; f2 = 1100; f3 = 2450;
				} else if (element.vowelHeight === 'open') {
					f1 = 800; f2 = 1000; f3 = 2500;
				}
				
				if (element.vowelBackness === 'front') {
					f2 = Math.max(f2, 1800); f3 = Math.max(f3, 2600);
				} else if (element.vowelBackness === 'back') {
					f2 = Math.min(f2, 1200); f3 = Math.min(f3, 2400);
				}
				
				if (element.vowelRoundedness === 'rounded') {
					f1 *= 0.9; f2 *= 0.8; f3 *= 0.9;
				}
				break;
		}
		
		// Apply personality adjustments
		const personality = this.voiceCharacteristics.personality;
		if (personality === 'energetic') {
			f1 *= 1.05; f2 *= 1.03; f3 *= 1.02;
		} else if (personality === 'calm') {
			f1 *= 0.98; f2 *= 0.99; f3 *= 0.99;
		} else if (personality === 'mysterious') {
			f1 *= 0.95; f2 *= 1.02; f3 *= 1.05;
		} else if (personality === 'playful') {
			f1 *= 1.02; f2 *= 1.01; f3 *= 1.01;
		}
		
		// Apply emotional state adjustments
		const emotion = this.voiceCharacteristics.emotionalState;
		if (emotion === 'excited') {
			f1 *= 1.03; f2 *= 1.02; f3 *= 1.01;
		} else if (emotion === 'sad') {
			f1 *= 0.97; f2 *= 0.98; f3 *= 0.99;
		} else if (emotion === 'contemplative') {
			f1 *= 0.99; f2 *= 1.01; f3 *= 1.02;
		}
		
		return { f1: Math.round(f1), f2: Math.round(f2), f3: Math.round(f3) };
	}
	
	// Synthesize a consonant sound using Web Audio API
	synthesizeConsonant(element: LinguisticElement): void {
		console.log(`[Creature Voice] synthesizeConsonant called for "${element.phoneticValue}"`);
		
		// Use Web Audio API synthesis instead of SpeechSynthesisUtterance
		this.synthesizeLinguisticElement(element);
	}
	
	// Get duration based on consonant type - very short for natural speech
	getConsonantDuration(element: LinguisticElement): number {
		if (element.mannerOfArticulation === 'plosive') return 0.02; // 20ms - very quick burst
		if (element.mannerOfArticulation === 'tap') return 0.015; // 15ms - quick tap
		if (element.mannerOfArticulation === 'trill') return 0.08; // 80ms - short trill
		if (element.mannerOfArticulation === 'nasal') return 0.06; // 60ms - brief nasal
		if (element.mannerOfArticulation === 'fricative') return 0.04; // 40ms - short fricative
		if (element.mannerOfArticulation === 'approximant') return 0.03; // 30ms - quick approximant
		if (element.mannerOfArticulation === 'lateral_approximant') return 0.035; // 35ms
		return 0.03; // Default - very short
	}
	
	// Synthesize plosive consonants using realistic human articulation
	synthesizePlosive(element: LinguisticElement, now: number, duration: number): void {
		console.log(`[Audio] Synthesizing plosive "${element.phoneticValue}" with human articulation`);
		
		// Generate fractal-enhanced voice characteristics
		const fractalVoice = generateFractalVoiceCharacteristics(this, this.voiceCharacteristics);
		
		// Create realistic plosive burst with aspiration
		const plosiveBurst = this.createPlosiveBurst(element, duration, now);
		
		// For voiced plosives, add vocal cord vibration after the burst
		if (element.voicing === 'voiced') {
			// Create brief vocal cord vibration for voiced plosives (lowered base pitch)
			const baseFreq = element.frequency || 70; // Lowered from 100 to 70
			const fractalPitch = calculateFractalPitchModulation(baseFreq, this, element);
			const fundamentalFreq = this.getPersonalityAdjustedPitch(fractalPitch);
			const glottalPulse = this.createGlottalPulse(fundamentalFreq, duration * 0.3, now + duration * 0.1);
			
			// Create vocal tract resonance
			const vocalTractResonance = this.createVocalTractResonance(now);
			
			// Mix burst with voice
			const mixer = this.audioContext!.createGain();
			const burstGain = this.audioContext!.createGain();
			const voiceGain = this.audioContext!.createGain();
			
			burstGain.gain.setValueAtTime(1.0, now);
			voiceGain.gain.setValueAtTime(0.5, now);
			
			// Filter based on place of articulation
			const filter = this.audioContext!.createBiquadFilter();
			filter.type = 'bandpass';
			const baseFreqPlosive = this.getPlaceOfArticulationFrequency(element.placeOfArticulation);
			filter.frequency.setValueAtTime(baseFreqPlosive, now);
			filter.Q.setValueAtTime(5, now);
			
			// Create envelope with sharp attack and quick decay
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
				envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.8, now + 0.001); // Balanced volume
			envelope.gain.exponentialRampToValueAtTime(0.001, now + duration);
			
			// Connect audio graph
			plosiveBurst.connect(burstGain);
			glottalPulse.connect(voiceGain);
			burstGain.connect(mixer);
			voiceGain.connect(mixer);
			mixer.connect(vocalTractResonance);
			vocalTractResonance.connect(filter);
			filter.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
			
			// Start synthesis
			plosiveBurst.start(now);
			plosiveBurst.stop(now + duration);
			glottalPulse.start(now + duration * 0.1);
			glottalPulse.stop(now + duration * 0.4);
		} else {
			// For voiceless plosives, use only burst
			const filter = this.audioContext!.createBiquadFilter();
			filter.type = 'bandpass';
			const baseFreqPlosive = this.getPlaceOfArticulationFrequency(element.placeOfArticulation);
			filter.frequency.setValueAtTime(baseFreqPlosive, now);
			filter.Q.setValueAtTime(5, now);
			
			// Create envelope with sharp attack
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
				envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.7, now + 0.001); // Balanced volume
			envelope.gain.exponentialRampToValueAtTime(0.001, now + duration);
			
			// Connect and start
			plosiveBurst.connect(filter);
			filter.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
			
			plosiveBurst.start(now);
			plosiveBurst.stop(now + duration);
		}
	}

	// Create realistic plosive burst with aspiration
	createPlosiveBurst(element: LinguisticElement, duration: number, now: number): AudioBufferSourceNode {
		const sampleRate = this.audioContext!.sampleRate;
		const bufferLength = Math.floor(sampleRate * duration);
		const buffer = this.audioContext!.createBuffer(1, bufferLength, sampleRate);
		const data = buffer.getChannelData(0);
		
		// Get place-specific burst characteristics
		const placeFreq = this.getPlaceOfArticulationFrequency(element.placeOfArticulation);
		const bandwidth = this.getPlaceBandwidth(element.placeOfArticulation);
		
		for (let i = 0; i < bufferLength; i++) {
			const time = i / sampleRate;
			
			// Create explosive burst
			let burst = 0;
			if (time < 0.01) {
				// Sharp initial burst
				burst = (Math.random() - 0.5) * 2 * Math.exp(-time * 200);
			} else {
				// Aspiration phase
				const aspiration = (Math.random() - 0.5) * 0.3 * Math.exp(-time * 10);
				burst = aspiration;
			}
			
			// Apply place-specific filtering
			const centerFreq = placeFreq;
			const filterResponse = Math.exp(-Math.pow((time * 1000 - centerFreq) / bandwidth, 2));
			
			// Add turbulence
			const turbulence = this.getTurbulenceLevel(element.mannerOfArticulation);
			
			data[i] = burst * filterResponse * turbulence;
		}
		
		const source = this.audioContext!.createBufferSource();
		source.buffer = buffer;
		return source;
	}
	
	// Synthesize fricative consonants using realistic human articulation
	synthesizeFricative(element: LinguisticElement, now: number, duration: number): void {
		console.log(`[Audio] Synthesizing fricative "${element.phoneticValue}" with human articulation`);
		
		// Generate fractal-enhanced voice characteristics
		const fractalVoice = generateFractalVoiceCharacteristics(this, this.voiceCharacteristics);
		
		// Create realistic fricative noise based on place of articulation
		const fricativeNoise = this.createFricativeNoise(element, duration, now);
		
		// For voiced fricatives, add vocal cord vibration
		if (element.voicing === 'voiced') {
			// Create realistic vocal cord vibration (lowered base pitch)
			const baseFreqFricative = element.frequency || 70; // Lowered from 100 to 70
			const fractalPitch = calculateFractalPitchModulation(baseFreqFricative, this, element);
			const fundamentalFreq = this.getPersonalityAdjustedPitch(fractalPitch);
			const glottalPulse = this.createGlottalPulse(fundamentalFreq, duration, now);
			
			// Create realistic vocal tract for voiced fricatives
			const vocalTractResonance = this.createVocalTractResonance(now);
			
			// Mix vocal cord vibration with fricative noise
			const mixer = this.audioContext!.createGain();
			const voiceGain = this.audioContext!.createGain();
			const noiseGain = this.audioContext!.createGain();
			
			voiceGain.gain.setValueAtTime(0.7, now);
			noiseGain.gain.setValueAtTime(fractalVoice.breathiness * 0.4, now);
			
			// Filter based on place of articulation
			const filter = this.audioContext!.createBiquadFilter();
			filter.type = 'bandpass';
			const baseFreq = this.getPlaceOfArticulationFrequency(element.placeOfArticulation);
			filter.frequency.setValueAtTime(baseFreq, now);
			filter.Q.setValueAtTime(4, now);
			
			// Create envelope
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
				envelope.gain.linearRampToValueAtTime(this.getPersonalityAdjustedVolume() * 0.9, now + 0.01); // Increased from 0.6 to 0.9
			envelope.gain.linearRampToValueAtTime(0, now + duration);
			
			// Connect audio graph
			glottalPulse.connect(voiceGain);
			fricativeNoise.connect(noiseGain);
			voiceGain.connect(mixer);
			noiseGain.connect(mixer);
			mixer.connect(vocalTractResonance);
			vocalTractResonance.connect(filter);
			filter.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
			
			// Start synthesis
			glottalPulse.start(now);
			glottalPulse.stop(now + duration);
			fricativeNoise.start(now);
			fricativeNoise.stop(now + duration);
		} else {
			// For voiceless fricatives, use only noise
			const filter = this.audioContext!.createBiquadFilter();
			filter.type = 'bandpass';
			const baseFreq = this.getPlaceOfArticulationFrequency(element.placeOfArticulation);
			filter.frequency.setValueAtTime(baseFreq, now);
			filter.Q.setValueAtTime(4, now);
			
			// Create envelope
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
				envelope.gain.linearRampToValueAtTime(this.getPersonalityAdjustedVolume() * 0.8, now + 0.01); // Increased from 0.5 to 0.8
			envelope.gain.linearRampToValueAtTime(0, now + duration);
			
			// Connect audio graph
			fricativeNoise.connect(filter);
			filter.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
			
			// Start synthesis
			fricativeNoise.start(now);
			fricativeNoise.stop(now + duration);
		}
	}
	
	// Synthesize nasal consonants
	synthesizeNasal(element: LinguisticElement, now: number, duration: number): void {
		// Create fundamental frequency
		const oscillator = this.audioContext!.createOscillator();
		oscillator.type = 'sine';
		oscillator.frequency.setValueAtTime(120, now);
		
		// Add nasal formants
		const formant1 = this.audioContext!.createBiquadFilter();
		formant1.type = 'bandpass';
		formant1.frequency.setValueAtTime(250, now);
		formant1.Q.setValueAtTime(8, now);
		
		const formant2 = this.audioContext!.createBiquadFilter();
		formant2.type = 'bandpass';
		formant2.frequency.setValueAtTime(2500, now);
		formant2.Q.setValueAtTime(5, now);
		
		// Create envelope
		const envelope = this.audioContext!.createGain();
		envelope.gain.setValueAtTime(0, now);
		envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.6, now + 0.02);
		envelope.gain.linearRampToValueAtTime(0, now + duration);
		
		// Connect and start
		oscillator.connect(formant1);
		formant1.connect(formant2);
		formant2.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
		
		oscillator.start(now);
		oscillator.stop(now + duration);
	}
	
	// Synthesize approximant consonants
	synthesizeApproximant(element: LinguisticElement, now: number, duration: number): void {
		// Create fundamental frequency
		const oscillator = this.audioContext!.createOscillator();
		oscillator.type = 'sine';
		oscillator.frequency.setValueAtTime(150, now);
		
		// Add formants
		const formant1 = this.audioContext!.createBiquadFilter();
		formant1.type = 'bandpass';
		formant1.frequency.setValueAtTime(800, now);
		formant1.Q.setValueAtTime(6, now);
		
		const formant2 = this.audioContext!.createBiquadFilter();
		formant2.type = 'bandpass';
		formant2.frequency.setValueAtTime(1200, now);
		formant2.Q.setValueAtTime(4, now);
		
		// Create envelope
		const envelope = this.audioContext!.createGain();
		envelope.gain.setValueAtTime(0, now);
		envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.4, now + 0.02);
		envelope.gain.linearRampToValueAtTime(0, now + duration);
		
		// Connect and start
		oscillator.connect(formant1);
		formant1.connect(formant2);
		formant2.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
		
		oscillator.start(now);
		oscillator.stop(now + duration);
	}
	
	// Synthesize trill consonants
	synthesizeTrill(element: LinguisticElement, now: number, duration: number): void {
		// Create rapid oscillation
		const oscillator = this.audioContext!.createOscillator();
		oscillator.type = 'sine';
		oscillator.frequency.setValueAtTime(25, now); // Rapid trill frequency
		
		// Add formants
		const formant1 = this.audioContext!.createBiquadFilter();
		formant1.type = 'bandpass';
		formant1.frequency.setValueAtTime(800, now);
		formant1.Q.setValueAtTime(8, now);
		
		// Create envelope
		const envelope = this.audioContext!.createGain();
		envelope.gain.setValueAtTime(0, now);
		envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.5, now + 0.01);
		envelope.gain.linearRampToValueAtTime(0, now + duration);
		
		// Connect and start
		oscillator.connect(formant1);
		formant1.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
		
		oscillator.start(now);
		oscillator.stop(now + duration);
	}
	
	
	// Synthesize diphthong sounds
	synthesizeDiphthong(element: LinguisticElement): void {
		console.log(`[Audio] synthesizeDiphthong called for "${element.phoneticValue}"`);
		if (!this.initializeAudioContext()) {
			console.log('[Audio] Failed to initialize audio context for diphthong synthesis');
			return;
		}
		
		try {
			const now = this.audioContext!.currentTime;
			const duration = 0.4; // Longer duration for diphthongs
			
			// Create main oscillator
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'sine';
			
			// Start with first vowel frequency
			const startFreq = element.frequency || 200;
			oscillator.frequency.setValueAtTime(startFreq, now);
			
			// Glide to second vowel frequency
			const endFreq = startFreq + 100; // Simple glide
			oscillator.frequency.linearRampToValueAtTime(endFreq, now + duration);
			
			// Add formants for vowel-like quality
			const formant1 = this.audioContext!.createBiquadFilter();
			formant1.type = 'bandpass';
			formant1.frequency.setValueAtTime(800, now);
			formant1.Q.setValueAtTime(6, now);
			
			const formant2 = this.audioContext!.createBiquadFilter();
			formant2.type = 'bandpass';
			formant2.frequency.setValueAtTime(1200, now);
			formant2.Q.setValueAtTime(4, now);
			
			// Create envelope
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.6, now + 0.02);
			envelope.gain.linearRampToValueAtTime(0, now + duration);
			
			// Connect and start
			oscillator.connect(formant1);
			formant1.connect(formant2);
			formant2.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
			
			// Update element properties
			element.audioSynthesized = true;
			element.lastSynthesisTime = now;
			element.synthesisQuality = 0.8 + Math.random() * 0.2;
		} catch (error) {
			console.warn('[Audio] Failed to synthesize diphthong:', error);
		}
	}
	
	// Synthesize tone patterns
	synthesizeTone(element: LinguisticElement): void {
		console.log(`[Audio] synthesizeTone called for "${element.phoneticValue}"`);
		if (!this.initializeAudioContext()) {
			console.log('[Audio] Failed to initialize audio context for tone synthesis');
			return;
		}
		
		try {
			const now = this.audioContext!.currentTime;
			const duration = 0.3;
			
			// Create oscillator
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'sine';
			
			// Set frequency based on tone level (1-5)
			const baseFreq = 150;
			const toneFreq = baseFreq + (element.tone || 1) * 50;
			oscillator.frequency.setValueAtTime(toneFreq, now);
			
			// Create envelope with tone contour
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.4, now + 0.01);
			envelope.gain.linearRampToValueAtTime(0, now + duration);
			
			// Connect and start
			oscillator.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
			
			// Update element properties
			element.audioSynthesized = true;
			element.lastSynthesisTime = now;
			element.synthesisQuality = 0.8 + Math.random() * 0.2;
		} catch (error) {
			console.warn('[Audio] Failed to synthesize tone:', error);
		}
	}
	
	// Synthesize stress patterns
	synthesizeStress(element: LinguisticElement): void {
		console.log(`[Audio] synthesizeStress called for "${element.phoneticValue}"`);
		if (!this.initializeAudioContext()) {
			console.log('[Audio] Failed to initialize audio context for stress synthesis');
			return;
		}
		
		try {
			const now = this.audioContext!.currentTime;
			const duration = 0.2;
			
			// Create oscillator
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'sine';
			
			// Set frequency based on stress level
			let frequency = 200;
			if (element.stress === 'primary') frequency = 300;
			else if (element.stress === 'secondary') frequency = 250;
			else frequency = 200; // unstressed
			
			oscillator.frequency.setValueAtTime(frequency, now);
			
			// Create envelope with stress characteristics
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			
			let volume = this.synthesisConfig.volume * 0.3;
			if (element.stress === 'primary') volume = this.synthesisConfig.volume * 0.8;
			else if (element.stress === 'secondary') volume = this.synthesisConfig.volume * 0.5;
			
			envelope.gain.linearRampToValueAtTime(volume, now + 0.01);
			envelope.gain.linearRampToValueAtTime(0, now + duration);
			
			// Connect and start
			oscillator.connect(envelope);
		if (this.reverbNode) {
			envelope.connect(this.reverbNode);
			this.reverbNode.connect(this.getAudioOutput());
		} else {
			envelope.connect(this.getAudioOutput());
		}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
			
			// Update element properties
			element.audioSynthesized = true;
			element.lastSynthesisTime = now;
			element.synthesisQuality = 0.8 + Math.random() * 0.2;
		} catch (error) {
			console.warn('[Audio] Failed to synthesize stress:', error);
		}
	}
	
	// Get frequency for articulation type
	getArticulationFrequency(articulation: string): number {
		const frequencies = {
			'bilabial': 200,
			'labiodental': 300,
			'dental': 400,
			'alveolar': 500,
			'postalveolar': 600,
			'retroflex': 700,
			'palatal': 800,
			'velar': 900,
			'uvular': 1000,
			'pharyngeal': 1100,
			'glottal': 1200
		};
		return frequencies[articulation as keyof typeof frequencies] || 500;
	}
	
	// Synthesize a syllable
	synthesizeSyllable(element: LinguisticElement): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			const now = this.audioContext!.currentTime;
			const syllable = this.discoveredSyllables.find(s => s.pronunciation === element.phoneticValue);
			
			if (!syllable) return;
			
			// Synthesize consonant first (if present)
			if (syllable.consonants.length > 0) {
				const consonantElement: LinguisticElement = {
					phoneticValue: syllable.consonants[0],
					type: 'consonant',
					articulation: 'alveolar',
					complexity: 1,
					discoveryStep: this.step,
					usageCount: 0,
					learningPhase: 'discovery'
				};
				this.synthesizeConsonant(consonantElement);
			}
			
			// Small delay between consonant and vowel
			setTimeout(() => {
				// Synthesize vowel
				const vowelElement: LinguisticElement = {
					phoneticValue: syllable.vowel,
					type: 'vowel',
					frequency: 200 + Math.random() * 400,
					complexity: 1,
					discoveryStep: this.step,
					usageCount: 0,
					learningPhase: 'discovery'
				};
				// Creature expresses the vowel discovery using Web Audio API
				this.synthesizeLinguisticElement(vowelElement);
			}, 50);
			
			// Update element properties
			element.audioSynthesized = true;
			element.lastSynthesisTime = now;
			element.synthesisQuality = 0.8 + Math.random() * 0.2;
		} catch (error) {
			console.warn('[Audio] Failed to synthesize syllable:', error);
		}
	}
	
	// Synthesize a word
	synthesizeWord(element: LinguisticElement): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			const now = this.audioContext!.currentTime;
			const word = this.discoveredWords.find(w => w.syllables.map(s => s.pronunciation).join('') === element.phoneticValue);
			
			if (!word) return;
			
			// Synthesize each syllable with timing
			word.syllables.forEach((syllable, index) => {
				setTimeout(() => {
					const syllableElement: LinguisticElement = {
						phoneticValue: syllable.pronunciation,
						type: 'syllable',
						complexity: syllable.complexity,
						discoveryStep: this.step,
						usageCount: 0,
						learningPhase: 'discovery'
					};
					this.synthesizeSyllable(syllableElement);
				}, index * 200); // 200ms between syllables
			});
			
			// Update element properties
			element.audioSynthesized = true;
			element.lastSynthesisTime = now;
			element.synthesisQuality = 0.9 + Math.random() * 0.1;
		} catch (error) {
			console.warn('[Audio] Failed to synthesize word:', error);
		}
	}
	
	// Synthesize any linguistic element using SpeechSynthesisUtterance with enhanced volume
	synthesizeLinguisticElement(element: LinguisticElement): void {
		// DISABLED: Old linguistic synthesis replaced with musical pattern synthesis
		// This method is kept for compatibility but does nothing to avoid repetitive speech
		return;
	}
	
		// Synthesize speech with enhanced volume using single utterance
		synthesizeSpeechWithEnhancedVolume(element: LinguisticElement): void {
			if (!('speechSynthesis' in window)) {
				console.warn('[Voice] Speech synthesis not supported');
				return;
			}

			try {
				// Create single utterance to avoid repetition
				const baseText = this.convertToPronounceableText(element.phoneticValue);
				const utterance = new SpeechSynthesisUtterance(baseText);
				
				// Configure voice characteristics
				utterance.volume = 1.0; // Maximum volume
				utterance.rate = 0.8; // Natural speech rate
				utterance.pitch = 1.0; // Natural pitch
				
				// Try to get a good voice
				const voices = speechSynthesis.getVoices();
				if (voices.length > 0) {
					// Prefer deeper, more resonant voices
					const preferredVoice = voices.find(v => 
						v.name.toLowerCase().includes('male') || 
						v.name.toLowerCase().includes('deep') ||
						v.name.toLowerCase().includes('microsoft') ||
						v.name.toLowerCase().includes('google')
					) || voices[0];
					utterance.voice = preferredVoice;
				}
				
				// Speak the utterance once
				speechSynthesis.speak(utterance);
				
				console.log(`[Voice] Enhanced speech synthesis: "${baseText}" (single utterance)`);
			} catch (error) {
				console.warn('[Voice] Failed to synthesize speech with enhanced volume:', error);
			}
		}
	
	// Convert phonetic values to pronounceable text
	convertToPronounceableText(phoneticValue: string): string {
		// Convert phonetic notation to readable text
		let text = phoneticValue;
		
		// Replace common phonetic symbols with readable equivalents
		const phoneticMap: { [key: string]: string } = {
			'ɑ': 'ah', 'æ': 'a', 'ʌ': 'uh', 'ə': 'uh',
			'ɛ': 'e', 'ɪ': 'i', 'i': 'ee', 'ɔ': 'aw', 'o': 'oh', 'u': 'oo',
			'p': 'p', 'b': 'b', 't': 't', 'd': 'd', 'k': 'k', 'g': 'g',
			'f': 'f', 'v': 'v', 's': 's', 'z': 'z', 'ʃ': 'sh', 'ʒ': 'zh',
			'θ': 'th', 'ð': 'th', 'h': 'h', 'm': 'm', 'n': 'n', 'ŋ': 'ng',
			'l': 'l', 'r': 'r', 'w': 'w', 'j': 'y'
		};
		
		// Replace phonetic symbols
		for (const [phonetic, readable] of Object.entries(phoneticMap)) {
			text = text.replace(new RegExp(phonetic, 'g'), readable);
		}
		
		// If it's still mostly symbols, use a fallback
		if (text.length < 2 || /[^a-zA-Z\s]/.test(text)) {
			const fallbacks = ['ah', 'eh', 'oh', 'oo', 'ee', 'ay', 'ai', 'ou', 'ea', 'ie'];
			text = fallbacks[Math.floor(Math.random() * fallbacks.length)];
		}
		
		return text;
	}
	
	// Web Audio API synthesis functions for different linguistic elements
	synthesizeVowelWithWebAudio(element: LinguisticElement): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			const now = this.audioContext!.currentTime;
			
			// Use musical properties if available, otherwise use defaults
			const pitch = element.musicalPitch || element.frequency || 200;
			const duration = (element.vowelLength || 300) / 1000; // Convert ms to seconds
			
			console.log(`[Musical Vowel] Synthesizing "${element.phoneticValue}" at ${pitch.toFixed(1)}Hz for ${(duration * 1000).toFixed(0)}ms`);
			
			// Create oscillator with vowel characteristics
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'sine';
			oscillator.frequency.setValueAtTime(pitch, now);
			
			// Create envelope with high volume
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.8, now + 0.01);
			envelope.gain.exponentialRampToValueAtTime(0.001, now + duration);
			
			// Connect audio graph
			oscillator.connect(envelope);
			if (this.reverbNode) {
				envelope.connect(this.reverbNode);
				this.reverbNode.connect(this.getAudioOutput());
			} else {
				envelope.connect(this.getAudioOutput());
			}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
			
			// Also synthesize with speech synthesis using musical pitch
			this.synthesizeVowelWithMusicalPitch(element.phoneticValue, pitch, duration * 1000);
			
		} catch (error) {
			console.warn('[Audio] Failed to synthesize vowel with Web Audio API:', error);
		}
	}
	
	synthesizeConsonantWithWebAudio(element: LinguisticElement): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			const now = this.audioContext!.currentTime;
			const duration = 0.2;
			
			// Create oscillator for consonant
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'square';
			oscillator.frequency.setValueAtTime(150, now);
			
			// Create envelope with high volume
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.7, now + 0.01);
			envelope.gain.exponentialRampToValueAtTime(0.001, now + duration);
			
			// Connect audio graph
			oscillator.connect(envelope);
			if (this.reverbNode) {
				envelope.connect(this.reverbNode);
				this.reverbNode.connect(this.getAudioOutput());
			} else {
				envelope.connect(this.getAudioOutput());
			}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
		} catch (error) {
			console.warn('[Audio] Failed to synthesize consonant with Web Audio API:', error);
		}
	}
	
	synthesizeSyllableWithWebAudio(element: LinguisticElement): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			const now = this.audioContext!.currentTime;
			const duration = 0.4;
			
			// Create oscillator for syllable
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'triangle';
			oscillator.frequency.setValueAtTime(180, now);
			
			// Create envelope with high volume
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.6, now + 0.02);
			envelope.gain.linearRampToValueAtTime(0, now + duration);
			
			// Connect audio graph
			oscillator.connect(envelope);
			if (this.reverbNode) {
				envelope.connect(this.reverbNode);
				this.reverbNode.connect(this.getAudioOutput());
			} else {
				envelope.connect(this.getAudioOutput());
			}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
		} catch (error) {
			console.warn('[Audio] Failed to synthesize syllable with Web Audio API:', error);
		}
	}
	
	synthesizeWordWithWebAudio(element: LinguisticElement): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			const now = this.audioContext!.currentTime;
			const duration = 0.6;
			
			// Create oscillator for word
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'sawtooth';
			oscillator.frequency.setValueAtTime(160, now);
			
			// Create envelope with high volume
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.5, now + 0.01);
			envelope.gain.linearRampToValueAtTime(0, now + duration);
			
			// Connect audio graph
			oscillator.connect(envelope);
			if (this.reverbNode) {
				envelope.connect(this.reverbNode);
				this.reverbNode.connect(this.getAudioOutput());
			} else {
				envelope.connect(this.getAudioOutput());
			}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
		} catch (error) {
			console.warn('[Audio] Failed to synthesize word with Web Audio API:', error);
		}
	}
	
	// Synthesize growth sound using Web Audio API
	synthesizeGrowthSound(): void {
		if (!this.initializeAudioContext()) return;
		
		try {
			const now = this.audioContext!.currentTime;
			const duration = 0.4;
			
			// Create oscillator for growth sound
			const oscillator = this.audioContext!.createOscillator();
			oscillator.type = 'triangle';
			oscillator.frequency.setValueAtTime(150, now);
			oscillator.frequency.linearRampToValueAtTime(300, now + duration);
			
			// Create envelope with high volume
			const envelope = this.audioContext!.createGain();
			envelope.gain.setValueAtTime(0, now);
			envelope.gain.linearRampToValueAtTime(this.synthesisConfig.volume * 0.6, now + 0.02);
			envelope.gain.exponentialRampToValueAtTime(0.001, now + duration);
			
			// Connect audio graph
			oscillator.connect(envelope);
			if (this.reverbNode) {
				envelope.connect(this.reverbNode);
				this.reverbNode.connect(this.getAudioOutput());
			} else {
				envelope.connect(this.getAudioOutput());
			}
			
			oscillator.start(now);
			oscillator.stop(now + duration);
		} catch (error) {
			console.warn('[Audio] Failed to synthesize growth sound:', error);
		}
	}
	
	// Simple beep fallback when Web Audio API is not available
	synthesizeSimpleBeep(element: LinguisticElement): void {
		try {
			// Create a simple beep using the Web Audio API or fallback to a basic tone
			const frequency = element.frequency || (element.type === 'vowel' ? 200 : 400);
			const duration = element.type === 'vowel' ? 300 : 100;
			
			// Try to create a simple oscillator if possible
			if (this.audioContext && this.audioContext.state === 'running') {
				const oscillator = this.audioContext.createOscillator();
				const gainNode = this.audioContext.createGain();
				
				oscillator.connect(gainNode);
				if (this.reverbNode) {
					gainNode.connect(this.reverbNode);
					this.reverbNode.connect(this.getAudioOutput());
				} else {
					gainNode.connect(this.getAudioOutput());
				}
				
				oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
				oscillator.type = 'sine';
				
				gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
				gainNode.gain.linearRampToValueAtTime(0.1, this.audioContext.currentTime + 0.01);
				gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + duration / 1000);
				
				oscillator.start(this.audioContext.currentTime);
				oscillator.stop(this.audioContext.currentTime + duration / 1000);
			}
		} catch (error) {
			console.warn('[Audio] Fallback beep failed:', error);
		}
	}
	
	// Synthesize verbal communication
	synthesizeVerbalCommunication(communication: VerbalCommunication): void {
		if (!this.initializeAudioContext()) return;
		
		// Synthesize each element in the communication
		communication.linguisticElements.forEach((element, index) => {
			setTimeout(() => {
				this.synthesizeLinguisticElement(element);
			}, index * 300); // 300ms between elements
		});
		
		console.log(`[Audio Synthesis] Synthesized communication: "${communication.message}"`);
	}
	
	// Evolve voice characteristics based on learning
	evolveVoiceCharacteristics(): void {
		// Slight random evolution of voice characteristics
		this.voiceCharacteristics.pitch += (Math.random() - 0.5) * 5;
		this.voiceCharacteristics.pitch = Math.max(100, Math.min(300, this.voiceCharacteristics.pitch));
		
		this.voiceCharacteristics.formant1 += (Math.random() - 0.5) * 50;
		this.voiceCharacteristics.formant1 = Math.max(500, Math.min(1500, this.voiceCharacteristics.formant1));
		
		this.voiceCharacteristics.formant2 += (Math.random() - 0.5) * 50;
		this.voiceCharacteristics.formant2 = Math.max(800, Math.min(2000, this.voiceCharacteristics.formant2));
		
		this.voiceCharacteristics.formant3 += (Math.random() - 0.5) * 100;
		this.voiceCharacteristics.formant3 = Math.max(1500, Math.min(4000, this.voiceCharacteristics.formant3));
		
		// Evolve personality and emotional state
		this.evolvePersonality();
		this.evolveEmotionalState();
		
		// Update synthesis config
		this.synthesisConfig.voiceCharacteristics = this.voiceCharacteristics;
	}
	
	// Evolve personality over time
	evolvePersonality(): void {
		// Occasionally change personality (5% chance)
		if (Math.random() < 0.05) {
			const personalities = ['warm', 'cold', 'energetic', 'calm', 'mysterious', 'playful', 'wise', 'youthful'];
			const currentIndex = personalities.indexOf(this.voiceCharacteristics.personality);
			const newIndex = (currentIndex + Math.floor(Math.random() * (personalities.length - 1)) + 1) % personalities.length;
			this.voiceCharacteristics.personality = personalities[newIndex] as any;
			console.log(`[Voice] Personality evolved to: ${this.voiceCharacteristics.personality}`);
		}
	}
	
	// Evolve emotional state over time
	evolveEmotionalState(): void {
		// Occasionally change emotional state (10% chance)
		if (Math.random() < 0.1) {
			const emotionalStates = ['neutral', 'happy', 'sad', 'excited', 'contemplative', 'curious', 'confident', 'uncertain'];
			const currentIndex = emotionalStates.indexOf(this.voiceCharacteristics.emotionalState);
			const newIndex = (currentIndex + Math.floor(Math.random() * (emotionalStates.length - 1)) + 1) % emotionalStates.length;
			this.voiceCharacteristics.emotionalState = emotionalStates[newIndex] as any;
			console.log(`[Voice] Emotional state evolved to: ${this.voiceCharacteristics.emotionalState}`);
		}
	}
	
	// Set specific voice personality
	setVoicePersonality(personality: 'warm' | 'cold' | 'energetic' | 'calm' | 'mysterious' | 'playful' | 'wise' | 'youthful'): void {
		this.voiceCharacteristics.personality = personality;
		console.log(`[Voice] Personality set to: ${personality}`);
	}
	
	// Set specific emotional state
	setEmotionalState(emotionalState: 'neutral' | 'happy' | 'sad' | 'excited' | 'contemplative' | 'curious' | 'confident' | 'uncertain'): void {
		this.voiceCharacteristics.emotionalState = emotionalState;
		console.log(`[Voice] Emotional state set to: ${emotionalState}`);
	}

	// Update voice synthesis parameters safely (bounds/clamped)
	setVoiceParams(params: Partial<Pick<VoiceCharacteristics,
		'pitch' | 'breathiness' | 'roughness' | 'attackTime' | 'decayTime' | 'sustainLevel' | 'releaseTime' | 'formant1' | 'formant2' | 'formant3'>>): void {
		const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
		if (typeof params.pitch === 'number') this.voiceCharacteristics.pitch = clamp(params.pitch, 50, 500);
		if (typeof params.breathiness === 'number') this.voiceCharacteristics.breathiness = clamp(params.breathiness, 0, 1);
		if (typeof params.roughness === 'number') this.voiceCharacteristics.roughness = clamp(params.roughness, 0, 1);
		if (typeof params.attackTime === 'number') this.voiceCharacteristics.attackTime = clamp(params.attackTime, 5, 1000);
		if (typeof params.decayTime === 'number') this.voiceCharacteristics.decayTime = clamp(params.decayTime, 5, 2000);
		if (typeof params.sustainLevel === 'number') this.voiceCharacteristics.sustainLevel = clamp(params.sustainLevel, 0, 1);
		if (typeof params.releaseTime === 'number') this.voiceCharacteristics.releaseTime = clamp(params.releaseTime, 10, 4000);
		if (typeof params.formant1 === 'number') this.voiceCharacteristics.formant1 = clamp(params.formant1, 200, 2000);
		if (typeof params.formant2 === 'number') this.voiceCharacteristics.formant2 = clamp(params.formant2, 500, 3000);
		if (typeof params.formant3 === 'number') this.voiceCharacteristics.formant3 = clamp(params.formant3, 1000, 5000);
		this.synthesisConfig.voiceCharacteristics = this.voiceCharacteristics;
	}
	
	// --- Structural Helper Methods ---
	
	// Calculate branch direction for structured growth
	calculateBranchDirection(tipNode: Node, branch: number[]): { x: number; y: number; z: number } {
		if (branch.length < 2) {
			// If branch is too short, use random direction
			const angle = Math.random() * Math.PI * 2;
			return {
				x: Math.cos(angle),
				y: Math.sin(angle),
				z: (Math.random() - 0.5) * 0.3
			};
		}
		
		// Calculate direction from previous node to tip
		const prevNodeId = branch[branch.length - 2];
		const prevNode = this.nodes[prevNodeId];
		
		if (!prevNode) {
			const angle = Math.random() * Math.PI * 2;
			return {
				x: Math.cos(angle),
				y: Math.sin(angle),
				z: (Math.random() - 0.5) * 0.3
			};
		}
		
		const dx = tipNode.x - prevNode.x;
		const dy = tipNode.y - prevNode.y;
		const dz = tipNode.z - prevNode.z;
		const length = Math.sqrt(dx*dx + dy*dy + dz*dz);
		
		if (length < 1e-6) {
			const angle = Math.random() * Math.PI * 2;
			return {
				x: Math.cos(angle),
				y: Math.sin(angle),
				z: (Math.random() - 0.5) * 0.3
			};
		}
		
		// Return normalized direction
		return {
			x: dx / length,
			y: dy / length,
			z: dz / length
		};
	}
	
	// --- Performance Optimization Methods ---
	
	// Generate spatial hash key from 3D coordinates
	getSpatialHashKey(x: number, y: number, z: number): string {
		const cellX = Math.floor(x / this.spatialHashCellSize);
		const cellY = Math.floor(y / this.spatialHashCellSize);
		const cellZ = Math.floor(z / this.spatialHashCellSize);
		return `${cellX},${cellY},${cellZ}`;
	}

	// Update spatial hash for all nodes in 3D space
	updateSpatialHash(): void {
		this.spatialHash.clear();
		
		for (let i = 0; i < this.nodes.length; i++) {
			const node = this.nodes[i];
			if (!node || node.corroded) continue;
			
			const key = this.getSpatialHashKey(node.x, node.y, node.z);
			if (!this.spatialHash.has(key)) {
				this.spatialHash.set(key, []);
			}
			this.spatialHash.get(key)!.push(i);
		}
	}

	// Get nearby nodes using spatial hash in 3D space (O(1) instead of O(N))
	getNearbyNodes(x: number, y: number, z: number, radius: number): number[] {
		const nearbyNodes: number[] = [];
		const searchRadius = Math.ceil(radius / this.spatialHashCellSize) + 1;
		const centerCellX = Math.floor(x / this.spatialHashCellSize);
		const centerCellY = Math.floor(y / this.spatialHashCellSize);
		const centerCellZ = Math.floor(z / this.spatialHashCellSize);
		
		for (let dx = -searchRadius; dx <= searchRadius; dx++) {
			for (let dy = -searchRadius; dy <= searchRadius; dy++) {
				for (let dz = -searchRadius; dz <= searchRadius; dz++) {
					const key = `${centerCellX + dx},${centerCellY + dy},${centerCellZ + dz}`;
					const cellNodes = this.spatialHash.get(key);
					if (cellNodes) {
						for (const nodeIndex of cellNodes) {
							const node = this.nodes[nodeIndex];
							if (node && !node.corroded) {
								const dist3D = Math.sqrt(
									(node.x - x) ** 2 + (node.y - y) ** 2 + (node.z - z) ** 2
								);
								if (dist3D <= radius) {
									nearbyNodes.push(nodeIndex);
								}
							}
						}
					}
				}
			}
		}
		
		return nearbyNodes;
	}

	// Structured neural evolution with skeletal integrity
	simulateEvolution() {
		// Check if we can generate more nodes this cycle
		if (!this.canGenerateNode()) {
			return;
		}
		
		const minDist = 18;
		let growthCount = 0;
		let growthColor: string | undefined;
		
		for (const branch of this.branches) {
			// Check cycle limit before each potential generation
			if (!this.canGenerateNode()) {
				break;
			}
			
			const tipId = branch[branch.length - 1];
			const tip = this.nodes.find(n => n.id === tipId);
			if (!tip) continue;
			
			// Use fractional generation for structured growth
			const evolutionProbability = 0.6; // Moderate probability for evolution
			if (this.canGenerateFractionalNode(evolutionProbability)) {
				// Calculate structured direction for growth
				const branchDirection = this.calculateBranchDirection(tip, branch);
				const distance = 45 + Math.random() * 25; // Structured spacing
				
				// Add slight variation to direction for natural growth
				const angleVariation = (Math.random() - 0.5) * 0.5; // ±0.25 radians
				const cosAngle = Math.cos(angleVariation);
				const sinAngle = Math.sin(angleVariation);
				
				const x = tip.x + (branchDirection.x * cosAngle - branchDirection.y * sinAngle) * distance;
				const y = tip.y + (branchDirection.x * sinAngle + branchDirection.y * cosAngle) * distance;
				const z = tip.z + branchDirection.z * distance * 0.4; // Small Z variation
				
				let overlaps = false;
				for (const node of this.nodes) {
					const d = Math.hypot(node.x - x, node.y - y) + Math.abs(node.z - z) * 0.5;
					if (d < minDist) {
						overlaps = true;
						break;
					}
				}
				if (overlaps) continue;
				
				// Increment counter and create node
				if (this.incrementNodeCounter()) {
					const value = Math.min(1, Math.max(0, tip.value + (Math.random() - 0.5) * 0.1));
					const energy = 0.6 + Math.random() * 0.3;
					const color = `hsl(${Math.floor(value * 360)}, 80%, 65%)`;
					const id = this.nodes.length > 0 ? Math.max(...this.nodes.map(n => n.id)) + 1 : 0;
					const newNode = { id, x, y, z, value, energy, color, highlight: 120, vz: 0 };
					this.nodes.push(newNode);
					this.edges.push({ from: tip.id, to: id, weight: 0.7 + Math.random() * 0.3 });
					
					// Add to existing branch to maintain structure
					branch.push(id);
					
					this.addRecentNode(id);
					growthCount++;
					growthColor = color;
					
					// Creature vocalizes the growth using Web Audio API
					if (Math.random() < 0.3) { // 30% chance to vocalize growth
						this.synthesizeGrowthSound();
					}
					tip.energy *= 0.3; // Less energy drain for stability
					if (this.onNodeGrowth) this.onNodeGrowth(1, color);
				}
			}
		}
		if (growthCount > 0 && this.onNodeGrowth) this.onNodeGrowth(growthCount, growthColor);
	}

	evolutionaryGrowthAfterPulse() {
		// Check if we can generate more nodes this cycle
		if (!this.canGenerateNode()) {
			return;
		}
		
		const minDist = 18;
		let growthCount = 0;
		
		for (const branch of this.branches) {
			// Check cycle limit before each potential generation
			if (!this.canGenerateNode()) {
				break;
			}
			
			const tipId = branch[branch.length - 1];
			const tip = this.nodes[tipId];
			if (!tip) continue;
			
			// Use fractional generation for pulse-based growth
			const pulseProbability = 0.5; // Moderate probability for pulse growth
			if (this.canGenerateFractionalNode(pulseProbability)) {
				// Calculate structured direction for growth
				const branchDirection = this.calculateBranchDirection(tip, branch);
				const distance = 35 + Math.random() * 20; // Structured spacing
				
				// Add slight variation to direction for natural growth
				const angleVariation = (Math.random() - 0.5) * 0.3; // ±0.15 radians
				const cosAngle = Math.cos(angleVariation);
				const sinAngle = Math.sin(angleVariation);
				
				const x = tip.x + (branchDirection.x * cosAngle - branchDirection.y * sinAngle) * distance;
				const y = tip.y + (branchDirection.x * sinAngle + branchDirection.y * cosAngle) * distance;
				const z = tip.z + branchDirection.z * distance * 0.3; // Small Z variation
				
				let overlaps = false;
				for (const node of this.nodes) {
					const d = Math.hypot(node.x - x, node.y - y) + Math.abs(node.z - z) * 0.5;
					if (d < minDist) {
						overlaps = true;
						break;
					}
				}
				if (overlaps) continue;
				
				// Increment counter and create node
				if (this.incrementNodeCounter()) {
					const value = Math.min(1, Math.max(0, tip.value + (Math.random() - 0.5) * 0.1));
					const energy = 0.5 + Math.random() * 0.4;
					const color = `hsl(${Math.floor(value * 360)}, 70%, 60%)`;
					const id = this.nodes.length;
					const newNode = { id, x, y, z, value, energy, color, highlight: 30, vz: 0 };
					this.nodes.push(newNode);
					this.edges.push({ from: tip.id, to: id, weight: 0.6 + Math.random() * 0.4 });
					branch.push(id);
					growthCount++;
					// Play sound for node growth after pulse
					if (this.onNodeGrowth) this.onNodeGrowth(1, color);
				}
			}
		}
		if (growthCount > 0 && this.onNodeGrowth) this.onNodeGrowth(growthCount);
	}

	// Add a restoreAllLife method to reset the simulation state
	restoreAllLife() {
		for (const node of this.nodes) {
			node.corroded = false;
			node.energy = 10 + Math.random() * 10;
			node.energyStorage = 20 + Math.random() * 20;
			node.highlight = 40;
		}
		for (let i = 0; i < this.branchSelfSustaining.length; i++) {
			this.branchSelfSustaining[i] = false;
		}
		this.simulationFailed = false;
	}

	applyPsychonautCreatureInfluence() {
		const t = this.step / 120;
		this.psychonautCreature.phase += 0.01 + Math.sin(t) * 0.005;
		this.psychonautCreature.depth = 2.5 + Math.sin(t / 2) * 1.2 + Math.cos(this.psychonautCreature.phase) * 0.3;
		this.psychonautCreature.x = 600 + Math.cos(this.psychonautCreature.phase) * 320 * Math.sin(t / 3);
		this.psychonautCreature.y = 400 + Math.sin(this.psychonautCreature.phase) * 220 * Math.cos(t / 2);

		const nodes = this.nodes;
		const len = nodes.length;
		const px = this.psychonautCreature.x;
		const py = this.psychonautCreature.y;
		const radius = this.psychonautCreature.influenceRadius;
		const depthFactor = Math.max(1, this.psychonautCreature.depth);
		const phase = this.psychonautCreature.phase;

		for (let i = 0; i < len; i++) {
			const node = nodes[i];
			const dx = node.x - px;
			const dy = node.y - py;
			if (dx * dx + dy * dy < radius * radius) {
				const phaseFactor = 0.7 + 0.3 * Math.sin(phase + node.id);
				node.energy += 0.2 * depthFactor * phaseFactor;
				node.highlight = Math.max(node.highlight || 0, Math.floor(20 * depthFactor * phaseFactor));
				if (Math.random() < 0.002 * depthFactor && this.canGenerateNode()) {
					const angle = Math.random() * Math.PI * 2;
					const r = 40 + Math.random() * 60;
					const x = node.x + Math.cos(angle) * r;
					const y = node.y + Math.sin(angle) * r;
					const minDist = 22;
					let overlaps = false;
					for (let j = 0; j < Math.min(40, len); j++) {
						const other = nodes[(i + j) % len];
						const ddx = other.x - x;
						const ddy = other.y - y;
						if (ddx * ddx + ddy * ddy < minDist * minDist) {
							overlaps = true;
							break;
						}
					}
					if (!overlaps) {
						// Increment counter and create node
						if (this.incrementNodeCounter()) {
							const value = Math.min(1, Math.max(0, node.value + (Math.random() - 0.5) * 0.2));
							const energy = Math.random() * depthFactor;
							const color = `hsl(${Math.floor(value * 360)}, 80%, 55%)`;
							const id = nodes.length;
							const newNode = { id, x, y, z: Math.random() * 100 - 50, value, energy, color, highlight: 40, vz: 0 };
							nodes.push(newNode);
							this.edges.push({ from: node.id, to: id, weight: Math.random() });
							this.branches.push([id]);
						}
					}
				}
			}
		}
	}

	registerPlugin(plugin: (sim: NeuralNetworkSimulator) => void) {
		this.plugins.push(plugin);
	}

	runPlugins() {
		for (const plugin of this.plugins) {
			plugin(this);
		}
	}

	// Randomly split/merge branches and inject chaos
	chaosStep() {
		// Increase frequency and magnitude of chaos events
		if (Math.random() < 0.08 && this.branches.length > 2) { // was 0.03
			const branchIdx = Math.floor(Math.random() * this.branches.length);
			const branch = this.branches[branchIdx];
			if (branch.length > 2) {
				const splitIdx = Math.floor(Math.random() * (branch.length - 1)) + 1;
				const newBranch = branch.splice(splitIdx);
				this.branches.push(newBranch);
				this.branchSelfSustaining.push(false);
			}
		}
		if (Math.random() < 0.06 && this.branches.length > 2) { // was 0.02
			const i = Math.floor(Math.random() * this.branches.length);
			const j = Math.floor(Math.random() * this.branches.length);
			if (i !== j && this.branches[i].length > 0 && this.branches[j].length > 0) {
				this.branches[i].push(...this.branches[j]);
				this.branches[j].length = 0;
			}
			this.branches = this.branches.filter(b => b.length > 0);
		}
		// More frequent and larger energy surges
		if (Math.random() < 0.12) { // was 0.05
			const node = this.nodes[Math.floor(Math.random() * this.nodes.length)];
			if (node && !node.corroded) node.energy += 40 + Math.random() * 80; // was 20 + ...
		}
		// More frequent corrosion
		if (Math.random() < 0.09) { // was 0.04
			const node = this.nodes[Math.floor(Math.random() * this.nodes.length)];
			if (node && !node.corroded && node.id !== this.crystalHeartId) {
				node.corroded = true;
				node.corrodeStep = this.step;
			}
		}
		// Randomly destabilize branches by removing random nodes
		if (Math.random() < 0.04 && this.nodes.length > 20) {
			const idx = Math.floor(Math.random() * this.nodes.length);
			const node = this.nodes[idx];
			if (node && node.id !== this.crystalHeartId && !node.corroded) {
				this.removeNode(node.id);
			}
		}
	}

	// Stability metric: ratio of active nodes to total nodes, weighted by energy
	updateStabilityMetric() {
		const activeNodes = this.nodes.filter(n => !n.corroded);
		const avgEnergy = activeNodes.length > 0 ? activeNodes.reduce((sum, n) => sum + (n.energy ?? 0), 0) / activeNodes.length : 0;
		const connectivity = this.edges.length / Math.max(1, this.nodes.length);
		const safeAvgEnergy = typeof avgEnergy === 'number' ? avgEnergy : 0;
		const safeConnectivity = typeof connectivity === 'number' ? connectivity : 0;
		this.stabilityMetric = (activeNodes.length / Math.max(1, this.nodes.length)) * safeAvgEnergy * safeConnectivity;
	}

	// Heart's humming life force cycle
	heartLifeForceCycle() {
		if (this.crystalHeartId === null) return;
		
		const heart = this.nodes[this.crystalHeartId];
		if (!heart) return;
		
		const time = this.step / 60;
		
		// Heart hums with life force frequency
		const humIntensity = Math.sin(time * this.heartHumFrequency * 0.1) * 0.5 + 0.5;
		this.heartLifeForce = 0.8 + humIntensity * 0.4; // Life force pulses
		
		// Heart visual pulsing
		heart.highlight = 1500 + humIntensity * 1000;
		heart.color = `hsl(${290 + Math.sin(time * 2) * 20}, 100%, ${70 + humIntensity * 20}%)`;
		
		// Gentle heart movement (breathing)
		if (typeof heart.vx === 'number') heart.vx += Math.sin(time * 0.3) * 0.01;
		if (typeof heart.vy === 'number') heart.vy += Math.cos(time * 0.4) * 0.01;
		
		// Trigger heart hum sound
		if (this.step % Math.round(60 / this.heartHumFrequency * 10) === 0 && this.onCrystalHeartPulse) {
			this.onCrystalHeartPulse();
		}
	}

	// Energy surges through the network
	energySurgeThroughNetwork() {
		if (this.crystalHeartId === null) return;
		
		const time = this.step / 60;
		
		// Create energy surges periodically
		if (this.step % Math.round(this.crystalHeartPulseInterval * 0.1) === 0) {
			this.energySurgeActive = true;
			this.energySurgeIntensity = 1.0;
		}
		
		// Process energy surge
		if (this.energySurgeActive) {
			const surgeDecay = 0.95;
			this.energySurgeIntensity *= surgeDecay;
			
			if (this.energySurgeIntensity < 0.01) {
				this.energySurgeActive = false;
			} else {
				// Surge energy through all edges
				for (const edge of this.edges) {
					const fromNode = this.nodes[edge.from];
					const toNode = this.nodes[edge.to];
					
					if (fromNode && toNode && !fromNode.corroded && !toNode.corroded) {
						const surgeAmount = this.energySurgeIntensity * 0.1 * edge.weight;
						
						// Energy flows from heart outward
						if (edge.from === this.crystalHeartId) {
							toNode.energy = Math.min(1.0, toNode.energy + surgeAmount);
							toNode.highlight = Math.max(toNode.highlight || 0, 30 * this.energySurgeIntensity);
						}
						// Energy can also flow between connected nodes
						else if (fromNode.energy > toNode.energy) {
							const transfer = Math.min(surgeAmount, fromNode.energy - toNode.energy);
							fromNode.energy -= transfer * 0.1;
							toNode.energy += transfer;
							toNode.highlight = Math.max(toNode.highlight || 0, 20 * this.energySurgeIntensity);
						}
					}
				}
			}
		}
		
		// Continuous gentle energy feed from heart
		const continuousFeed = this.heartLifeForce * 0.001;
		for (const edge of this.edges) {
			if (edge.from === this.crystalHeartId) {
				const node = this.nodes[edge.to];
				if (node && !node.corroded) {
					node.energy = Math.min(1.0, node.energy + continuousFeed * edge.weight);
				}
			}
		}
	}
	
	// --- Living Language System Access ---
	
	public getLivingLanguage(): LivingLanguageSystem {
		return this.livingLanguage;
	}
	
	public getConceptualAbstraction(): ConceptualAbstractionSystem {
		return this.conceptualAbstraction;
	}
	
	public generateLivingExpression(): string {
		const context = {
			emotionalState: this.getCreatureEmotionalState(),
			energyLevel: this.totalNetworkEnergy / 1000,
			complexity: this.linguisticComplexity,
			urgency: this.calculateUrgency(),
			intimacy: this.calculateIntimacy(),
			creativity: this.livingLanguage.getLanguageEcology().creativity,
			time: this.step / 60,
			environment: this.creatureState
		};
		
		return this.livingLanguage.generateLivingExpression(context);
	}
	
	public generateConceptualExpression(): string {
		return this.conceptualAbstraction.generateConceptualExpression();
	}
	
	public getLanguageTranscendence(): number {
		return this.languageTranscendence;
	}
	
	public setLanguageTranscendence(transcendence: number): void {
		this.languageTranscendence = Math.max(0, Math.min(1, transcendence));
		this.livingLanguage.setEmergence(this.languageTranscendence);
		this.conceptualAbstraction.setTranscendence(this.languageTranscendence);
	}
	
	public toggleConceptualThinking(): void {
		this.conceptualThinking = !this.conceptualThinking;
		console.log(`[Creature ${this.creatureId}] Conceptual thinking ${this.conceptualThinking ? 'enabled' : 'disabled'}`);
	}
	
	private getCreatureEmotionalState(): string {
		const energyLevel = this.totalNetworkEnergy / 1000;
		if (energyLevel > 0.8) return 'ecstatic';
		if (energyLevel > 0.6) return 'joyful';
		if (energyLevel > 0.4) return 'peaceful';
		if (energyLevel > 0.2) return 'contemplative';
		return 'dormant';
	}
	
	private calculateUrgency(): number {
		// Calculate urgency based on creature state and energy
		switch (this.creatureState) {
			case 'stabilizing': return 0.8;
			case 'sensing': return 0.6;
			case 'exploring': return 0.4;
			case 'growing': return 0.2;
			default: return 0.5;
		}
	}
	
	private calculateIntimacy(): number {
		// Calculate intimacy based on successful communications
		const successRate = this.communicationAttempts > 0 ? this.successfulCommunications / this.communicationAttempts : 0;
		return successRate;
	}
}
